import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Save, Link, Download, ChevronUp, ChevronDown, ChevronRight, PlusCircle, Grid, Upload, ArrowLeft, FolderOpen, RefreshCw, Database } from 'lucide-react';
import { createBezierPath as createBezierPathUtil, getTotalThickness as getTotalThicknessUtil, toPastelColor as toPastelColorUtil, toDarkerColor as toDarkerColorUtil } from './utils/svgHelpers';
import { layerHasOwnColor } from './utils/layerColors';
import { getSoilGroup, SOIL_GROUP_COLORS } from './utils/soilGroups';
import { getMaxDepth as getMaxDepthUtil, getMaxDistance as getMaxDistanceUtil, getSvgHeight as getSvgHeightUtil, getSvgWidth as getSvgWidthUtil } from './utils/svgDimensions';
import { useSvgCoordinates } from './hooks/useSvgCoordinates';
import { useDisplaySettings, DEFAULT_TYPOGRAPHY, FONT_FAMILY_OPTIONS, TYPOGRAPHY_LABELS } from './hooks/useDisplaySettings';
import { useProjectData } from './hooks/useProjectData';
import { useDrawings } from './hooks/useDrawings';
import { useLenses } from './hooks/useLenses';
import { useConnections } from './hooks/useConnections';
import { useBoreholes } from './hooks/useBoreholes';
import { useBoreholeMap } from './hooks/useBoreholeMap';
import { useGlobalMouseHandlers } from './hooks/useGlobalMouseHandlers';
import { useUndoRedo } from './hooks/useUndoRedo';
import { useLithologyDictionaries } from './hooks/useLithologyDictionaries';
import LayerEditDialog from './components/LayerEditDialog';
import LithologyForm from './components/LithologyForm';
import { parseLithologyName } from './firebase/dictionaries';
import { fetchTerrainProfile } from './utils/gugikNmt';
import { exportToDxf, DXF_EXPORT_ENABLED } from './utils/dxfExport';
import { exportToDxfV2, DXF_EXPORT_V2_ENABLED } from './utils/dxfExportV2';
import { generateVirtualBoreholeLayers } from './utils/interpolation';
import { formatDateExecution } from './utils/dateFormat';
const GeologicalProfile = ({ initialData, onDataChange, onSave, onReload, onSaveToSource, saveStatus, sourceSaveStatus, dirtyBoreholeIds, onRevertBorehole, isImported, autoInterpolate = false }) => {
  const navigate = useNavigate();
  // Core data z hooka useProjectData
  const {
    boreholes, setBoreholes,
    drawings, setDrawings,
    lenses, setLenses,
    scale, setScale,
    horizontalScale, setHorizontalScale,
    fileInputRef,
    exportData: exportDataHook,
    importData: importDataHook,
    exportToPDF,
  } = useProjectData(initialData);

  // Display settings z hooka
  const {
    showGrid, setShowGrid,
    showBoreholeRectangles, setShowBoreholeRectangles,
    showSoilName, setShowSoilName,
    showSoilState, setShowSoilState,
    showGeotechnicalLayer, setShowGeotechnicalLayer,
    showGenesisAndStratigraphy, setShowGenesisAndStratigraphy,
    showThickness, setShowThickness,
    showDepths, setShowDepths,
    showElevationScale, setShowElevationScale,
    showElevationScaleLeft, setShowElevationScaleLeft,
    showCrossSectionTitle, setShowCrossSectionTitle,
    crossSectionTitle, setCrossSectionTitle,
    crossSectionTitlePos, setCrossSectionTitlePos,
    typography, setTypography,
    showDrawingTools, setShowDrawingTools,
    usePastelColors, setUsePastelColors,
    outlineOnly, setOutlineOnly,
    sharpenContours, setSharpenContours,
    movePolygonMode, setMovePolygonMode,
    snapToOrtho, setSnapToOrtho,
    snapToGrid, setSnapToGrid,
    gridSize, setGridSize,
    snapToEdge, setSnapToEdge,
    snapToBorehole, setSnapToBorehole,
    showLiveMeasure, setShowLiveMeasure,
    showLenses, setShowLenses,
    fullscreenMode, setFullscreenMode,
    showBoreholeEditPanel, setShowBoreholeEditPanel,
    showDrawingToolsPanel, setShowDrawingToolsPanel,
    showGeologicalObjectsPanel, setShowGeologicalObjectsPanel,
    showBoreholeMapPanel, setShowBoreholeMapPanel,
    showTerrain, setShowTerrain,
    showWater, setShowWater,
    showBoreholeMeta, setShowBoreholeMeta,
    showPolygonLabels, setShowPolygonLabels,
    labelFieldOrder, setLabelFieldOrder,
    smoothWaterLine, setSmoothWaterLine,
    showFreeWaterLine, setShowFreeWaterLine,
    showConfinedWaterLine, setShowConfinedWaterLine,
  } = useDisplaySettings(initialData);

  // Przesuwa pole w kolejności etykiety o jedno miejsce w lewo (dir=-1) lub prawo (dir=+1).
  const moveLabelField = (fieldKey, dir) => {
    setLabelFieldOrder(prev => {
      const idx = prev.indexOf(fieldKey);
      if (idx < 0) return prev;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });
  };

  // Strzałki ◀ ▶ + numer pozycji obok checkboxa pola etykiety.
  const renderLabelOrderArrows = (fieldKey) => {
    const idx = labelFieldOrder.indexOf(fieldKey);
    if (idx < 0) return null;
    const atStart = idx <= 0;
    const atEnd = idx >= labelFieldOrder.length - 1;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <span className="flex items-center gap-0.5" onClick={stop}>
        <button
          type="button"
          disabled={atStart}
          onMouseDown={stop}
          onClick={(e) => { stop(e); moveLabelField(fieldKey, -1); }}
          className="w-5 h-5 flex items-center justify-center text-gray-500 disabled:opacity-25 hover:bg-gray-200 rounded text-xs leading-none"
          title="Wcześniej w kolejności etykiety"
        >◀</button>
        <button
          type="button"
          disabled={atEnd}
          onMouseDown={stop}
          onClick={(e) => { stop(e); moveLabelField(fieldKey, +1); }}
          className="w-5 h-5 flex items-center justify-center text-gray-500 disabled:opacity-25 hover:bg-gray-200 rounded text-xs leading-none"
          title="Później w kolejności etykiety"
        >▶</button>
        <span className="text-xs text-gray-400 w-3 text-center">{idx + 1}</span>
      </span>
    );
  };

  // Hook dla współrzędnych SVG
  const { svgRef, getSvgCoordinates } = useSvgCoordinates();

  // Hook dla otworów wiertniczych
  const {
    expandedBoreholes, setExpandedBoreholes,
    resizingLayer, setResizingLayer,
    resizeStart,
    draggingBorehole, setDraggingBorehole,
    editingLayerDialog, setEditingLayerDialog,
    draggingWaterLevel,
    nameInputRef, thicknessInputRef,
    addBoreholeFromMap, addBoreholeAfter,
    moveBoreholeUp, moveBoreholeDown, removeBorehole, updateBorehole,
    addLayer, addLayerAfter, moveLayerUp, moveLayerDown, removeLayer, updateLayer,
    updateLayerFields, toggleLayerExpanded, isLayerExpanded,
    mergeLayerSelection, mergeLayerDialog, setMergeLayerDialog,
    toggleLayerMergeSelection, clearMergeLayerSelection,
    isMergeSelectionContiguous, openMergeLayerDialog, mergeLayers,
    openLayerEditDialog, handleLayerDialogSubmit, handleLayerDialogCancel,
    addWaterLevel, removeWaterLevel, updateWaterLevel,
    handleBoreholeHeaderDragStart, handleDragMove, handleDragEnd,
    handleResizeStart, handleResizeMove, handleResizeEnd,
    handleWaterLevelMouseDown, handleWaterLevelMouseMove, handleWaterLevelMouseUp,
  } = useBoreholes({ boreholes, setBoreholes, getSvgCoordinates, scale, horizontalScale });

  // Slowniki litologii dla dialogu edycji warstwy. sourceProjectId bierzemy z pierwszego
  // otworu, ktory zostal zaimportowany z bazy zrodlowej (otwory dodane recznie nie maja).
  const lithologySourceProjectId = (boreholes || []).find(b => b.sourceProjectId)?.sourceProjectId || null;
  const {
    lithoDict: layerLithoDict,
    genesisDict: layerGenesisDict,
    rockDict: layerRockDict,
    rockSubDict: layerRockSubDict,
    numberDict: layerNumberDict,
  } = useLithologyDictionaries(lithologySourceProjectId);

  // Topografia (NMT) — zadeklarowana wcześnie żeby useConnections mógł rozciągać najwyższą strefę do terenu.
  const [terrainProfile, setTerrainProfile] = useState(initialData?.terrainProfile || null); // [{distance, elevation}]

  // Hook dla połączeń warstw
  const {
    connectLayers, setConnectLayers,
    autoConnectByName, setAutoConnectByName,
    autoConnectMode, setAutoConnectMode,
    manualConnections, setManualConnections,
    selectedLayerForConnection, setSelectedLayerForConnection,
    excludedAutoConnections, setExcludedAutoConnections,
    connectionPointAdjustments, setConnectionPointAdjustments,
    draggingConnectionPoint,
    manualConnectionMode, setManualConnectionMode,
    interpolationMode, setInterpolationMode,
    interpolationPoints, setInterpolationPoints,
    draggingInterpolation,
    hoveredConnection, setHoveredConnection,
    getConnectionKey, getConnectionPointKey,
    findLayerConnections,
    handleInterpolationPointDrag, handleInterpolationMouseMove, handleInterpolationMouseUp,
    resetInterpolationPoint,
    handleConnectionPointMouseDown, handleConnectionPointMouseMove, handleConnectionPointMouseUp,
    resetConnectionPoint,
    deleteManualConnection, deleteConnection,
    handleLayerClickForConnection,
    freeDrawMode, setFreeDrawMode,
    freeDrawClipMode, setFreeDrawClipMode,
    freeDrawPoints, setFreeDrawPoints,
    freeDrawPreview, setFreeDrawPreview,
    selectedFreeDraw, setSelectedFreeDraw, deleteFreeDraw,
    selectedFreeDrawIds, setSelectedFreeDrawIds,
    editingFreeDraw, setEditingFreeDraw,
    freeDrawEditVerticesMode, setFreeDrawEditVerticesMode,
    freeDrawSnapVertices, setFreeDrawSnapVertices,
    freeDrawFillGaps, setFreeDrawFillGaps,
    freeDrawExpandMode, setFreeDrawExpandMode,
    freeDrawExpandClip, setFreeDrawExpandClip,
    draggingFreeDrawPoint,
    addFreeDrawVertex, removeFreeDrawVertex, simplifyFreeDrawPolygon, simplifyAllFreeDrawPolygons, detachFreeDrawFromBoreholes, reattachFreeDrawToBoreholes, expandFreeDrawBoundary, applyFreeDrawEditClipping,
    handleFreeDrawPointDragStart, handleFreeDrawPointDragMove, handleFreeDrawPointDragEnd,
    draggingFreeDrawControl,
    handleFreeDrawControlDragStart, handleFreeDrawControlDragMove, handleFreeDrawControlDragEnd,
    resetFreeDrawControlPoint,
    draggingFreeDrawEdge,
    handleFreeDrawEdgeDragStart, handleFreeDrawEdgeDragMove, handleFreeDrawEdgeDragEnd,
    draggingFreeDrawPolygon,
    handleFreeDrawPolygonDragStart, handleFreeDrawPolygonDragMove, handleFreeDrawPolygonDragEnd,
    restoreFreeDrawPolygonPosition, restoreAllFreeDrawPolygonPositions, shiftFreeDrawsByMeters,
    convertConnectionsToFreeDraw,
    clipOverlappingFreeDraws,
    fillGapsBetweenFreeDraws,
    snapToLayerBoundary, snapToFreeDrawVertex, handleFreeDrawClick, handleFreeDrawDoubleClick, resolvePointSvg, applyOrthoSnap,
    splitMode, setSplitMode,
    splittingConnId, setSplittingConnId,
    splitPickMode, setSplitPickMode,
    splitLinePoints, setSplitLinePoints,
    splitLinePreview, setSplitLinePreview,
    polylineSplitMode, setPolylineSplitMode,
    polylineSplitPoints, setPolylineSplitPoints,
    polylineSplitPreview, setPolylineSplitPreview,
    polylineSplitStatus, setPolylineSplitStatus,
    handlePolylineSplitClick,
    finishPolylineSplit,
    cancelPolylineSplit,
    contextMenuFreeDraw, setContextMenuFreeDraw,
    handleSplitClick,
    cancelSplit,
    lensInLayerMode, setLensInLayerMode,
    lensInLayerParentId, setLensInLayerParentId,
    cancelLensInLayer,
    copiedFreeDrawProps, setCopiedFreeDrawProps,
    editPropsDialog, setEditPropsDialog,
    updateFreeDrawProps,
    copyFreeDrawProps,
    pasteFreeDrawProps,
    bringFreeDrawToFront,
    sendFreeDrawToBack,
    cutHoleMode, setCutHoleMode,
    cutHoleParentId, setCutHoleParentId,
    cancelCutHole,
    smoothFreeDrawEdges,
    mergePolygonsMode, setMergePolygonsMode,
    mergeFromConnId, setMergeFromConnId,
    mergeFreeDrawPolygons,
    draggingPolygonLabel,
    handlePolygonLabelDragStart, handlePolygonLabelDragMove, handlePolygonLabelDragEnd,
    resetPolygonLabelPos,
    stickyLayerLock, setStickyLayerLock,
  } = useConnections({ boreholes, getSvgCoordinates, scale, horizontalScale, snapToOrtho, snapToGrid, gridSize, snapToEdge, snapToBorehole, initialData, terrainProfile });

  // Hook dla soczewek geologicznych
  const {
    lensMode, setLensMode,
    lensShape, setLensShape,
    currentLens, setCurrentLens,
    editingLens, setEditingLens,
    draggingLens,
    lensPolygonPoints, setLensPolygonPoints,
    lensEditMode, setLensEditMode,
    lensMoveMode, setLensMoveMode,
    lensControlPoints,
    draggingLensControl,
    lensVertices,
    draggingLensVertex,
    handleLensStart, handleLensMove, handleLensEnd,
    handleLensPolygonComplete,
    openLensPropertiesDialog, saveLensProperties, deleteLens,
    handleLensDoubleClick,
    handleLensControlMouseDown, handleLensControlMouseMove, handleLensControlMouseUp,
    resetLensControlPoint,
    handleLensVertexMouseDown, handleLensVertexMouseMove, handleLensVertexMouseUp,
    resetLensVertex,
    handleLensDragStart, handleLensDragMove, handleLensDragEnd,
    saveLensEdits,
    renderLens,
  } = useLenses({ lenses, setLenses, boreholes, getSvgCoordinates, usePastelColors, outlineOnly, createBezierPath: createBezierPathUtil });

  // (useUndoRedo przeniesione poniżej deklaracji terrainProfile, żeby uniknąć TDZ)

  // Hook dla mapki otworów
  const {
    addBoreholeFromMapMode, setAddBoreholeFromMapMode,
    selectBoreholeForProfileMode, setSelectBoreholeForProfileMode,
    selectedBoreholesForProfile, setSelectedBoreholesForProfile,
    handleMapClick,
    handleBoreholeSelectOnMap,
    createProfileFromSelected,
  } = useBoreholeMap({ boreholes, setBoreholes, addBoreholeFromMap });

  // Hook dla narzędzi rysunkowych
  const {
    drawingMode, setDrawingMode,
    currentDrawing,
    drawingColor, setDrawingColor,
    scaleDrawingTools, setScaleDrawingTools,
    moveDrawingMode, setMoveDrawingMode,
    draggingDrawing,
    measureFirstPoint, setMeasureFirstPoint,
    measurePreview, setMeasurePreview,
    activeMeasurements, setActiveMeasurements,
    handleDrawingStart,
    handleDrawingMove,
    handleDrawingEnd,
    handleDrawingDragStart,
    handleDrawingDragMove,
    handleDrawingDragEnd,
    removeDrawing,
    clearAllDrawings,
    renderDrawing,
  } = useDrawings({ drawings, setDrawings, getSvgCoordinates, scale, horizontalScale, initialData });

  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showConnectionsMenu, setShowConnectionsMenu] = useState(false);
  const [showPolygonOpsMenu, setShowPolygonOpsMenu] = useState(false);
  const [showEditModesMenu, setShowEditModesMenu] = useState(false);
  const [showPrecisionPanel, setShowPrecisionPanel] = useState(false);
  const [clipboardPolygon, setClipboardPolygon] = useState(null); // skopiowany freeDraw
  const cursorSvgPosRef = useRef({ x: 0, y: 0 }); // ostatnia pozycja kursora w SVG (do wklejania)
  // Zoom/Pan widoku (manipulacja SVG viewBox — czyste renderowanie, bez zmian danych).
  // Domyślny zoom 0.85 + delikatne przesunięcie w lewo — żeby przy pierwszym otwarciu boczne
  // etykiety skrajnego-lewego otworu (textAnchor="end" wystaje na ujemny X) nie były obcięte.
  const [viewZoom, setViewZoom] = useState(0.7);
  const [viewPanX, setViewPanX] = useState(-60);
  const [viewPanY, setViewPanY] = useState(0);
  const [isViewPanning, setIsViewPanning] = useState(false);
  const [handTool, setHandTool] = useState(false); // łapka — lewy klik+drag = pan widoku
  const viewPanStartRef = useRef(null); // {clientX, clientY, panX, panY}
  const spaceHeldRef = useRef(false);
  const justFinishedPanRef = useRef(false); // krótkotrwały flag żeby zablokować context menu po lewym+prawym pan
  // Drukowanie wycinka do PDF A3/A4 — wymiary prostokąta na ekranie wynikają bezpośrednio
  // z dolnych skal "Skala pionowa 1:" i "Skala pozioma 1:" (interpretowanych jako skale wydruku 1:N
  // przy CSS 96 DPI). Po włączeniu trybu prostokąt pojawia się automatycznie i można go tylko przesuwać.
  const [printSelectMode, setPrintSelectMode] = useState(false);
  const [printSelectFormat, setPrintSelectFormat] = useState('a3'); // 'a3' | 'a4'
  const [printSelectRect, setPrintSelectRect] = useState(null); // {x, y, w, h} w SVG coords
  const [printDrag, setPrintDrag] = useState(null); // null | { mode: 'move', startSvg, origRect }
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printOrientation, setPrintOrientation] = useState('landscape'); // 'landscape' | 'portrait'
  const [showDxfV2Dialog, setShowDxfV2Dialog] = useState(false);
  const [mergeWarning, setMergeWarning] = useState(null); // { title, message, tone } dla dialogu walidacji scalania polygonów
  const [quickToolbar, setQuickToolbar] = useState(null); // { x, y } w pikselach okna — pływający panel narzędzi po Shift+prawy klik
  const quickToolbarDragRef = useRef(null); // { offsetX, offsetY } — offset kursor → róg panelu w trakcie drag
  const quickToolbarRef = useRef(null); // ref do floating wrapper — dla detekcji „klik na zewnątrz"
  const [dxfV2UseHatch, setDxfV2UseHatch] = useState(true);
  const [showWaterPanel, setShowWaterPanel] = useState(false);
  const waterPanelRef = useRef(null);
  const [contextSubmenu, setContextSubmenu] = useState(null); // 'color' | 'pattern' | null — submenu w menu kontekstowym freeDraw
  const [showAttributeTable, setShowAttributeTable] = useState(false);
  const [editingAttrRow, setEditingAttrRow] = useState(null); // id edytowanego wiersza
  const [attrTableFocusId, setAttrTableFocusId] = useState(null); // poligon do podświetlenia + scroll-into-view po otwarciu tabeli z menu kontekstowego
  const [attrTablePos, setAttrTablePos] = useState({ x: 100, y: 80 });
  const [attrTableDrag, setAttrTableDrag] = useState(null); // {startX, startY, origX, origY}
  const [virtualBoreholeMode, setVirtualBoreholeMode] = useState(false); // Tryb wstawiania wirtualnego otworu
  const [blockTextSelection, setBlockTextSelection] = useState(true); // Blokada zaznaczania tekstu na przekroju
  const [labelContextMenu, setLabelContextMenu] = useState(null); // {mcId, clientX, clientY} — PPM na etykiecie poligonu
  const [altKeyHeld, setAltKeyHeld] = useState(false); // Alt held = polygony pointer-events:none (click-through do otworów pod spodem)

  // Per-otwór offset SVG (px) dla bloku metadanych — ruchome przeciąganiem
  const [boreholeMetaOffsets, setBoreholeMetaOffsets] = useState(
    () => initialData?.settings?.boreholeMetaOffsets || {}
  );
  const [draggingBoreholeMeta, setDraggingBoreholeMeta] = useState(null); // { boreholeId, startX, startY, baseDx, baseDy }

  const handleBoreholeMetaDragStart = (e, boreholeId) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = getSvgCoordinates(e);
    const current = boreholeMetaOffsets[boreholeId] || { dx: 0, dy: 0 };
    setDraggingBoreholeMeta({ boreholeId, startX: x, startY: y, baseDx: current.dx, baseDy: current.dy });
  };

  const handleBoreholeMetaDragMove = (e) => {
    if (!draggingBoreholeMeta) return;
    e.preventDefault();
    const { x, y } = getSvgCoordinates(e);
    setBoreholeMetaOffsets((prev) => ({
      ...prev,
      [draggingBoreholeMeta.boreholeId]: {
        dx: draggingBoreholeMeta.baseDx + (x - draggingBoreholeMeta.startX),
        dy: draggingBoreholeMeta.baseDy + (y - draggingBoreholeMeta.startY),
      },
    }));
  };

  const handleBoreholeMetaDragEnd = () => {
    if (draggingBoreholeMeta) setDraggingBoreholeMeta(null);
  };

  const resetBoreholeMetaOffset = (boreholeId) => {
    setBoreholeMetaOffsets((prev) => {
      const next = { ...prev };
      delete next[boreholeId];
      return next;
    });
  };

  // Drag bloku tytułu/skali przekroju
  const [draggingCrossSectionTitle, setDraggingCrossSectionTitle] = useState(null); // { startX, startY, baseX, baseY }

  const handleCrossSectionTitleDragStart = (e, defaultPos) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = getSvgCoordinates(e);
    const current = crossSectionTitlePos || defaultPos;
    setDraggingCrossSectionTitle({ startX: x, startY: y, baseX: current.x, baseY: current.y });
  };

  const handleCrossSectionTitleDragMove = (e) => {
    if (!draggingCrossSectionTitle) return;
    e.preventDefault();
    const { x, y } = getSvgCoordinates(e);
    setCrossSectionTitlePos({
      x: draggingCrossSectionTitle.baseX + (x - draggingCrossSectionTitle.startX),
      y: draggingCrossSectionTitle.baseY + (y - draggingCrossSectionTitle.startY),
    });
  };

  const handleCrossSectionTitleDragEnd = () => {
    if (draggingCrossSectionTitle) setDraggingCrossSectionTitle(null);
  };

  // Tracking klawisza Alt — gdy trzymany, polygony freeDraw stają się transparent dla kliknięć.
  useEffect(() => {
    const onDown = (e) => { if (e.altKey) setAltKeyHeld(true); };
    const onUp = (e) => { if (!e.altKey) setAltKeyHeld(false); };
    const onBlur = () => setAltKeyHeld(false);
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Scroll-into-view dla wiersza tabeli atrybutów po otwarciu z menu kontekstowego.
  useEffect(() => {
    if (!showAttributeTable || attrTableFocusId == null) return;
    // Poczekaj na render modala i tbody.
    const t = setTimeout(() => {
      const row = document.querySelector(`[data-attr-mcid="${attrTableFocusId}"]`);
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => clearTimeout(t);
  }, [showAttributeTable, attrTableFocusId]);

  useEffect(() => {
    if (!showWaterPanel) return;
    const handleClickOutside = (e) => {
      if (waterPanelRef.current && !waterPanelRef.current.contains(e.target)) {
        setShowWaterPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWaterPanel]);

  // Globalne handlery mousemove/mouseup zbierane ze wszystkich hooków
  useGlobalMouseHandlers({
    moveHandlers: [
      handleDrawingDragMove, handleResizeMove, handleDrawingMove,
      handleLensMove, handleInterpolationMouseMove, handleConnectionPointMouseMove,
      handleWaterLevelMouseMove, handleLensControlMouseMove, handleLensVertexMouseMove,
      handleLensDragMove, handleFreeDrawPointDragMove, handleFreeDrawControlDragMove,
      handleFreeDrawEdgeDragMove, handleFreeDrawPolygonDragMove,
      handlePolygonLabelDragMove,
      handleBoreholeMetaDragMove,
      handleCrossSectionTitleDragMove,
    ],
    upHandlers: [
      handleDrawingDragEnd, handleResizeEnd, handleDrawingEnd,
      handleLensEnd, handleInterpolationMouseUp, handleConnectionPointMouseUp,
      handleWaterLevelMouseUp, handleLensControlMouseUp, handleLensVertexMouseUp,
      handleLensDragEnd, handleFreeDrawPointDragEnd, handleFreeDrawControlDragEnd,
      handleFreeDrawEdgeDragEnd, handleFreeDrawPolygonDragEnd,
      handlePolygonLabelDragEnd,
      handleBoreholeMetaDragEnd,
      handleCrossSectionTitleDragEnd,
    ],
    deps: [draggingDrawing, resizingLayer, resizeStart, currentDrawing, currentLens, draggingInterpolation, draggingConnectionPoint, draggingWaterLevel, draggingLensControl, draggingLensVertex, draggingLens, draggingFreeDrawPoint, draggingFreeDrawControl, draggingFreeDrawEdge, draggingFreeDrawPolygon, draggingPolygonLabel, draggingBoreholeMeta, draggingCrossSectionTitle],
  });

  // --- Topografia z NMT GUGiK (terrainProfile zadeklarowany wcześniej, przed useConnections) ---
  const [terrainLoading, setTerrainLoading] = useState(false);
  const [terrainError, setTerrainError] = useState(null);
  const [clipToTerrain, setClipToTerrain] = useState(false); // Przycinaj warstwy do linii terenu

  // Historia Undo/Redo — śledzi wszystkie kluczowe stany edytowalne przez użytkownika.
  // Każda zmiana po 800ms debounce trafia do undoStack; Ctrl+Z przywraca poprzedni snapshot.
  const {
    undo: undoHistory,
    redo: redoHistory,
    canUndo, canRedo,
  } = useUndoRedo(
    {
      boreholes,
      manualConnections,
      lenses,
      drawings,
      interpolationPoints,
      connectionPointAdjustments,
      excludedAutoConnections,
      terrainProfile,
    },
    {
      boreholes: setBoreholes,
      manualConnections: setManualConnections,
      lenses: setLenses,
      drawings: setDrawings,
      interpolationPoints: setInterpolationPoints,
      connectionPointAdjustments: setConnectionPointAdjustments,
      excludedAutoConnections: setExcludedAutoConnections,
      terrainProfile: setTerrainProfile,
    }
  );

  // --- Wirtualny otwór: kliknięcie na przekroju ---
  const handleVirtualBoreholeClick = (svgX) => {
    // Zamień SVG x → distance w metrach
    const distanceM = (svgX - 100) / horizontalScale;

    // Znajdź parę sąsiednich otworów, między którymi kliknięto
    const sorted = [...boreholes].sort((a, b) => a.distance - b.distance);
    let leftBh = null;
    let rightBh = null;
    for (let i = 0; i < sorted.length - 1; i++) {
      if (distanceM >= sorted[i].distance && distanceM <= sorted[i + 1].distance) {
        leftBh = sorted[i];
        rightBh = sorted[i + 1];
        break;
      }
    }

    if (!leftBh || !rightBh) {
      alert('Kliknij między dwoma otworami na przekroju.');
      return;
    }

    if (leftBh.layers.length === 0 || rightBh.layers.length === 0) {
      alert('Oba sąsiednie otwory muszą mieć warstwy.');
      return;
    }

    // Generuj warstwy wirtualnego otworu
    const result = generateVirtualBoreholeLayers(leftBh, rightBh, distanceM);
    if (!result.layers.length) {
      alert('Nie udało się wygenerować warstw.');
      return;
    }

    // Utwórz nowy otwór z flagą isVirtual
    const newId = Math.max(0, ...boreholes.map(b => b.id)) + 1;
    const virtualBorehole = {
      id: newId,
      name: `V-${newId}`,
      depth: result.depth,
      distance: Math.round(distanceM * 100) / 100,
      isVirtual: true,
      waterLevels: [],
      layers: result.layers,
    };

    // Wstaw w odpowiednie miejsce (posortowane wg distance)
    const insertIndex = boreholes.findIndex(b => b.distance > distanceM);
    const newBoreholes = [...boreholes];
    if (insertIndex === -1) {
      newBoreholes.push(virtualBorehole);
    } else {
      newBoreholes.splice(insertIndex, 0, virtualBorehole);
    }
    setBoreholes(newBoreholes);

    // Wyłącz tryb po wstawieniu
    setVirtualBoreholeMode(false);
  };

  // Reinterpolacja warstw: usuwa wszystkie freeDraw polygony, włącza auto-connect,
  // i po cyklu renderowania konwertuje aktualne połączenia warstw na świeże freeDraw.
  // Używane po przesunięciu otworu (drag) albo po wstawieniu wirtualnego otworu,
  // żeby białe luki między nowym otworem a sąsiednimi warstwami zostały wypełnione.
  const handleReinterpolateLayers = () => {
    setManualConnections(prev => prev.filter(mc => mc.fromLayerId !== 'freeDraw'));
    setAutoConnectByName(true);
    setExcludedAutoConnections([]);
    setConnectLayers(true);
    setTimeout(() => {
      convertConnectionsToFreeDraw();
    }, 100);
  };

  const handleFetchTerrain = async () => {
    const withCoords = boreholes.filter(b => b.coordX != null && b.coordY != null);
    if (withCoords.length < 2) {
      const msg = `Minimum 2 otwory z współrzędnymi PL-2000 (znaleziono: ${withCoords.length}). Wpisz coordX i coordY dla otworów.`;
      setTerrainError(msg);
      console.warn('NMT:', msg);
      return;
    }
    setTerrainLoading(true);
    setTerrainError(null);
    try {
      const { profile, source } = await fetchTerrainProfile(boreholes, 10);
      // Skoryguj profil terenu — wymuś przejście przez rzędne otworów.
      // Oblicz offset (borehole.depth - NMT) na każdym otworze i interpoluj liniowo
      // dla punktów pośrednich, żeby kształt terenu z GUGiK był zachowany.
      const corrected = [...profile];
      const anchors = []; // {distance, offset}
      for (const bh of withCoords) {
        const idx = corrected.findIndex(pt => Math.abs(pt.distance - bh.distance) < 0.01);
        if (idx !== -1) {
          const offset = bh.depth - corrected[idx].elevation;
          anchors.push({ distance: bh.distance, offset });
          corrected[idx] = { ...corrected[idx], elevation: bh.depth };
        }
      }
      // Interpoluj offset dla punktów pośrednich
      if (anchors.length >= 2) {
        for (let i = 0; i < corrected.length; i++) {
          const d = corrected[i].distance;
          // Znajdź dwa sąsiednie anchory
          if (anchors.some(a => Math.abs(a.distance - d) < 0.01)) continue; // sam anchor
          let left = anchors[0], right = anchors[anchors.length - 1];
          for (let j = 0; j < anchors.length - 1; j++) {
            if (anchors[j].distance <= d && anchors[j + 1].distance >= d) {
              left = anchors[j];
              right = anchors[j + 1];
              break;
            }
          }
          const t = right.distance === left.distance ? 0 : (d - left.distance) / (right.distance - left.distance);
          const offset = left.offset + t * (right.offset - left.offset);
          corrected[i] = { ...corrected[i], elevation: corrected[i].elevation + offset };
        }
      }
      setTerrainProfile(corrected);
      setTerrainError(null);
    } catch (err) {
      console.error('Blad pobierania NMT:', err);
      setTerrainError(err.message || 'Nie udało się pobrać danych NMT');
    } finally {
      setTerrainLoading(false);
    }
  };

  // --- Auto-pipeline: automatyczne przetwarzanie importowanych przekrojów ---
  const autoPipelineRanRef = useRef(false);
  const [autoPipelineStep, setAutoPipelineStep] = useState(null); // null | 'connect' | 'fetchTerrain' | 'clip' | 'convert' | 'realize'

  const runAutoInterpolationPipeline = useCallback(() => {
    if (boreholes.length < 2) {
      window.alert('Pipeline wymaga co najmniej 2 otworów na przekroju.');
      return;
    }
    autoPipelineRanRef.current = true;
    setConnectLayers(true);
    setAutoConnectByName(true);
    setAutoConnectMode('byWeatheringProfile');
    setAutoPipelineStep('fetchTerrain');
  }, [boreholes.length, setConnectLayers, setAutoConnectByName, setAutoConnectMode]);

  // Krok 1: Uruchom pipeline dla importowanych otworów (tylko gdy autoInterpolate, np. z 3D)
  useEffect(() => {
    if (!isImported || !autoInterpolate || autoPipelineRanRef.current || boreholes.length < 2) return;
    // Włącz wyświetlanie wody jeśli któryś importowany otwór ma waterLevels
    // (przekroje z modelu 3D dostają interpolowane zwierciadło wody — sekcja 34/cross-section).
    if (boreholes.some(b => b.waterLevels && b.waterLevels.length > 0)) {
      setShowWater(true);
    }
    runAutoInterpolationPipeline();
  }, [isImported, autoInterpolate, boreholes.length, runAutoInterpolationPipeline, setShowWater, boreholes]);

  // Krok 2: Pobierz topografię (po włączeniu auto-connect)
  useEffect(() => {
    if (autoPipelineStep !== 'fetchTerrain') return;
    const withCoords = boreholes.filter(b => b.coordX != null && b.coordY != null);
    if (withCoords.length < 2) {
      // Brak współrzędnych — pomiń pobieranie terenu, przejdź do konwersji
      console.warn('Auto-pipeline: brak współrzędnych PL-2000, pomijam pobieranie NMT');
      setAutoPipelineStep('convert');
      return;
    }
    (async () => {
      try {
        setTerrainLoading(true);
        setTerrainError(null);
        const { profile } = await fetchTerrainProfile(boreholes, 10);
        // Korekta terenu — wymuś przejście przez rzędne otworów
        const corrected = [...profile];
        const anchors = [];
        const withCoords2 = boreholes.filter(b => b.coordX != null && b.coordY != null);
        for (const bh of withCoords2) {
          const idx = corrected.findIndex(pt => Math.abs(pt.distance - bh.distance) < 0.01);
          if (idx !== -1) {
            const offset = bh.depth - corrected[idx].elevation;
            anchors.push({ distance: bh.distance, offset });
            corrected[idx] = { ...corrected[idx], elevation: bh.depth };
          }
        }
        if (anchors.length >= 2) {
          for (let i = 0; i < corrected.length; i++) {
            const d = corrected[i].distance;
            if (anchors.some(a => Math.abs(a.distance - d) < 0.01)) continue;
            let left = anchors[0], right = anchors[anchors.length - 1];
            for (let j = 0; j < anchors.length - 1; j++) {
              if (anchors[j].distance <= d && anchors[j + 1].distance >= d) {
                left = anchors[j];
                right = anchors[j + 1];
                break;
              }
            }
            const t = right.distance === left.distance ? 0 : (d - left.distance) / (right.distance - left.distance);
            const offset = left.offset + t * (right.offset - left.offset);
            corrected[i] = { ...corrected[i], elevation: corrected[i].elevation + offset };
          }
        }
        setTerrainProfile(corrected);
        setClipToTerrain(true);
        setShowTerrain(true);
        setAutoPipelineStep('convert');
      } catch (err) {
        console.error('Auto-pipeline: błąd NMT:', err);
        setTerrainError(err.message || 'Nie udało się pobrać danych NMT');
        // Kontynuuj mimo błędu — konwertuj warstwy bez terenu
        setAutoPipelineStep('convert');
      } finally {
        setTerrainLoading(false);
      }
    })();
  }, [autoPipelineStep, boreholes]);

  // Krok 3: Konwertuj warstwy (po pobraniu terenu, potrzebuje jednego cyklu renderowania z auto-connect)
  useEffect(() => {
    if (autoPipelineStep !== 'convert') return;
    // Daj Reactowi jeden cykl renderowania żeby findLayerConnections() miało dane z auto-connect
    const timer = setTimeout(() => {
      convertConnectionsToFreeDraw();
      setAutoPipelineStep('realize');
    }, 100);
    return () => clearTimeout(timer);
  }, [autoPipelineStep]);

  // Krok 4: Urealnij warstwy (po konwersji + jeśli jest teren)
  useEffect(() => {
    if (autoPipelineStep !== 'realize') return;
    // Daj Reactowi cykl renderowania żeby manualConnections miały skonwertowane freeDraw
    const timer = setTimeout(() => {
      if (terrainProfile && terrainProfile.length >= 2) {
        applyTerrainShapeToLayers();
      }
      setAutoPipelineStep(null);
    }, 100);
    return () => clearTimeout(timer);
  }, [autoPipelineStep, terrainProfile]);

  // Przelicz prostokąt wydruku przy zmianie orientacji / formatu — zachowuje środek
  useEffect(() => {
    if (!printSelectMode) return;
    setPrintSelectRect(prev => {
      if (!prev) return prev;
      const center = { x: prev.x + prev.w / 2, y: prev.y + prev.h / 2 };
      return computePrintRectAround(printSelectFormat, printOrientation, center);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printSelectFormat, printOrientation]);

  // Auto-clear komunikatu po cięciu polilinią (5 s)
  useEffect(() => {
    if (!polylineSplitStatus) return;
    const t = setTimeout(() => setPolylineSplitStatus(null), 5000);
    return () => clearTimeout(t);
  }, [polylineSplitStatus, setPolylineSplitStatus]);

  // Obsługa klawisza Escape w trybie pełnoekranowym
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showPrintDialog) {
          setShowPrintDialog(false);
        } else if (printSelectMode) {
          setPrintSelectMode(false);
          setPrintSelectRect(null);
          setPrintDrag(null);
        } else if (editPropsDialog) {
          setEditPropsDialog(null);
        } else if (mergePolygonsMode) {
          setMergePolygonsMode(false);
          setMergeFromConnId(null);
        } else if (splitPickMode) {
          setSplitPickMode(false);
        } else if (splitMode) {
          cancelSplit();
        } else if (polylineSplitMode) {
          cancelPolylineSplit();
        } else if (stickyLayerLock && lensInLayerParentId != null) {
          // W sticky lock: pierwszy ESC odpina parent + wyłącza rysowanie (powrót do fazy "wybierz warstwę")
          setFreeDrawMode(false);
          setLensInLayerParentId(null);
          setFreeDrawPoints([]);
          setFreeDrawPreview(null);
        } else if (stickyLayerLock) {
          // Drugi ESC (już bez parenta) wyłącza sticky lock całkiem
          setStickyLayerLock(false);
        } else if (lensInLayerMode) {
          cancelLensInLayer();
        } else if (cutHoleMode) {
          cancelCutHole();
        } else if (contextMenuFreeDraw) {
          setContextMenuFreeDraw(null);
        } else if (editingFreeDraw !== null) {
          setEditingFreeDraw(null);
        } else if (selectedFreeDraw !== null || (selectedFreeDrawIds && selectedFreeDrawIds.length > 0)) {
          setSelectedFreeDraw(null);
          setSelectedFreeDrawIds([]);
        } else if (freeDrawMode && freeDrawPoints.length > 0) {
          setFreeDrawPoints([]);
        } else if (freeDrawMode) {
          setFreeDrawMode(false);
        } else if (fullscreenMode) {
          setFullscreenMode(false);
        }
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFreeDraw !== null) {
        // Nie usuwaj poligonu gdy: tabela atrybutów otwarta (user edytuje pola)
        // lub focus jest na polu tekstowym (input/textarea/contentEditable).
        if (showAttributeTable) return;
        const t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        deleteFreeDraw(selectedFreeDraw);
      }
      // Strzałki — nudge zaznaczonych poligonów o stały krok w metrach.
      // Krok: Alt = 0.1 m (precyzyjny), Shift = 2 m (gruby), default = 0.5 m.
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (showAttributeTable) return;
        const t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        const ids = (selectedFreeDrawIds && selectedFreeDrawIds.length > 0)
          ? selectedFreeDrawIds
          : (selectedFreeDraw != null ? [selectedFreeDraw] : []);
        if (ids.length === 0) return;
        e.preventDefault();
        const step = e.altKey ? 0.1 : (e.shiftKey ? 2.0 : 0.5);
        let dx = 0, dy = 0;
        if (e.key === 'ArrowUp') dy = -step;
        else if (e.key === 'ArrowDown') dy = step;
        else if (e.key === 'ArrowLeft') dx = -step;
        else if (e.key === 'ArrowRight') dx = step;
        shiftFreeDrawsByMeters(ids, dx, dy);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenMode, setFullscreenMode, freeDrawMode, freeDrawPoints, setFreeDrawPoints, setFreeDrawMode, selectedFreeDraw, setSelectedFreeDraw, selectedFreeDrawIds, setSelectedFreeDrawIds, editingFreeDraw, setEditingFreeDraw, deleteFreeDraw, splitMode, cancelSplit, polylineSplitMode, cancelPolylineSplit, contextMenuFreeDraw, setContextMenuFreeDraw, lensInLayerMode, cancelLensInLayer, cutHoleMode, cancelCutHole, editPropsDialog, setEditPropsDialog, showAttributeTable, shiftFreeDrawsByMeters]);

  // Skróty Undo/Redo + Copy/Paste: Ctrl+Z, Ctrl+Y, Ctrl+Shift+Z, Ctrl+C, Ctrl+V
  useEffect(() => {
    const handleHistoryKeys = (e) => {
      // Pomijaj gdy użytkownik pisze w polu input/textarea
      const tag = e.target?.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;
      if (isEditable) return;

      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      const key = e.key.toLowerCase();

      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoHistory();
      } else if ((key === 'y') || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redoHistory();
      } else if (key === 'c' && selectedFreeDraw !== null) {
        // Kopiuj zaznaczony freeDraw — zapamiętaj punkty w metrach (free), niezależne od otworów
        const mc = manualConnections.find(m => m.id === selectedFreeDraw);
        if (!mc || !mc.points || mc.points.length < 3) return;
        e.preventDefault();
        const ppm = scale / 10;
        const freePoints = mc.points.map(p => {
          const svg = resolvePointSvg(p);
          return {
            distanceM: (svg.x - 100) / horizontalScale,
            depthM: (svg.y - 80) / ppm,
          };
        });
        // Centroid w metrach — odniesienie dla paste
        const cx = freePoints.reduce((s, p) => s + p.distanceM, 0) / freePoints.length;
        const cy = freePoints.reduce((s, p) => s + p.depthM, 0) / freePoints.length;
        setClipboardPolygon({
          points: freePoints,
          centroidM: { distanceM: cx, depthM: cy },
          color: mc.color,
          pattern: mc.pattern,
          name: mc.name,
          controlPoints: mc.controlPoints ? { ...mc.controlPoints } : undefined,
        });
      } else if (key === 'v' && clipboardPolygon) {
        e.preventDefault();
        // Pozycja kursora w metrach
        const ppm = scale / 10;
        const cur = cursorSvgPosRef.current;
        const cursorDistanceM = (cur.x - 100) / horizontalScale;
        const cursorDepthM = (cur.y - 80) / ppm;
        // Offset = kursor - centroid skopiowanego polygonu
        const dx = cursorDistanceM - clipboardPolygon.centroidM.distanceM;
        const dy = cursorDepthM - clipboardPolygon.centroidM.depthM;
        const newPoints = clipboardPolygon.points.map(p => ({
          distanceM: p.distanceM + dx,
          depthM: p.depthM + dy,
        }));
        const newId = Math.max(0, ...manualConnections.map(m => m.id || 0)) + 1;
        const newConn = {
          id: newId,
          fromLayerId: 'freeDraw',
          toLayerId: 'freeDraw',
          color: clipboardPolygon.color || `hsl(${Math.floor(Math.random() * 360)}, 60%, 65%)`,
          pattern: clipboardPolygon.pattern,
          name: clipboardPolygon.name,
          points: newPoints,
          controlPoints: clipboardPolygon.controlPoints ? { ...clipboardPolygon.controlPoints } : undefined,
          createdAt: Date.now(),
        };
        setManualConnections(prev => [...prev, newConn]);
        setSelectedFreeDraw(newId);
      }
    };
    document.addEventListener('keydown', handleHistoryKeys);
    return () => document.removeEventListener('keydown', handleHistoryKeys);
  }, [undoHistory, redoHistory, selectedFreeDraw, manualConnections, clipboardPolygon, horizontalScale, scale, resolvePointSvg, setManualConnections, setSelectedFreeDraw]);

  // Dropdowny w pasku narzędzi: zamykaj wszystkie gdy klik trafia poza:
  //   (a) przycisk toggle (button sam robi toggle)
  //   (b) zawartość samego dropdownu (pozycja menu — onClick wykonuje akcję, potem ten handler i tak zamyka)
  // Używamy `click` (nie mousedown), żeby React zdążył obsłużyć click w pozycję menu
  // ZANIM ten handler odmontuje dropdown z DOM.
  useEffect(() => {
    const anyOpen = showConnectionsMenu || showEditModesMenu || showPolygonOpsMenu || showMoreOptions || showPrecisionPanel;
    if (!anyOpen) return;
    const closeAll = () => {
      setShowConnectionsMenu(false);
      setShowEditModesMenu(false);
      setShowPolygonOpsMenu(false);
      setShowMoreOptions(false);
      setShowPrecisionPanel(false);
    };
    const onClick = (e) => {
      const target = e.target;
      if (!target || !target.closest) { closeAll(); return; }
      // Klik na sam toggle button — button sam toggle'uje, nie zamykaj globalnie
      if (target.closest('[data-toolbar-dropdown-toggle]')) return;
      // Klik wewnątrz containera dropdownu (toggle + menu) — np. wybór z sub-selecta
      // typu „Wg nazwy gruntu" w menu Połączenia. Container to .relative.inline-block
      // zawierający button z data-toolbar-dropdown-toggle.
      const container = target.closest('.relative.inline-block');
      if (container && container.querySelector('[data-toolbar-dropdown-toggle]')) return;
      closeAll();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closeAll();
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showConnectionsMenu, showEditModesMenu, showPolygonOpsMenu, showMoreOptions, showPrecisionPanel]);

  // Pływający panel narzędzi: tylko Esc zamyka. Klik w akcję NIE zamyka,
  // żeby dropdowny i akcje można było bezpiecznie używać bez chowania panelu.
  // Zamknięcie: ×, Esc lub Shift+prawy klik ponownie (toggle).
  useEffect(() => {
    if (!quickToolbar) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setQuickToolbar(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [quickToolbar]);

  // Wheel zoom — non-passive listener (React onWheel jest passive, preventDefault by nie zadziałał).
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const handleWheel = (e) => {
      // Wymagamy Ctrl (lub fullscreen) żeby nie kolidować ze scrollem strony
      if (!fullscreenMode && !e.ctrlKey) return;
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const oldZoom = viewZoom;
      const newZoom = Math.max(0.05, Math.min(20, oldZoom * factor));
      if (newZoom === oldZoom) return;
      const rect = svgEl.getBoundingClientRect();
      const baseW = getSvgWidth();
      const baseH = getSvgHeight();
      const mouseSvgX = (e.clientX - rect.left) * (baseW / viewZoom) / rect.width + viewPanX;
      const mouseSvgY = (e.clientY - rect.top) * (baseH / viewZoom) / rect.height + viewPanY;
      const newPanX = mouseSvgX - (e.clientX - rect.left) * (baseW / newZoom) / rect.width;
      const newPanY = mouseSvgY - (e.clientY - rect.top) * (baseH / newZoom) / rect.height;
      setViewZoom(newZoom);
      setViewPanX(newPanX);
      setViewPanY(newPanY);
    };
    svgEl.addEventListener('wheel', handleWheel, { passive: false });
    return () => svgEl.removeEventListener('wheel', handleWheel);
  }, [fullscreenMode, viewZoom, viewPanX, viewPanY, scale, horizontalScale, boreholes]);

  // Klawisz Space — przytrzymanie aktywuje pan widoku (lewy klik + drag)
  useEffect(() => {
    const handleSpaceDown = (e) => {
      if (e.code !== 'Space') return;
      const tag = e.target?.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;
      if (isEditable) return;
      e.preventDefault();
      spaceHeldRef.current = true;
    };
    const handleSpaceUp = (e) => {
      if (e.code !== 'Space') return;
      spaceHeldRef.current = false;
    };
    document.addEventListener('keydown', handleSpaceDown);
    document.addEventListener('keyup', handleSpaceUp);
    return () => {
      document.removeEventListener('keydown', handleSpaceDown);
      document.removeEventListener('keyup', handleSpaceUp);
    };
  }, []);

  // Auto-save: informuj rodzica o zmianach danych
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        boreholes, scale, horizontalScale, drawings, lenses,
        connectLayers, autoConnectByName, autoConnectMode,
        showGrid, showBoreholeRectangles, showSoilName, showSoilState,
        showGeotechnicalLayer, showGenesisAndStratigraphy, showThickness, usePastelColors,
        manualConnections, excludedAutoConnections,
        connectionPointAdjustments, interpolationPoints,
        drawingColor, scaleDrawingTools,
        terrainProfile,
        activeMeasurements,
        showPolygonLabels,
        sharpenContours,
        labelFieldOrder,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boreholes, scale, horizontalScale, drawings, lenses,
      connectLayers, autoConnectByName, autoConnectMode, manualConnections,
      excludedAutoConnections, connectionPointAdjustments, interpolationPoints,
      terrainProfile, activeMeasurements, showPolygonLabels, sharpenContours, labelFieldOrder]);

  // Wrapper na toPastelColor z uwzględnieniem stanu usePastelColors
  const toPastelColor = (hexColor) => toPastelColorUtil(hexColor, usePastelColors);

  // Kolor warstwy w kolumnie otworu. W trybie "Wg grupy gruntu" cała grupa ma
  // jeden kolor — Pd i Pπ to oba piaski, więc mają wyglądać tak samo, a kolumna
  // zgadza się ze strefą interpolacji obok. Kolor świadomie ustawiony (ręczny
  // albo z numeru warstwy geotechnicznej) zawsze wygrywa nad kolorem grupy.
  const effectiveLayerColor = (layer) => {
    if (autoConnectMode !== 'bySoilGroup' || layerHasOwnColor(layer)) return layer.color;
    const group = getSoilGroup(layer.name);
    return (group && SOIL_GROUP_COLORS[group]) || layer.color;
  };
  // Wrapper na toDarkerColor: gdy sharpenContours OFF zwraca kolor bez zmiany
  const toContourColor = (hexColor) => sharpenContours ? toDarkerColorUtil(hexColor) : hexColor;

  const createBezierPath = createBezierPathUtil;

  const getTotalThickness = getTotalThicknessUtil;

  // Export/Import - wrappery na hooki z dodatkowym stanem
  const exportData = () => {
    exportDataHook({
      connectLayers,
      autoConnectByName,
      autoConnectMode,
      showGrid,
      showBoreholeRectangles,
      showSoilName,
      showSoilState,
      showGeotechnicalLayer,
      showGenesisAndStratigraphy,
      showThickness,
      usePastelColors,
      showWater,
      showBoreholeMeta,
      boreholeMetaOffsets,
      showElevationScaleLeft,
      showCrossSectionTitle,
      crossSectionTitle,
      crossSectionTitlePos,
      typography,
      manualConnections,
      excludedAutoConnections,
      connectionPointAdjustments,
      interpolationPoints,
      drawingColor,
      scaleDrawingTools,
      terrainProfile,
      activeMeasurements,
      showPolygonLabels,
      sharpenContours,
      labelFieldOrder,
    });
  };

  const importData = (event) => {
    importDataHook(event, (data) => {
      // Ustaw stany z innych hooków
      setConnectLayers(data.settings.connectLayers);
      setAutoConnectByName(data.settings.autoConnectByName);
      if (data.settings.autoConnectMode) setAutoConnectMode(data.settings.autoConnectMode);
      setShowGrid(data.settings.showGrid);
      setShowBoreholeRectangles(data.settings.showBoreholeRectangles);
      setShowSoilName(data.settings.showSoilName);
      setShowSoilState(data.settings.showSoilState);
      setShowGeotechnicalLayer(data.settings.showGeotechnicalLayer);
      if (data.settings.showGenesisAndStratigraphy !== undefined) setShowGenesisAndStratigraphy(data.settings.showGenesisAndStratigraphy);
      setShowThickness(data.settings.showThickness);
      setUsePastelColors(data.settings.usePastelColors);
      if (data.settings.showWater !== undefined) setShowWater(data.settings.showWater);
      if (data.settings.showBoreholeMeta !== undefined) setShowBoreholeMeta(data.settings.showBoreholeMeta);
      if (data.settings.boreholeMetaOffsets) setBoreholeMetaOffsets(data.settings.boreholeMetaOffsets);
      if (data.settings.showElevationScaleLeft !== undefined) setShowElevationScaleLeft(data.settings.showElevationScaleLeft);
      if (data.settings.showCrossSectionTitle !== undefined) setShowCrossSectionTitle(data.settings.showCrossSectionTitle);
      if (data.settings.crossSectionTitle !== undefined) setCrossSectionTitle(data.settings.crossSectionTitle);
      if (data.settings.crossSectionTitlePos !== undefined) setCrossSectionTitlePos(data.settings.crossSectionTitlePos);
      if (data.settings.typography) {
        const merged = { ...DEFAULT_TYPOGRAPHY };
        for (const k of Object.keys(merged)) {
          merged[k] = { ...merged[k], ...(data.settings.typography[k] || {}) };
        }
        setTypography(merged);
      }
      if (data.settings.showPolygonLabels !== undefined) setShowPolygonLabels(data.settings.showPolygonLabels);
      if (data.settings.sharpenContours !== undefined) setSharpenContours(data.settings.sharpenContours);
      if (Array.isArray(data.settings.labelFieldOrder)) setLabelFieldOrder(data.settings.labelFieldOrder);
      setManualConnections(data.connections.manualConnections);
      setExcludedAutoConnections(data.connections.excludedAutoConnections);
      setConnectionPointAdjustments(data.connections.connectionPointAdjustments);
      setInterpolationPoints(data.connections.interpolationPoints);
      setDrawingColor(data.drawingSettings.drawingColor);
      setScaleDrawingTools(data.drawingSettings.scaleDrawingTools);
      if (data.terrainProfile) setTerrainProfile(data.terrainProfile);
      setActiveMeasurements(data.activeMeasurements || []);
    });
  };

  const getMaxDepth = () => getMaxDepthUtil(boreholes);

  const getMinDepth = () => {
    return Math.min(...boreholes.map(b => b.depth - getTotalThickness(b.layers)));
  };

  const renderVerticalScale = (side = 'right') => {
    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepth();
    const minDepth = getMinDepth();
    const topY = 80;
    const maxDist = getMaxDistance();
    const startX = 100;
    const scaleX = side === 'left'
      ? startX - 125
      : startX + maxDist * horizontalScale + 145;
    const labelAnchor = side === 'left' ? 'end' : 'start';
    const labelX = side === 'left' ? scaleX - 15 : scaleX + 25;

    const elements = [];
    const range = Math.ceil(maxDepth) - Math.floor(minDepth);

    const labelInterval = range > 50 ? 10 : 5;
    const tickInterval = 1;

    const scaleTopY = topY + (maxDepth - Math.ceil(maxDepth)) * pixelsPerMeter;
    const scaleBottomY = topY + (maxDepth - Math.floor(minDepth)) * pixelsPerMeter;

    elements.push(
      <line
        key={`scale-line-${side}`}
        x1={scaleX}
        y1={scaleTopY}
        x2={scaleX}
        y2={scaleBottomY}
        stroke="#000"
        strokeWidth="3"
      />
    );

    for (let depth = Math.floor(minDepth); depth <= Math.ceil(maxDepth); depth += tickInterval) {
      const y = topY + (maxDepth - depth) * pixelsPerMeter;
      const isLabelDepth = depth % labelInterval === 0;

      elements.push(
        <line
          key={`tick-${side}-${depth}`}
          x1={scaleX - (isLabelDepth ? 10 : 5)}
          y1={y}
          x2={scaleX + (isLabelDepth ? 10 : 5)}
          y2={y}
          stroke="#000"
          strokeWidth={isLabelDepth ? "2" : "1"}
        />
      );

      if (isLabelDepth) {
        elements.push(
          <text
            key={`label-${side}-${depth}`}
            x={labelX}
            y={y}
            textAnchor={labelAnchor}
            dominantBaseline="middle"
            fontSize={typography.elevationScale.size}
            fontWeight={typography.elevationScale.weight}
            fontFamily={typography.elevationScale.family}
          >
            {depth}
          </text>
        );
      }
    }

    return elements;
  };

  const renderHorizontalScale = () => {
    const startX = 100;
    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepth();

    let lowestY = 80;
    boreholes.forEach(borehole => {
      const depthOffset = (maxDepth - borehole.depth) * pixelsPerMeter;
      const boreholeBottom = 80 + depthOffset + getTotalThickness(borehole.layers) * pixelsPerMeter;
      if (boreholeBottom > lowestY) {
        lowestY = boreholeBottom;
      }
    });

    // Polygony połączeń (freeDraw + interpolacje) potrafią schodzić poniżej najniższego otworu —
    // jeśli o tym nie wiedzieć, oś nakłada się na warstwę. Uwzględniamy najniższy punkt każdego polygonu.
    (manualConnections || []).forEach(mc => {
      if (!mc || !mc.points || mc.points.length < 2) return;
      mc.points.forEach(p => {
        const sp = resolvePointSvg(p);
        if (sp && Number.isFinite(sp.y) && sp.y > lowestY) lowestY = sp.y;
      });
    });

    const scaleY = lowestY + 40;
    
    const elements = [];
    // Oś dystansów pokazuje tylko widoczne otwory (A/B) — hidden są pomocnicze dla interpolacji warstw
    const sortedBoreholes = [...boreholes].filter(b => !b.hidden).sort((a, b) => a.distance - b.distance);
    
    if (sortedBoreholes.length > 0) {
      const firstX = startX + sortedBoreholes[0].distance * horizontalScale;
      const lastX = startX + sortedBoreholes[sortedBoreholes.length - 1].distance * horizontalScale;
      
      elements.push(
        <line 
          key="h-scale-line" 
          x1={firstX} 
          y1={scaleY} 
          x2={lastX} 
          y2={scaleY} 
          stroke="#000" 
          strokeWidth="3" 
        />
      );
      
      sortedBoreholes.forEach((borehole, idx) => {
        const x = startX + borehole.distance * horizontalScale;
        
        elements.push(
          <line 
            key={`tick-${borehole.id}`}
            x1={x} 
            y1={scaleY - 10} 
            x2={x} 
            y2={scaleY + 10} 
            stroke="#000" 
            strokeWidth="2" 
          />
        );
        
        if (idx < sortedBoreholes.length - 1) {
          const nextBorehole = sortedBoreholes[idx + 1];
          const distance = nextBorehole.distance - borehole.distance;
          const midX = x + ((startX + nextBorehole.distance * horizontalScale) - x) / 2;
          
          elements.push(
            <text 
              key={`dist-${borehole.id}`}
              x={midX} 
              y={scaleY + 30} 
              textAnchor="middle" 
              fontSize="16"
              fontWeight="bold"
            >
              {parseFloat(distance.toFixed(2))}m
            </text>
          );
        }
      });
    }
    
    return elements;
  };

  /**
   * "Urealnienie warstw" — nadaje każdej krawędzi freeDraw polygonu
   * kształt proporcjonalny do profilu terenu.
   * Dla każdej krawędzi: interpoluje terrain profile między jej końcami,
   * wstawia dodatkowe punkty podążające za terenem (proporcjonalnie).
   */
  const applyTerrainShapeToLayers = () => {
    if (!terrainProfile || terrainProfile.length < 2) {
      alert('Brak profilu terenu. Najpierw pobierz teren (NMT).');
      return;
    }

    const freeDrawConns = manualConnections.filter(mc =>
      mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3
    );
    if (freeDrawConns.length === 0) {
      alert('Brak warstw freeDraw do urealnienia.');
      return;
    }

    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepth();

    // Terrain profile jako punkty w metrach (distance, depth od maxDepth)
    const terrainPts = terrainProfile.map(pt => ({
      distanceM: pt.distance,
      depthM: maxDepth - pt.elevation, // głębokość od poziomu odniesienia
    }));

    // Interpoluj terrain depth na danej odległości
    const getTerrainDepthAt = (distM) => {
      if (distM <= terrainPts[0].distanceM) return terrainPts[0].depthM;
      if (distM >= terrainPts[terrainPts.length - 1].distanceM) return terrainPts[terrainPts.length - 1].depthM;
      for (let i = 0; i < terrainPts.length - 1; i++) {
        const a = terrainPts[i];
        const b = terrainPts[i + 1];
        if (distM >= a.distanceM && distM <= b.distanceM) {
          const t = (distM - a.distanceM) / (b.distanceM - a.distanceM);
          return a.depthM + t * (b.depthM - a.depthM);
        }
      }
      return terrainPts[0].depthM;
    };

    // Konwertuj punkt freeDraw na metry
    const ptToM = (pt) => {
      if (pt.boreholeId) {
        const svg = resolvePointSvg(pt);
        return { distanceM: (svg.x - 100) / horizontalScale, depthM: (svg.y - 80) / pixelsPerMeter };
      }
      return { distanceM: pt.distanceM, depthM: pt.depthM };
    };

    const numIntermediatePoints = 8; // punktów pośrednich na krawędź

    setManualConnections(prev => prev.map(mc => {
      if (mc.fromLayerId !== 'freeDraw' || !mc.points || mc.points.length < 3) return mc;

      const pts = mc.points;
      const n = pts.length;

      // Konwertuj wszystkie punkty na metry
      const ptsM = pts.map(ptToM);

      // Dla każdej krawędzi: wstaw punkty pośrednie z kształtem terenu
      const newPoints = [];
      const newControlPoints = {};

      for (let i = 0; i < n; i++) {
        // Wierzchołek zapisywany jako wolny punkt w metrach — po urealnieniu polygon
        // jest odłączony od otworów, więc drag otworu nie rozjeżdża warstwy.
        const nextIdx = (i + 1) % n;
        const startM = ptsM[i];
        const endM = ptsM[nextIdx];

        newPoints.push({ distanceM: startM.distanceM, depthM: startM.depthM });

        // Terrain depth na początku i końcu krawędzi
        const terrainStart = getTerrainDepthAt(startM.distanceM);
        const terrainEnd = getTerrainDepthAt(endM.distanceM);

        // Prosta linia (bez terenu) - interpolacja liniowa depth
        const linearDepthAt = (t) => startM.depthM + t * (endM.depthM - startM.depthM);
        const linearDistAt = (t) => startM.distanceM + t * (endM.distanceM - startM.distanceM);

        // Terrain linia - interpolacja liniowa terrain
        const terrainLinearAt = (t) => terrainStart + t * (terrainEnd - terrainStart);

        // Wstaw punkty pośrednie
        for (let j = 1; j <= numIntermediatePoints; j++) {
          const t = j / (numIntermediatePoints + 1);
          const dist = linearDistAt(t);
          const linearDepth = linearDepthAt(t);
          const terrainLinear = terrainLinearAt(t);
          const terrainActual = getTerrainDepthAt(dist);

          // Offset terenu = różnica między rzeczywistym terenem a liniową interpolacją terenu
          const terrainOffset = terrainActual - terrainLinear;

          // Zastosuj ten sam offset do krawędzi
          const newDepth = linearDepth + terrainOffset;

          newPoints.push({
            distanceM: dist,
            depthM: newDepth,
          });
        }
      }

      return { ...mc, points: newPoints, controlPoints: {} };
    }));
  };

  const renderTerrainLine = () => {
    if (!terrainProfile || terrainProfile.length < 2 || !showTerrain) return null;

    const pixelsPerMeter = scale / 10;
    const maxDepth = getMaxDepth();

    // Konwertuj profil terenu na punkty SVG
    const svgPoints = terrainProfile.map(pt => ({
      x: 100 + pt.distance * horizontalScale,
      y: 80 + (maxDepth - pt.elevation) * pixelsPerMeter,
    }));

    // Gładka krzywa SVG
    let pathD = `M ${svgPoints[0].x} ${svgPoints[0].y}`;
    for (let i = 1; i < svgPoints.length; i++) {
      const prev = svgPoints[i - 1];
      const curr = svgPoints[i];
      const cpX = (prev.x + curr.x) / 2;
      pathD += ` C ${cpX} ${prev.y}, ${cpX} ${curr.y}, ${curr.x} ${curr.y}`;
    }

    return (
      <g className="terrain-profile">
        {/* Główna linia terenu */}
        <path
          d={pathD}
          fill="none"
          stroke="#5d4037"
          strokeWidth="2.5"
          strokeLinejoin="round"
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  };

  const getMaxDistance = () => getMaxDistanceUtil(boreholes);


  const getSvgHeight = () => {
    const base = getSvgHeightUtil(boreholes, scale);
    // Polygony połączeń mogą schodzić poniżej najniższego otworu — rozciągnij canvas SVG,
    // żeby oś pozioma (rysowana ~40 px pod najniższym elementem) i etykiety mieściły się w ramce.
    let polyBottom = 0;
    (manualConnections || []).forEach(mc => {
      if (!mc || !mc.points || mc.points.length < 2) return;
      mc.points.forEach(p => {
        const sp = resolvePointSvg(p);
        if (sp && Number.isFinite(sp.y) && sp.y > polyBottom) polyBottom = sp.y;
      });
    });
    const polyExtraNeeded = polyBottom + 80 - base; // 80 = SVG_BOTTOM_PADDING + axis + label gap
    return polyExtraNeeded > 0 ? base + polyExtraNeeded : base;
  };

  const getSvgWidth = () => getSvgWidthUtil(boreholes, horizontalScale);

  // Realne wymiary papieru ISO w mm (długi × krótki bok)
  const PAPER_DIMS_MM = {
    a3: { long: 420, short: 297 },
    a4: { long: 297, short: 210 },
  };

  // Margines wydruku w mm — musi być zsynchronizowany z `margin` w handleExport (poniżej).
  // Prostokąt zaznaczenia na ekranie pokazuje obszar drukowalny (po odjęciu marginesów),
  // żeby skala 1:N wyświetlana w UI dokładnie odpowiadała fizycznej skali na papierze.
  const PRINT_MARGIN_MM = 8;

  // 1mm ≈ 3.7795 CSS px (96 DPI). Używane do konwersji 1:N ↔ px/m oraz wymiarów papieru.
  const CSS_PX_PER_MM = 96 / 25.4;

  // Konwersje skali ekranowej ↔ skala wydruku 1:N:
  //   px/m = (1000 mm/m) × CSS_PX_PER_MM / N   →   N = 1000 × CSS_PX_PER_MM / (px/m)
  // Wewnętrznie: horizontalScale = px/m H, scale/10 = px/m V.
  const horizontalScaleToN = (pxPerM) => Math.round((1000 * CSS_PX_PER_MM) / pxPerM);
  const nToHorizontalScale = (n) => (1000 * CSS_PX_PER_MM) / Math.max(1, n);
  const verticalScaleToN = (scaleVal) => Math.round((1000 * CSS_PX_PER_MM) / (scaleVal / 10));
  const nToVerticalScale = (n) => 10 * (1000 * CSS_PX_PER_MM) / Math.max(1, n);

  // Prostokąt wydruku reprezentujący obszar terenu, jaki zmieści się na papierze przy aktualnych
  // skalach 1:N. Wymiar = (paper_mm − 2×margin) × CSS_PX_PER_MM, więc rect pokazuje obszar
  // drukowalny po odjęciu marginesów. W połączeniu z mapowaniem 1:1 px→mm w pdfExport
  // (gałąź customViewBox) daje wydruk dokładnie w skali 1:N pokazanej w UI.
  const computePrintRectAround = (fmt, orient, center) => {
    const dims = PAPER_DIMS_MM[fmt];
    const paperW_mm = orient === 'landscape' ? dims.long : dims.short;
    const paperH_mm = orient === 'landscape' ? dims.short : dims.long;
    const w = (paperW_mm - 2 * PRINT_MARGIN_MM) * CSS_PX_PER_MM;
    const h = (paperH_mm - 2 * PRINT_MARGIN_MM) * CSS_PX_PER_MM;
    return { x: center.x - w / 2, y: center.y - h / 2, w, h };
  };

  // Domyślny środek prostokąta — środek aktualnego viewportu
  const getViewportCenter = () => {
    const visibleW = getSvgWidth() / viewZoom;
    const visibleH = getSvgHeight() / viewZoom;
    return { x: viewPanX + visibleW / 2, y: viewPanY + visibleH / 2 };
  };

  return (
<div
  className="min-h-screen bg-gray-50 p-6"
  onMouseMove={(e) => {
    if (drawingMode) {
      handleDrawingMove(e);
    } else if (!interpolationMode) {
      handleDragMove(e);
      handleResizeMove(e);
    } else {
      handleInterpolationMouseMove(e);
      handleConnectionPointMouseMove(e);
    }
    handleWaterLevelMouseMove(e);
  }}
  onTouchMove={(e) => {
    if (drawingMode) {
      handleDrawingMove(e);
    } else if (!interpolationMode) {
      handleDragMove(e);
      handleResizeMove(e);
    } else {
      handleInterpolationMouseMove(e);
      handleConnectionPointMouseMove(e);
    }
    handleWaterLevelMouseMove(e);
  }}
  onMouseUp={() => {
    if (drawingMode) {
      handleDrawingEnd();
    } else if (!interpolationMode) {
      handleDragEnd();
      handleResizeEnd();
    } else {
      handleInterpolationMouseUp();
      handleConnectionPointMouseUp();
    }
    handleWaterLevelMouseUp();
  }}
  onTouchEnd={() => {
    if (drawingMode) {
      handleDrawingEnd();
    } else if (!interpolationMode) {
      handleDragEnd();
      handleResizeEnd();
    } else {
      handleInterpolationMouseUp();
      handleConnectionPointMouseUp();
    }
    handleWaterLevelMouseUp();
  }}
>
      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { size: landscape; margin: 1cm; }
        }
        .print-only { display: none; }
      `}</style>
      
      <div className="mx-auto" style={{ maxWidth: '96rem' }}>
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6 no-print">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Generator Przekrojów Geologicznych
          </h1>
          
          <div className="flex gap-4 mb-6 flex-wrap">
            <button
              onClick={() => navigate('/browse')}
              className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              <ArrowLeft size={20} />
              Cofnij
            </button>
            <button
              onClick={() => navigate('/projects')}
              className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              <FolderOpen size={20} />
              Zapisane
            </button>
            <button
              onClick={exportData}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              <Save size={20} />
              Eksportuj JSON
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 bg-brand-700 text-white px-4 py-2 rounded hover:bg-brand-800"
            >
              <Upload size={20} />
              Wczytaj JSON
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={importData}
              style={{ display: 'none' }}
            />
            {onReload && (
              <button
                onClick={onReload}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                title="Pobierz aktualne dane projektu z bazy (nadpisze biezacy stan)"
              >
                <RefreshCw size={20} />
                Odswiez z bazy
              </button>
            )}
            {onSaveToSource && (
              <button
                onClick={onSaveToSource}
                className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700"
                title="Zapisz dane otworow z powrotem do bazy danych otworow (currentProjects)"
              >
                <Database size={20} />
                Zapisz do bazy otworow
              </button>
            )}
            <button
              onClick={exportToPDF}
              className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              <Download size={20} />
              Drukuj/PDF
            </button>
            {DXF_EXPORT_ENABLED && (
              <button
                onClick={() => exportToDxf({
                  boreholes, scale, horizontalScale,
                  connectLayers, findLayerConnections,
                  interpolationPoints, connectionPointAdjustments,
                  lenses, drawings, terrainProfile,
                  manualConnections, resolvePointSvg,
                  showGrid, showLenses, showBoreholeRectangles,
                  showDepths, showSoilName, showSoilState,
                  showGeotechnicalLayer, showThickness, showTerrain,
                })}
                className="flex items-center gap-2 bg-brand-700 text-white px-4 py-2 rounded hover:bg-brand-800"
                title="Eksport przekroju do pliku DXF (eksperymentalne)"
              >
                <Download size={20} />
                Eksport DXF
              </button>
            )}
            {DXF_EXPORT_V2_ENABLED && (
              <button
                onClick={() => setShowDxfV2Dialog(true)}
                className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                title="Eksport DXF v2 - warstwy gruntu na dnie (eksperymentalne)"
              >
                <Download size={20} />
                DXF v2
              </button>
            )}
            {/* PRZYCISKI STERUJĄCE PRZENIESIONE DO SEKCJI "Przekrój Interaktywny" (linie ~1892-2007) */}
            {/* Przyciski: Połączenia, Siatka, Auto-łączenie, Dodaj warstwę ręcznie, Słupki, Interpolacja, Reset interpolacji, Pełny ekran, Reset do domyślnych */}

          </div>
        </div>

        <>
    {!fullscreenMode && (
      <div className="grid gap-6 mb-6 grid-cols-1">
            {/* Panel Edycji Otworów - ROZWIJANY */}
            <div className="bg-white rounded-lg shadow-lg">
              <button
                onClick={() => setShowBoreholeEditPanel(!showBoreholeEditPanel)}
                className="w-full flex justify-between items-center p-6 text-left hover:bg-gray-50 transition"
              >
                <h2 className="text-xl font-bold">⛏️ Edycja Otworów</h2>
                {showBoreholeEditPanel ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </button>
              {showBoreholeEditPanel && (
              <div className="px-6 pb-6">

            {/* Tabela otworów */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-left text-xs text-gray-600 uppercase tracking-wider">
                    <th className="px-2 py-2 w-8">#</th>
                    <th className="px-2 py-2">Nazwa</th>
                    <th className="px-2 py-2 w-20">Głęb. (m)</th>
                    <th className="px-2 py-2 w-20">Odl. (m)</th>
                    <th className="px-2 py-2 w-16">Warstwy</th>
                    <th className="px-2 py-2 w-20">Miąższ.</th>
                    <th className="px-2 py-2 w-32">Akcje</th>
                  </tr>
                </thead>
                <tbody>
              {boreholes.map((borehole, index) => {
                // Otwory hidden (pośrednie z przekroju 3D) nie zaśmiecają tabeli
                if (borehole.hidden) return null;
                const isExpanded = expandedBoreholes[borehole.id] === true;
                return (
                <React.Fragment key={borehole.id}>
                  {/* Wiersz główny otworu */}
                  <tr
                    onClick={() => setExpandedBoreholes(prev => ({ ...prev, [borehole.id]: !isExpanded }))}
                    className={`cursor-pointer border-b transition ${isExpanded ? 'bg-brand-50 font-semibold' : 'hover:bg-gray-50'} ${borehole.isVirtual ? 'text-emerald-700' : ''}`}
                  >
                    <td className="px-2 py-2 text-gray-400">{index + 1}</td>
                    <td className="px-2 py-2 font-medium">
                      {borehole.isVirtual && '🔮 '}{borehole.name}
                    </td>
                    <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                      <input type="number" step="0.01" value={borehole.depth}
                        onChange={(e) => updateBorehole(borehole.id, 'depth', parseFloat(e.target.value))}
                        className="w-full border border-gray-200 rounded px-1.5 py-1 text-sm bg-transparent hover:border-brand-300 focus:border-brand-700 focus:outline-none" />
                    </td>
                    <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                      <input type="number" step="0.1" value={Math.round(borehole.distance * 100) / 100}
                        onChange={(e) => updateBorehole(borehole.id, 'distance', parseFloat(e.target.value) || 0)}
                        className="w-full border border-gray-200 rounded px-1.5 py-1 text-sm bg-transparent hover:border-brand-300 focus:border-brand-700 focus:outline-none" />
                    </td>
                    <td className="px-2 py-2 text-center">{borehole.layers.length}</td>
                    <td className="px-2 py-2">{getTotalThickness(borehole.layers).toFixed(2)}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => moveBoreholeUp(borehole.id)} disabled={index === 0}
                          className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-200'}`} title="W górę">
                          <ChevronUp size={14} />
                        </button>
                        <button onClick={() => moveBoreholeDown(borehole.id)} disabled={index === boreholes.length - 1}
                          className={`p-1 rounded ${index === boreholes.length - 1 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-200'}`} title="W dół">
                          <ChevronDown size={14} />
                        </button>
                        <button onClick={() => addBoreholeAfter(borehole.id)}
                          className="p-1 rounded text-green-600 hover:bg-green-50" title="Dodaj otwór po tym">
                          <PlusCircle size={14} />
                        </button>
                        <button onClick={() => removeBorehole(borehole.id)}
                          className="p-1 rounded text-red-600 hover:bg-red-50" title="Usuń otwór">
                          <Trash2 size={14} />
                        </button>
                        {isExpanded ? <ChevronUp size={14} className="text-brand-700" /> : <ChevronDown size={14} className="text-gray-400" />}
                      </div>
                    </td>
                  </tr>

                  {/* Rozwinięte szczegóły */}
                  {isExpanded && (
                  <tr>
                    <td colSpan={7} className="px-4 py-4 bg-gray-50 border-b">
                  {/* Parametry otworu */}
                  <div className="grid grid-cols-6 gap-3 mb-4">
                    <div>
                      <label className="text-xs text-gray-500">Nazwa</label>
                      <input type="text" value={borehole.name}
                        onChange={(e) => updateBorehole(borehole.id, 'name', e.target.value)}
                        className="border rounded px-2 py-1 w-full text-sm font-semibold" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Głębokość (m n.p.m.)</label>
                      <input type="number" step="0.01" value={borehole.depth}
                        onChange={(e) => updateBorehole(borehole.id, 'depth', parseFloat(e.target.value))}
                        className="border rounded px-2 py-1 w-full text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Odległość (m)</label>
                      <input type="number" step="1" value={borehole.distance}
                        onChange={(e) => updateBorehole(borehole.id, 'distance', parseFloat(e.target.value) || 0)}
                        className="border rounded px-2 py-1 w-full text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">X (lok.)</label>
                      <input type="number" step="0.01" value={borehole.x !== undefined ? borehole.x : ''}
                        onChange={(e) => updateBorehole(borehole.id, 'x', parseFloat(e.target.value) || 0)}
                        className="border rounded px-2 py-1 w-full text-sm" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">PL-2000 X</label>
                      <input type="number" step="0.01" value={borehole.coordX != null ? borehole.coordX : ''}
                        onChange={(e) => updateBorehole(borehole.id, 'coordX', e.target.value ? parseFloat(e.target.value) : null)}
                        className="border rounded px-2 py-1 w-full text-sm" placeholder="5548123" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">PL-2000 Y</label>
                      <input type="number" step="0.01" value={borehole.coordY != null ? borehole.coordY : ''}
                        onChange={(e) => updateBorehole(borehole.id, 'coordY', e.target.value ? parseFloat(e.target.value) : null)}
                        className="border rounded px-2 py-1 w-full text-sm" placeholder="7423567" />
                    </div>
                  </div>

                  {/* Metadane otworu z bazy (read-only) — widoczne dla otworów wczytanych z bazy */}
                  {borehole.sourceBoreholeId && (
                    <div className="mb-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-700">
                      <span>
                        <span className="text-slate-500">👤 Nadzorował:</span>{' '}
                        <strong>{borehole.supervisingDrilling || <span className="text-slate-400 font-normal">—</span>}</strong>
                      </span>
                      <span>
                        <span className="text-slate-500">📅 Data wykonania:</span>{' '}
                        <strong>
                          {borehole.dateExecution
                            ? formatDateExecution(borehole.dateExecution)
                            : <span className="text-slate-400 font-normal">—</span>}
                        </strong>
                      </span>
                      {!borehole.supervisingDrilling && !borehole.dateExecution && (
                        <span className="text-amber-600 text-[10px]" title="Otwór wczytany przed dodaniem obsługi metadanych. PPM na nazwie otworu na przekroju → 'Cofnij do wartości z bazy' żeby pobrać świeże dane.">
                          (odśwież z bazy aby pobrać)
                        </span>
                      )}
                    </div>
                  )}

                  {/* Warstwy + Woda — obok siebie */}
                  <div className="flex gap-4">
                  {/* Tabela warstw */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="font-semibold text-xs text-gray-600 uppercase">Warstwy</h3>
                      <div className="flex items-center gap-2">
                        {mergeLayerSelection.boreholeId === borehole.id && mergeLayerSelection.layerIds.length >= 2 && (
                          <>
                            {isMergeSelectionContiguous(borehole.id, mergeLayerSelection.layerIds) ? (
                              <button onClick={openMergeLayerDialog}
                                className="text-emerald-700 hover:text-emerald-800 text-xs font-medium border border-emerald-300 bg-emerald-50 rounded px-2 py-0.5"
                                title="Trwale scal zaznaczone warstwy">
                                🔗 Scal ({mergeLayerSelection.layerIds.length})
                              </button>
                            ) : (
                              <span className="text-amber-700 text-[10px]" title="Zaznacz tylko warstwy sąsiadujące">
                                ⚠ niesąsiadujące
                              </span>
                            )}
                            <button onClick={clearMergeLayerSelection}
                              className="text-gray-500 hover:text-gray-700 text-xs"
                              title="Wyczyść zaznaczenie">✕</button>
                          </>
                        )}
                        <button onClick={() => addLayer(borehole.id)}
                          className="text-brand-700 hover:text-brand-800 text-xs font-medium">+ Dodaj</button>
                      </div>
                    </div>
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-gray-500 uppercase">
                          <th className="px-1 py-1 w-5" title="Scal warstwy"></th>
                          <th className="px-1 py-1 w-5" title="Rozwiń szczegóły"></th>
                          <th className="px-1 py-1 w-6"></th>
                          <th className="px-1 py-1 w-7">Kol.</th>
                          <th className="px-1 py-1 w-7">Szr.</th>
                          <th className="px-1 py-1 text-left">Grunt</th>
                          <th className="px-1 py-1 w-12">m</th>
                          <th className="px-1 py-1 text-left">Stan</th>
                          <th className="px-1 py-1 w-14">Warst.</th>
                          <th className="px-1 py-1 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                    {borehole.layers.map((layer, layerIndex) => {
                      const isSelectedForMerge =
                        mergeLayerSelection.boreholeId === borehole.id &&
                        mergeLayerSelection.layerIds.includes(layer.id);
                      const expanded = isLayerExpanded(borehole.id, layer.id);
                      return (
                      <React.Fragment key={layer.id}>
                      <tr className={`border-b border-gray-100 hover:bg-gray-50 ${isSelectedForMerge ? 'bg-emerald-50' : ''}`}>
                        <td className="px-1 py-0.5">
                          <input type="checkbox" checked={isSelectedForMerge}
                            onChange={() => toggleLayerMergeSelection(borehole.id, layer.id)}
                            className="cursor-pointer"
                            title="Zaznacz do scalenia" />
                        </td>
                        <td className="px-1 py-0.5">
                          <button onClick={() => {
                              if (!expanded && !layer.lithologyRockSoil01 && !layer.lithologyRockSoil02 && !layer.lithologyRockSoil03 && layer.name) {
                                const parsed = parseLithologyName(layer.name, { lithoDict: layerLithoDict, rockDict: layerRockDict, rockSubDict: layerRockSubDict, isRock: layer.materialType === 'skala' });
                                if (parsed.lithologyRockSoil01 || parsed.lithologyRockSoil02) {
                                  updateLayerFields(borehole.id, layer.id, parsed);
                                }
                              }
                              toggleLayerExpanded(borehole.id, layer.id);
                            }}
                            className="text-gray-500 hover:text-gray-800 cursor-pointer"
                            title="Rozwiń/zwiń szczegóły litologii (auto-parsuje nazwę)">
                            {expanded ? <ChevronDown size={11}/> : <ChevronRight size={11}/>}
                          </button>
                        </td>
                        <td className="px-1 py-0.5">
                          <div className="flex flex-col">
                            <button onClick={() => moveLayerUp(borehole.id, layerIndex)} disabled={layerIndex === 0}
                              className={`${layerIndex === 0 ? 'text-gray-200' : 'text-gray-500 hover:text-gray-800'}`}>
                              <ChevronUp size={11} />
                            </button>
                            <button onClick={() => moveLayerDown(borehole.id, layerIndex)} disabled={layerIndex === borehole.layers.length - 1}
                              className={`${layerIndex === borehole.layers.length - 1 ? 'text-gray-200' : 'text-gray-500 hover:text-gray-800'}`}>
                              <ChevronDown size={11} />
                            </button>
                          </div>
                        </td>
                        <td className="px-1 py-0.5">
                          <input type="color" value={layer.color}
                            onChange={(e) => updateLayer(borehole.id, layer.id, 'color', e.target.value)}
                            className="w-6 h-6 cursor-pointer border border-gray-300 rounded p-0" />
                        </td>
                        <td className="px-1 py-0.5">
                          <div className="relative">
                            <button type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const sel = e.currentTarget.nextElementSibling;
                                if (sel) sel.style.display = sel.style.display === 'none' ? 'flex' : 'none';
                              }}
                              className="w-6 h-6 border border-gray-300 rounded cursor-pointer bg-white flex items-center justify-center"
                              title={layer.pattern && layer.pattern !== 'none' ? layer.pattern : 'Brak szrafury'}>
                              <svg width="24" height="24" viewBox="0 0 24 24">
                                <defs>
                                  <pattern id={`tbl-${borehole.id}-${layer.id}`} x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                                    {layer.pattern === 'sand' && <><circle cx="1.5" cy="1.5" r="0.6" fill="#333"/><circle cx="4.5" cy="4.5" r="0.6" fill="#333"/></>}
                                    {layer.pattern === 'clay' && <line x1="0" y1="3" x2="6" y2="3" stroke="#333" strokeWidth="0.5"/>}
                                    {layer.pattern === 'gravel' && <><circle cx="2" cy="2" r="1.2" fill="#333"/><circle cx="5" cy="5" r="1.2" fill="#333"/></>}
                                    {layer.pattern === 'rock' && <><line x1="0" y1="0" x2="6" y2="6" stroke="#333" strokeWidth="0.5"/><line x1="6" y1="0" x2="0" y2="6" stroke="#333" strokeWidth="0.5"/></>}
                                  </pattern>
                                </defs>
                                <rect width="24" height="24" fill={layer.pattern && layer.pattern !== 'none' ? `url(#tbl-${borehole.id}-${layer.id})` : 'white'} stroke="#ccc" strokeWidth="0.5"/>
                              </svg>
                            </button>
                            <div style={{ display: 'none' }}
                              className="absolute left-7 top-0 bg-white border border-gray-300 rounded shadow-lg p-1 flex gap-1 z-50"
                              onClick={(e) => e.stopPropagation()}>
                              {[
                                { value: 'none', label: 'Brak' },
                                { value: 'sand', label: 'Piasek' },
                                { value: 'clay', label: 'Glina' },
                                { value: 'gravel', label: 'Żwir' },
                                { value: 'rock', label: 'Skała' }
                              ].map(({ value, label }) => (
                                <button key={value} type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateLayer(borehole.id, layer.id, 'pattern', value);
                                    e.currentTarget.parentElement.style.display = 'none';
                                  }}
                                  className={`w-7 h-7 border rounded cursor-pointer ${layer.pattern === value ? 'border-brand-700' : 'border-gray-300'}`}
                                  title={label}>
                                  <svg width="100%" height="100%" viewBox="0 0 28 28">
                                    <defs>
                                      <pattern id={`opt-${borehole.id}-${layer.id}-${value}`} x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                                        {value === 'sand' && <><circle cx="1.5" cy="1.5" r="0.6" fill="#333"/><circle cx="4.5" cy="4.5" r="0.6" fill="#333"/></>}
                                        {value === 'clay' && <line x1="0" y1="3" x2="6" y2="3" stroke="#333" strokeWidth="0.5"/>}
                                        {value === 'gravel' && <><circle cx="2" cy="2" r="1.2" fill="#333"/><circle cx="5" cy="5" r="1.2" fill="#333"/></>}
                                        {value === 'rock' && <><line x1="0" y1="0" x2="6" y2="6" stroke="#333" strokeWidth="0.5"/><line x1="6" y1="0" x2="0" y2="6" stroke="#333" strokeWidth="0.5"/></>}
                                      </pattern>
                                    </defs>
                                    <rect width="28" height="28" fill={value !== 'none' ? `url(#opt-${borehole.id}-${layer.id}-${value})` : 'white'} stroke="#ccc" strokeWidth="0.5"/>
                                  </svg>
                                </button>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="px-1 py-0.5">
                          <input type="text" value={layer.name}
                            onChange={(e) => updateLayer(borehole.id, layer.id, 'name', e.target.value)}
                            className="w-full border border-gray-200 rounded px-1 py-0.5 text-xs" placeholder="Grunt" />
                        </td>
                        <td className="px-1 py-0.5">
                          <input type="number" step="0.1" value={layer.thickness}
                            onChange={(e) => updateLayer(borehole.id, layer.id, 'thickness', parseFloat(e.target.value))}
                            className="w-full border border-gray-200 rounded px-1 py-0.5 text-xs text-center" />
                        </td>
                        <td className="px-1 py-0.5">
                          <input type="text" value={layer.soilState || ''}
                            onChange={(e) => updateLayer(borehole.id, layer.id, 'soilState', e.target.value)}
                            className="w-full border border-gray-200 rounded px-1 py-0.5 text-xs" placeholder="Stan" />
                        </td>
                        <td className="px-1 py-0.5">
                          <input type="text" value={layer.geotechnicalLayer || ''}
                            onChange={(e) => updateLayer(borehole.id, layer.id, 'geotechnicalLayer', e.target.value)}
                            className="w-full border border-gray-200 rounded px-1 py-0.5 text-xs" placeholder="Ig..." />
                        </td>
                        <td className="px-1 py-0.5">
                          <div className="flex gap-0.5">
                            <button onClick={() => addLayerAfter(borehole.id, layerIndex)}
                              className="text-green-600 hover:text-green-800"><PlusCircle size={12} /></button>
                            <button onClick={() => removeLayer(borehole.id, layer.id)}
                              className="text-red-600 hover:text-red-800"><Trash2 size={12} /></button>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <td colSpan={9} className="px-3 py-2">
                            <div className="grid grid-cols-12 gap-3 text-xs">
                              <div className="col-span-3">
                                <label className="block text-[10px] text-gray-500 mb-0.5 font-semibold uppercase">Od (m):</label>
                                <input type="number" step="0.01" value={layer.from != null ? layer.from : ''}
                                  onChange={(e) => updateLayer(borehole.id, layer.id, 'from', parseFloat(e.target.value))}
                                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs" />
                                <div className="mt-2">
                                  <label className="block text-[10px] text-gray-500 mb-0.5 font-semibold uppercase">Geneza i stratygrafia:</label>
                                  <select value={layer.genesisAndStratigraphy || ''}
                                    onChange={(e) => {
                                      const parts = (e.target.value || '').split(' - ');
                                      const short = (parts[0] || '').trim();
                                      updateLayer(borehole.id, layer.id, 'genesisAndStratigraphy', short);
                                    }}
                                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs">
                                    <option value="">-- brak --</option>
                                    {layerGenesisDict.map(e => <option key={e.fullString} value={e.fullString}>{e.fullString}</option>)}
                                  </select>
                                </div>
                              </div>
                              <div className="col-span-9">
                                <LithologyForm
                                  value={layer}
                                  onChange={(patch) => updateLayerFields(borehole.id, layer.id, patch)}
                                  lithoDict={layerLithoDict}
                                  rockDict={layerRockDict}
                                  rockSubDict={layerRockSubDict}
                                  compact
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                      </tbody>
                    </table>
                  </div>

                  {/* Poziomy wody — obok warstw */}
                  <div className="w-48 flex-shrink-0">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="font-semibold text-xs text-gray-600 uppercase">Woda</h3>
                      <div className="flex gap-1">
                        <button onClick={() => addWaterLevel(borehole.id, 'stabilized-drilled')}
                          className="text-brand-700 hover:text-brand-800 text-xs" title="Ustabilizowany/Nawiercony">+ U/N</button>
                        <button onClick={() => addWaterLevel(borehole.id, 'seepage')}
                          className="text-brand-700 hover:text-brand-800 text-xs" title="Sączenie">+ S</button>
                      </div>
                    </div>

                    {borehole.waterLevels && borehole.waterLevels.length > 0 ? (
                      <div className="space-y-1">
                        {borehole.waterLevels.map((waterLevel) => (
                          <div key={waterLevel.id} className="bg-cyan-50 p-1.5 rounded flex items-center gap-1 text-xs">
                            {waterLevel.type === 'stabilized-drilled' ? (
                              <>
                                <span className="text-blue-600">△▽</span>
                                <input type="number" step="0.1" value={waterLevel.depthFrom}
                                  onChange={(e) => updateWaterLevel(borehole.id, waterLevel.id, 'depthFrom', parseFloat(e.target.value))}
                                  className="border rounded px-1 py-0.5 w-14 text-xs" placeholder="Od" />
                                <input type="number" step="0.1" value={waterLevel.depthTo}
                                  onChange={(e) => updateWaterLevel(borehole.id, waterLevel.id, 'depthTo', parseFloat(e.target.value))}
                                  className="border rounded px-1 py-0.5 w-14 text-xs" placeholder="Do" />
                              </>
                            ) : (
                              <>
                                <span className="text-blue-600">∿</span>
                                <input type="number" step="0.1" value={waterLevel.depth}
                                  onChange={(e) => updateWaterLevel(borehole.id, waterLevel.id, 'depth', parseFloat(e.target.value))}
                                  className="border rounded px-1 py-0.5 flex-1 text-xs" placeholder="Głęb." />
                              </>
                            )}
                            <button onClick={() => removeWaterLevel(borehole.id, waterLevel.id)}
                              className="text-red-600 hover:text-red-800"><Trash2 size={11} /></button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">Brak</p>
                    )}
                  </div>
                  </div>

                    </td>
                  </tr>
                  )}
                </React.Fragment>
                );
              })}
                </tbody>
              </table>
            </div>
            </div>
            )}
          </div>

          {/* Panel Narzędzi Rysunkowych - ROZWIJANY */}
          <div className="bg-white rounded-lg shadow-lg">
            <button
              onClick={() => setShowDrawingToolsPanel(!showDrawingToolsPanel)}
              className="w-full flex justify-between items-center p-6 text-left hover:bg-gray-50 transition"
            >
              <h2 className="text-xl font-bold">🎨 Narzędzia Rysunkowe</h2>
              {showDrawingToolsPanel ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
            </button>
            {showDrawingToolsPanel && (
            <div className="px-6 pb-6">
            <div className="mb-3 p-3 bg-green-50 rounded text-sm text-green-800">
              🎨 <strong>Wybierz narzędzie</strong> i kliknij na przekroju aby rysować. Rysunki pojawią się w historii poniżej.
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setDrawingMode(drawingMode === 'line' ? null : 'line')}
                className={`px-3 py-2 rounded text-sm ${drawingMode === 'line' ? 'bg-brand-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                title="Rysuj linię"
              >
                ─ Linia
              </button>
              <button
                onClick={() => setDrawingMode(drawingMode === 'arrow' ? null : 'arrow')}
                className={`px-3 py-2 rounded text-sm ${drawingMode === 'arrow' ? 'bg-brand-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                title="Rysuj strzałkę"
              >
                → Strzałka
              </button>
              <button
                onClick={() => setDrawingMode(drawingMode === 'rect' ? null : 'rect')}
                className={`px-3 py-2 rounded text-sm ${drawingMode === 'rect' ? 'bg-brand-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                title="Rysuj prostokąt"
              >
                ▭ Prostokąt
              </button>
              <button
                onClick={() => setDrawingMode(drawingMode === 'circle' ? null : 'circle')}
                className={`px-3 py-2 rounded text-sm ${drawingMode === 'circle' ? 'bg-brand-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                title="Rysuj okrąg"
              >
                ○ Okrąg
              </button>
              <button
                onClick={() => setDrawingMode(drawingMode === 'text' ? null : 'text')}
                className={`px-3 py-2 rounded text-sm ${drawingMode === 'text' ? 'bg-brand-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                title="Dodaj tekst"
              >
                T Tekst
              </button>
              <button
                onClick={() => {
                  const isCurrentlyMeasuring = drawingMode === 'measure';
                  setDrawingMode(isCurrentlyMeasuring ? null : 'measure');
                  setMeasureFirstPoint(null);
                  setMeasurePreview(null);
                  // Jeśli wyłączamy tryb pomiaru, usuń wszystkie tymczasowe pomiary
                  if (isCurrentlyMeasuring) {
                    setActiveMeasurements([]);
                  }
                }}
                className={`px-3 py-2 rounded text-sm ${drawingMode === 'measure' ? 'bg-brand-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                title="Narzędzie pomiaru"
              >
                📏 Pomiar
              </button>
              <button
                onClick={() => {
                  setMoveDrawingMode(!moveDrawingMode);
                  // Wyłącz tryb rysowania gdy włączamy tryb przesuwania
                  if (!moveDrawingMode) {
                    setDrawingMode(null);
                  }
                }}
                className={`px-3 py-2 rounded text-sm ${moveDrawingMode ? 'bg-green-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                title="Przesuń rysunki i teksty"
              >
                ✋ Przesuń
              </button>
            </div>

            <div className="flex items-center gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 font-semibold">Kolor:</label>
                <input
                  type="color"
                  value={drawingColor}
                  onChange={(e) => setDrawingColor(e.target.value)}
                  className="w-12 h-10 cursor-pointer border rounded"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="scaleDrawingTools"
                  checked={scaleDrawingTools}
                  onChange={(e) => setScaleDrawingTools(e.target.checked)}
                  className="cursor-pointer"
                />
                <label htmlFor="scaleDrawingTools" className="text-sm text-gray-700 cursor-pointer">
                  Skaluj narzędzia z obrazkiem
                </label>
              </div>
            </div>

            {/* Historia Rysunków */}
            {drawings.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-bold">Historia Rysunków ({drawings.length})</h3>
                  <button
                    onClick={clearAllDrawings}
                    className="bg-red-600 text-white hover:bg-red-700 px-3 py-1 rounded text-sm font-semibold flex items-center gap-1"
                  >
                    <Trash2 size={16} />
                    Wyczyść wszystkie
                  </button>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {drawings.map((drawing, index) => (
                    <div key={drawing.id} className="flex items-center gap-3 bg-gray-50 p-3 rounded border border-gray-200 hover:bg-gray-100">
                      <div className="w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl" style={{ borderColor: drawing.color, color: drawing.color, backgroundColor: `${drawing.color}15` }}>
                        {drawing.type === 'pencil' && '✏️'}
                        {drawing.type === 'line' && '─'}
                        {drawing.type === 'arrow' && '→'}
                        {drawing.type === 'rect' && '▭'}
                        {drawing.type === 'circle' && '○'}
                        {drawing.type === 'text' && 'T'}
                        {drawing.type === 'measure' && '📏'}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">
                          {drawing.type === 'pencil' && 'Ołówek'}
                          {drawing.type === 'line' && 'Linia'}
                          {drawing.type === 'arrow' && 'Strzałka'}
                          {drawing.type === 'rect' && 'Prostokąt'}
                          {drawing.type === 'circle' && 'Okrąg'}
                          {drawing.type === 'text' && 'Tekst'}
                          {drawing.type === 'measure' && 'Pomiar'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Rysunek #{index + 1}
                        </div>
                      </div>
                      <button
                        onClick={() => removeDrawing(drawing.id)}
                        className="bg-red-600 text-white hover:bg-red-700 px-3 py-2 rounded text-sm font-semibold flex items-center gap-1"
                        title="Usuń ten rysunek"
                      >
                        <Trash2 size={14} />
                        Usuń
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>
            )}
          </div>

          {/* Panel Obiektów Geologicznych - TYMCZASOWO WYŁĄCZONY — przywróć zmieniając null na JSX */}
          {null}

        </div>
    )}

    <div className={`bg-white rounded-lg shadow-lg ${fullscreenMode ? 'fixed inset-0 z-50 overflow-auto p-0' : 'p-6'}`}>
            <div className={`flex justify-between items-center flex-wrap gap-2 ${fullscreenMode ? 'p-4 bg-gray-100 border-b' : 'mb-4'}`}>
              <h2 className="text-xl font-bold">
                Przekrój Interaktywny
              </h2>
              <div className="flex items-center gap-3 text-xs">
                {saveStatus && (
                  <span className={`px-2 py-1 rounded ${saveStatus.includes('Niezapisane') ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}>
                    📄 {saveStatus}
                  </span>
                )}
                {sourceSaveStatus && (
                  <span className={`px-2 py-1 rounded ${sourceSaveStatus.includes('Niezapisane') ? 'bg-red-100 text-red-700 font-semibold' : sourceSaveStatus.includes('Zapisywanie') ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-800'}`} title="Auto-zapis warstw, wody i nazw otworów do globalnej bazy otworów">
                    🗄️ {sourceSaveStatus}
                  </span>
                )}
              </div>
            </div>

            {/* Panel kontrolny - przyciski sterujące. W trybie floating (Shift+prawy klik) owinięty w fixed wrapper z drag handle. */}
            <div
              ref={quickToolbarRef}
              className={quickToolbar ? 'fixed z-[80] bg-white rounded-lg shadow-2xl border border-gray-300' : 'contents'}
              style={quickToolbar ? { left: quickToolbar.x, top: quickToolbar.y, maxWidth: 'min(96vw, 1200px)' } : undefined}
            >
              {quickToolbar && (
                <div
                  className="flex items-center justify-between px-3 py-1.5 bg-gray-200 border-b border-gray-300 cursor-move select-none"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    quickToolbarDragRef.current = {
                      offsetX: e.clientX - quickToolbar.x,
                      offsetY: e.clientY - quickToolbar.y,
                    };
                    const onMove = (ev) => {
                      if (!quickToolbarDragRef.current) return;
                      const w = window.innerWidth;
                      const h = window.innerHeight;
                      const nx = Math.max(0, Math.min(w - 100, ev.clientX - quickToolbarDragRef.current.offsetX));
                      const ny = Math.max(0, Math.min(h - 40, ev.clientY - quickToolbarDragRef.current.offsetY));
                      setQuickToolbar({ x: nx, y: ny });
                    };
                    const onUp = () => {
                      quickToolbarDragRef.current = null;
                      document.removeEventListener('mousemove', onMove);
                      document.removeEventListener('mouseup', onUp);
                    };
                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                  }}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <span className="text-gray-400">⋮⋮</span>
                    Przekrój Interaktywny — narzędzia
                  </span>
                  <button
                    onClick={() => setQuickToolbar(null)}
                    className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:bg-red-100 hover:text-red-600 text-xl leading-none"
                    title="Zamknij (wróć do paska na górze)"
                  >
                    ×
                  </button>
                </div>
              )}
            <div
              className={`flex flex-wrap gap-2 p-4 bg-gray-50 ${quickToolbar ? '' : 'border-b'}`}
              style={quickToolbar
                ? undefined
                : { borderBottom: '3px solid #b22723' }}
            >
                <div className="relative inline-block">
                  <button
                    onClick={() => setShowConnectionsMenu(!showConnectionsMenu)}
                    data-toolbar-dropdown-toggle="connections"
                    className={`flex items-center gap-2 px-4 py-2 rounded ${
                      (connectLayers || autoConnectByName)
                        ? 'bg-brand-700 text-white hover:bg-brand-800'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                    }`}
                    title="Ustawienia łączenia warstw między otworami"
                  >
                    <Link size={20} />
                    Połączenia {showConnectionsMenu ? '▲' : '▼'}
                  </button>
                  {showConnectionsMenu && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 p-2 flex flex-col gap-1 min-w-[260px]">
                      <button
                        onClick={() => setConnectLayers(!connectLayers)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          connectLayers ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                      >
                        <Link size={16} />
                        Połączenia: {connectLayers ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => setAutoConnectByName(!autoConnectByName)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          autoConnectByName ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Automatycznie łącz warstwy"
                      >
                        🔄 Auto-łączenie: {autoConnectByName ? 'ON' : 'OFF'}
                      </button>
                      {autoConnectByName && (
                        <select
                          value={autoConnectMode}
                          onChange={(e) => setAutoConnectMode(e.target.value)}
                          className="px-2 py-1.5 rounded bg-brand-800 text-white text-sm cursor-pointer"
                          title="Tryb łączenia warstw"
                        >
                          <option value="byName">Wg nazwy gruntu</option>
                          <option value="byGeotechnicalLayer">Wg nr warstwy geotechnicznej</option>
                          <option value="byWeatheringProfile">Wg profilu zwietrzelinowego</option>
                          <option value="byRomanPrefix">Wg nr warstwy rzymskiej</option>
                          <option value="bySoilGroup">Wg grupy gruntu</option>
                        </select>
                      )}
                      <button
                        onClick={() => { runAutoInterpolationPipeline(); setShowConnectionsMenu(false); }}
                        disabled={boreholes.length < 2 || autoPipelineStep !== null}
                        className="flex items-center gap-2 px-3 py-1.5 mt-1 rounded text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed border-t border-gray-200"
                        title="Auto-połącz warstwy wg profilu zwietrzeliny (grunt/zwietrzelina/skała), pobierz teren z NMT, urealnij polygony"
                      >
                        🔗 Połącz warstwy
                      </button>
                    </div>
                  )}
                </div>

                {interpolationMode && Object.keys(interpolationPoints).length > 0 && (
                  <button
                    onClick={() => {
                      if (window.confirm('Czy na pewno chcesz zresetować wszystkie punkty interpolacji (zielone)?')) {
                        setInterpolationPoints({});
                      }
                    }}
                    className="flex items-center gap-2 border border-brand-700 text-brand-700 bg-white px-4 py-2 rounded hover:bg-brand-50"
                  >
                    🔄 Reset interpolacji
                  </button>
                )}

                <div className="relative inline-block">
                  <button
                    onClick={() => setShowEditModesMenu(!showEditModesMenu)}
                    data-toolbar-dropdown-toggle="editModes"
                    className={`flex items-center gap-2 px-4 py-2 rounded ${
                      (freeDrawMode || freeDrawEditVerticesMode || freeDrawExpandMode)
                        ? 'bg-brand-700 text-white hover:bg-brand-800'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                    }`}
                    title="Tryby rysowania i edycji warstw"
                  >
                    {freeDrawMode ? '✏️ Rysowanie' :
                     freeDrawEditVerticesMode ? '📐 Edycja wierzchołków' :
                     freeDrawExpandMode ? '🔀 Rozszerzanie granic' :
                     '✏️ Rysowanie/Edycja'} {showEditModesMenu ? '▲' : '▼'}
                  </button>
                  {showEditModesMenu && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 p-2 flex flex-col gap-1 min-w-[280px]">
                      {/* Rysowanie */}
                      <button
                        onClick={() => {
                          const newMode = !freeDrawMode;
                          setFreeDrawMode(newMode);
                          if (newMode) {
                            setFreeDrawPoints([]);
                            setManualConnectionMode(false);
                            setConnectLayers(true);
                            setFreeDrawEditVerticesMode(false);
                            setFreeDrawExpandMode(false);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          freeDrawMode ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Rysuj połączenia ręcznie - punkty przyciągają się do krawędzi warstw"
                      >
                        ✏️ Rysowanie: {freeDrawMode ? 'ON' : 'OFF'}
                      </button>
                      {freeDrawMode && (
                        <button
                          onClick={() => setFreeDrawClipMode(freeDrawClipMode === 'clipNew' ? 'clipOld' : 'clipNew')}
                          className={`flex items-center justify-center gap-1 px-3 py-1 ml-4 rounded text-white text-xs font-medium ${
                            freeDrawClipMode === 'clipNew' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-orange-500 hover:bg-orange-600'
                          }`}
                          title={freeDrawClipMode === 'clipNew'
                            ? 'Tryb: nowa warstwa wypełnia tylko puste miejsca'
                            : 'Tryb: nowa warstwa nadpisuje starsze'}
                        >
                          {freeDrawClipMode === 'clipNew' ? '🔽 Pod (starsze priorytet)' : '🔼 Na (nowsza nadpisuje)'}
                        </button>
                      )}

                      {/* Sticky lock — ✏️ Rysowanie ograniczone do wybranej warstwy */}
                      <button
                        onClick={() => {
                          const next = !stickyLayerLock;
                          setStickyLayerLock(next);
                          if (!next) {
                            // Wyłączamy lock — sprzątamy parent + tryby
                            setFreeDrawMode(false);
                            setLensInLayerMode(false);
                            setLensInLayerParentId(null);
                            setFreeDrawPoints([]);
                            setFreeDrawPreview(null);
                          } else {
                            // Włączamy — wyłącz konfliktujące tryby, ale jeszcze nie freeDraw (czeka na klik warstwy)
                            setFreeDrawMode(false);
                            setLensInLayerMode(false);
                            setFreeDrawEditVerticesMode(false);
                            setFreeDrawExpandMode(false);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          stickyLayerLock ? 'bg-amber-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-amber-600 hover:text-amber-600'
                        }`}
                        title="Klik na warstwie wybiera ją jako parent + włącza ✏️ Rysowanie. Wszystkie nowe rysunki klipowane do niej. ESC: 1× odpina parent, 2× wyłącza tryb."
                      >
                        🔒 Rysuj tylko w warstwie: {stickyLayerLock ? (lensInLayerParentId != null ? `LOCK→#${lensInLayerParentId}` : 'WYBIERZ WARSTWĘ') : 'OFF'}
                      </button>

                      <div className="border-t border-gray-200 my-1" />

                      {/* Edycja wierzchołków */}
                      <button
                        onClick={() => {
                          const newMode = !freeDrawEditVerticesMode;
                          setFreeDrawEditVerticesMode(newMode);
                          if (newMode) {
                            setFreeDrawMode(false);
                            setFreeDrawPoints([]);
                            setFreeDrawExpandMode(false);
                          } else {
                            if (editingFreeDraw !== null) {
                              applyFreeDrawEditClipping(editingFreeDraw);
                            }
                            setEditingFreeDraw(null);
                            setSelectedFreeDraw(null);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          freeDrawEditVerticesMode ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Edytuj wierzchołki warstw — klik na krawędź dodaje punkt, drag przesuwa"
                      >
                        📐 Edycja wierzchołków: {freeDrawEditVerticesMode ? 'ON' : 'OFF'}
                      </button>
                      {freeDrawEditVerticesMode && (
                        <button
                          onClick={() => setFreeDrawFillGaps(!freeDrawFillGaps)}
                          className={`flex items-center justify-center gap-1 px-3 py-1 ml-4 rounded text-white text-xs font-medium ${
                            freeDrawFillGaps ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-400 hover:bg-gray-500'
                          }`}
                          title={freeDrawFillGaps ? 'Wypełnianie luk: ON — sąsiednie warstwy rozszerzą się' : 'Wypełnianie luk: OFF'}
                        >
                          🔲 Wypełnianie luk: {freeDrawFillGaps ? 'ON' : 'OFF'}
                        </button>
                      )}

                      <div className="border-t border-gray-200 my-1" />

                      {/* Rozszerzanie granic */}
                      <button
                        onClick={() => {
                          const newMode = !freeDrawExpandMode;
                          setFreeDrawExpandMode(newMode);
                          if (newMode) {
                            setFreeDrawMode(false);
                            setFreeDrawPoints([]);
                            setFreeDrawEditVerticesMode(false);
                          } else {
                            if (editingFreeDraw !== null) {
                              applyFreeDrawEditClipping(editingFreeDraw);
                            }
                            setEditingFreeDraw(null);
                            setSelectedFreeDraw(null);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          freeDrawExpandMode ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Rozszerzanie granic — kliknij warstwę, potem kliknij aby przesunąć granicę"
                      >
                        🔀 Rozszerzanie granic: {freeDrawExpandMode ? 'ON' : 'OFF'}
                      </button>
                      {freeDrawExpandMode && (
                        <div className="flex gap-1 ml-4">
                          <button
                            onClick={() => setFreeDrawExpandClip(!freeDrawExpandClip)}
                            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-white text-xs font-medium ${
                              freeDrawExpandClip ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-400 hover:bg-gray-500'
                            }`}
                            title={freeDrawExpandClip ? 'Nakładanie: ON — warstwa nadpisuje sąsiednie' : 'Nakładanie: OFF — bez przycinania sąsiadów'}
                          >
                            ✂️ Nakładanie: {freeDrawExpandClip ? 'ON' : 'OFF'}
                          </button>
                          <button
                            onClick={() => setFreeDrawFillGaps(!freeDrawFillGaps)}
                            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-white text-xs font-medium ${
                              freeDrawFillGaps ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-400 hover:bg-gray-500'
                            }`}
                            title={freeDrawFillGaps ? 'Wypełnianie luk: ON' : 'Wypełnianie luk: OFF'}
                          >
                            🔲 Luki: {freeDrawFillGaps ? 'ON' : 'OFF'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="relative inline-block">
                  <button
                    onClick={() => setShowPolygonOpsMenu(!showPolygonOpsMenu)}
                    data-toolbar-dropdown-toggle="polygonOps"
                    className={`flex items-center gap-2 px-4 py-2 rounded ${
                      (splitPickMode || polylineSplitMode)
                        ? 'bg-brand-700 text-white hover:bg-brand-800'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                    }`}
                    title="Operacje na polygonach: scalanie (Ctrl+klik 2 polygony) i podział"
                  >
                    🧬 Scal/Podziel {splitPickMode ? 'Podziel (klik polygon)' : (polylineSplitMode ? 'Tnij polilinią' : '')} {showPolygonOpsMenu ? '▲' : '▼'}
                  </button>
                  {showPolygonOpsMenu && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 p-2 flex flex-col gap-1 min-w-[240px]">
                      <button
                        onClick={() => {
                          const ids = selectedFreeDrawIds || [];
                          if (ids.length < 2) {
                            setMergeWarning({
                              tone: 'info',
                              title: 'Zaznacz dwa polygony',
                              message: 'Aby scalić, przytrzymaj Ctrl (lub Shift / Cmd) i kliknij kolejno dwa polygony. Następnie wybierz „Połącz zaznaczone".',
                              selected: ids.length,
                            });
                            setShowPolygonOpsMenu(false);
                            return;
                          }
                          if (ids.length > 2) {
                            setMergeWarning({
                              tone: 'warn',
                              title: 'Za dużo zaznaczonych polygonów',
                              message: 'Możesz scalić maksymalnie dwa polygony naraz. Odznacz nadmiarowe przez Ctrl+klik i spróbuj ponownie.',
                              selected: ids.length,
                            });
                            setShowPolygonOpsMenu(false);
                            return;
                          }
                          const newId = mergeFreeDrawPolygons(ids[0], ids[1]);
                          if (newId != null) {
                            setSelectedFreeDraw(newId);
                            setSelectedFreeDrawIds([newId]);
                          }
                          setShowPolygonOpsMenu(false);
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          (selectedFreeDrawIds || []).length === 2
                            ? 'bg-brand-700 text-white hover:bg-brand-800'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Zaznacz 2 polygony przez Ctrl+klik i kliknij ten przycisk, by je scalić w jeden (unia geometryczna)."
                      >
                        🧬 Połącz zaznaczone {(selectedFreeDrawIds || []).length > 0 ? `(${(selectedFreeDrawIds || []).length})` : ''}
                      </button>
                      <button
                        onClick={() => {
                          const newMode = !splitPickMode;
                          setSplitPickMode(newMode);
                          if (newMode) {
                            setMergePolygonsMode(false);
                            setMergeFromConnId(null);
                            setPolylineSplitMode(false);
                            setPolylineSplitPoints([]);
                            setPolylineSplitPreview(null);
                            setFreeDrawMode(false);
                            setFreeDrawPoints([]);
                            setFreeDrawEditVerticesMode(false);
                            setFreeDrawExpandMode(false);
                            setEditingFreeDraw(null);
                          }
                          setShowPolygonOpsMenu(false);
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          splitPickMode ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Podział polygonu: kliknij polygon, potem narysuj linię cięcia (2 punkty)"
                      >
                        ✂️ Podziel polygon {splitPickMode ? '(klik polygon)' : ''}
                      </button>
                      <button
                        onClick={() => {
                          const newMode = !polylineSplitMode;
                          setPolylineSplitMode(newMode);
                          setPolylineSplitPoints([]);
                          setPolylineSplitPreview(null);
                          if (newMode) {
                            setSplitPickMode(false);
                            cancelSplit();
                            setMergePolygonsMode(false);
                            setMergeFromConnId(null);
                            setFreeDrawMode(false);
                            setFreeDrawPoints([]);
                            setFreeDrawEditVerticesMode(false);
                            setFreeDrawExpandMode(false);
                            setEditingFreeDraw(null);
                            setVirtualBoreholeMode(false);
                          }
                          setShowPolygonOpsMenu(false);
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          polylineSplitMode ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Tnij polilinią: kolejne kliki = punkty linii, dbl-klik kończy. Każda freeDraw warstwa, którą polilinia przecina na wylot, zostanie podzielona."
                      >
                        ✂️📐 Tnij wiele warstw polilinią {polylineSplitMode ? `(${polylineSplitPoints.length} pkt)` : ''}
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    const newMode = !virtualBoreholeMode;
                    setVirtualBoreholeMode(newMode);
                    if (newMode) {
                      setFreeDrawMode(false);
                      setFreeDrawEditVerticesMode(false);
                      setFreeDrawExpandMode(false);
                      setDrawingMode(null);
                      setLensMode(false);
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded ${
                    virtualBoreholeMode
                      ? 'bg-brand-700 text-white hover:bg-brand-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                  }`}
                  title="Kliknij między otworami na przekroju aby wstawić wirtualny otwór z interpolowanymi warstwami"
                >
                  🔮 Wirtualny otwór
                </button>

                <button
                  onClick={() => {
                    if (terrainProfile) {
                      setShowTerrain(!showTerrain);
                    } else {
                      handleFetchTerrain();
                    }
                  }}
                  disabled={terrainLoading || (!terrainProfile && boreholes.filter(b => b.coordX != null && b.coordY != null).length < 2)}
                  className={`flex items-center gap-2 px-4 py-2 rounded ${
                    terrainProfile && showTerrain
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={terrainProfile
                    ? (showTerrain ? 'Ukryj linię terenu NMT (dane pozostają w pamięci)' : 'Pokaż linię terenu NMT')
                    : 'Pobierz profil terenu (NMT) z GUGiK wzdłuż linii przekroju. Wymaga współrzędnych PL-2000 dla min. 2 otworów.'}
                >
                  ⛰️ {terrainLoading
                    ? 'Pobieranie NMT…'
                    : (terrainProfile
                        ? `Teren NMT (${terrainProfile.length} pkt): ${showTerrain ? 'ON' : 'OFF'}`
                        : 'Pobierz teren NMT')}
                </button>

                <div className="relative inline-block" ref={waterPanelRef}>
                  <button
                    onClick={() => {
                      const newValue = !showWater;
                      setShowWater(newValue);
                      setShowWaterPanel(newValue);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 ${showWater && showWaterPanel ? 'rounded-t' : 'rounded'} ${
                      showWater
                        ? 'bg-brand-700 text-white hover:bg-brand-800'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                    }`}
                    title="Pokaż/ukryj symbole poziomu wody na przekroju"
                  >
                    💧 Woda: {showWater ? 'ON' : 'OFF'}
                  </button>
                  {showWater && showWaterPanel && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 p-1.5 flex flex-col gap-1 min-w-[180px]">
                      <button
                        onClick={() => setShowFreeWaterLine(!showFreeWaterLine)}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium ${
                          showFreeWaterLine
                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-500 hover:text-blue-500'
                        }`}
                        title="Linia zwierciadła swobodnego (najwyższe ustabilizowane)"
                      >
                        △ Zw. swobodne: {showFreeWaterLine ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => setShowConfinedWaterLine(!showConfinedWaterLine)}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium ${
                          showConfinedWaterLine
                            ? 'bg-blue-700 text-white hover:bg-blue-800'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-700 hover:text-blue-700'
                        }`}
                        title="Linia zwierciadła napiętego (najwyższe nawiercone)"
                      >
                        ▽ Zw. napięte: {showConfinedWaterLine ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => setSmoothWaterLine(!smoothWaterLine)}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium ${
                          smoothWaterLine
                            ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-cyan-500 hover:text-cyan-500'
                        }`}
                        title="Wygładzenie linii wg profilu terenu"
                      >
                        〰️ Wygładzenie: {smoothWaterLine ? 'ON' : 'OFF'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="relative inline-block">
                  <button
                    onClick={() => setShowMoreOptions(!showMoreOptions)}
                    data-toolbar-dropdown-toggle="moreOptions"
                    className={`flex items-center gap-2 px-4 py-2 rounded ${
                      showMoreOptions ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                    }`}
                  >
                    ⚙️ Więcej opcji {showMoreOptions ? '▲' : '▼'}
                  </button>
                  {showMoreOptions && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 p-2 flex flex-col gap-1 min-w-[200px]">
                      <button
                        onClick={() => setShowGrid(!showGrid)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          showGrid ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                      >
                        <Grid size={16} />
                        Siatka: {showGrid ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => setBlockTextSelection(!blockTextSelection)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          blockTextSelection ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Blokuj zaznaczanie tekstu na przekroju"
                      >
                        🔒 Blokada zaznaczania: {blockTextSelection ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => setUsePastelColors(!usePastelColors)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          usePastelColors ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Przełącz między normalnymi a pastelowymi (wyblakłymi) kolorami"
                      >
                        🎨 {usePastelColors ? 'Kolory pastelowe' : 'Kolory normalne'}
                      </button>
                      <button
                        onClick={() => setShowBoreholeRectangles(!showBoreholeRectangles)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          showBoreholeRectangles ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                      >
                        📦 Słupki: {showBoreholeRectangles ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => {
                          setInterpolationMode(!interpolationMode);
                          if (!interpolationMode) setManualConnectionMode(false);
                          if (interpolationMode) {
                            setResizingLayer(null);
                            setEditingLayerDialog(null);
                            setDraggingBorehole(null);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          interpolationMode ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                      >
                        ⛰️ Interpolacja: {interpolationMode ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => {
                          setManualConnectionMode(!manualConnectionMode);
                          setSelectedLayerForConnection(null);
                          if (!manualConnectionMode) {
                            setInterpolationMode(false);
                            setResizingLayer(null);
                            setEditingLayerDialog(null);
                            setDraggingBorehole(null);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          manualConnectionMode ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Tryb ręcznego łączenia warstw: kliknij warstwę w jednym otworze, potem warstwę w sąsiednim otworze — utworzy się ręczne połączenie"
                      >
                        🔗 Połącz warstwy ręcznie {manualConnectionMode ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Skonwertować automatyczne warstwy na edytowalne polygony?\nPo konwersji automatyczne połączenia zostaną wyłączone.')) {
                            convertConnectionsToFreeDraw();
                            setShowMoreOptions(false);
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-brand-700 text-white hover:bg-brand-800"
                      >
                        🔄 Konwertuj warstwy
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Reinterpolować warstwy?\nWszystkie edytowalne polygony (freeDraw) zostaną usunięte i wygenerowane od nowa z aktualnych pozycji otworów.\nRęczne edycje krawędzi zostaną utracone.')) {
                            handleReinterpolateLayers();
                            setShowMoreOptions(false);
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-emerald-600 text-white hover:bg-emerald-700"
                        title="Usuwa wszystkie polygony freeDraw i generuje je od nowa z aktualnych otworów — przydatne po przesunięciu otworu lub wstawieniu wirtualnego otworu."
                      >
                        ♻️ Reinterpoluj warstwy
                      </button>
                      <button
                        onClick={() => {
                          const freeDrawCount = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3).length;
                          if (freeDrawCount < 1) {
                            alert('Brak polygonów freeDraw do uproszczenia.');
                            return;
                          }
                          const input = window.prompt(`Uprościć wszystkie polygony freeDraw (${freeDrawCount})?\nTolerancja w metrach (większa = mocniej upraszcza):`, '0.5');
                          if (input === null) return;
                          const tol = parseFloat(input.replace(',', '.'));
                          if (!isFinite(tol) || tol <= 0) {
                            alert('Nieprawidłowa tolerancja.');
                            return;
                          }
                          const removed = simplifyAllFreeDrawPolygons(tol);
                          if (removed === 0) {
                            alert('Nic do uproszczenia przy tej tolerancji.');
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-brand-700 text-white hover:bg-brand-800"
                        title="Redukuje wierzchołki we wszystkich polygonach freeDraw (Douglas-Peucker). Przydatne gdy warstwy mają zbyt dużo punktów."
                      >
                        🪶 Uprość wszystkie warstwy
                      </button>
                      <button
                        onClick={() => {
                          const freeDrawCount = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3).length;
                          if (freeDrawCount < 2) {
                            alert('Potrzeba co najmniej 2 warstw freeDraw do przycięcia.\nNajpierw skonwertuj warstwy przyciskiem "Konwertuj warstwy".');
                            return;
                          }
                          if (window.confirm(`Przyciąć nakładające się warstwy (${freeDrawCount} polygonów)?\nWyższe warstwy przytną te pod spodem.`)) {
                            clipOverlappingFreeDraws();
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-brand-700 text-white hover:bg-brand-800"
                      >
                        ✂️ Przytnij nakładające się
                      </button>
                      <button
                        onClick={() => {
                          const freeDrawCount = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3).length;
                          if (freeDrawCount < 2) {
                            alert('Potrzeba co najmniej 2 warstw freeDraw.');
                            return;
                          }
                          fillGapsBetweenFreeDraws();
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-brand-700 text-white hover:bg-brand-800"
                      >
                        🔲 Wypełnij luki
                      </button>
                      <button
                        onClick={() => {
                          if (!terrainProfile || terrainProfile.length < 2) {
                            alert('Brak profilu terenu. Najpierw pobierz NMT.');
                            return;
                          }
                          if (window.confirm('Urealić kształt warstw wg profilu terenu?\nDoda punkty pośrednie podążające za rzeźbą terenu.')) {
                            applyTerrainShapeToLayers();
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-brand-700 text-white hover:bg-brand-800"
                      >
                        🏔️ Urealnienie warstw
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowAttributeTable(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700"
                  title="Otwórz tabelę atrybutów wszystkich polygonów"
                >
                  📋 Tabela atrybutów
                </button>

                <div className="relative inline-block">
                  <button
                    onClick={() => setShowPrecisionPanel(!showPrecisionPanel)}
                    data-toolbar-dropdown-toggle="precision"
                    className={`flex items-center gap-2 px-4 py-2 rounded ${
                      showPrecisionPanel ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                    }`}
                    title="Tryby precyzji: Undo/Redo, snap, pomiar"
                  >
                    🎯 Precyzja {showPrecisionPanel ? '▲' : '▼'}
                  </button>
                  {showPrecisionPanel && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 p-2 flex flex-col gap-1 min-w-[220px]">
                      <div className="text-xs text-gray-500 font-semibold px-2 pt-1">Historia</div>
                      <button
                        onClick={() => undoHistory()}
                        disabled={!canUndo}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          canUndo
                            ? 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                            : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                        }`}
                        title="Cofnij ostatnią zmianę (Ctrl+Z)"
                      >
                        ↶ Cofnij <span className="text-xs text-gray-500 ml-auto">Ctrl+Z</span>
                      </button>
                      <button
                        onClick={() => redoHistory()}
                        disabled={!canRedo}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          canRedo
                            ? 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                            : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                        }`}
                        title="Ponów cofniętą zmianę (Ctrl+Y / Ctrl+Shift+Z)"
                      >
                        ↷ Ponów <span className="text-xs text-gray-500 ml-auto">Ctrl+Y</span>
                      </button>

                      <div className="text-xs text-gray-500 font-semibold px-2 pt-2">Snap</div>
                      <button
                        onClick={() => setSnapToBorehole(!snapToBorehole)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          snapToBorehole ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Łapanie kursora do osi otworu (X) i krawędzi warstw — wyłącz dla rysowania z wolnej ręki"
                      >
                        🎯 Snap do otworu {snapToBorehole ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={() => setSnapToOrtho(!snapToOrtho)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          snapToOrtho ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Trzymanie Shift wymusza linię poziomą lub pionową (od ostatniego punktu)"
                      >
                        📐 Snap do osi {snapToOrtho ? 'ON' : 'OFF'} <span className="text-xs text-gray-300 ml-auto">Shift</span>
                      </button>
                      <button
                        onClick={() => setSnapToGrid(!snapToGrid)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          snapToGrid ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Wolne punkty są zaokrąglane do najbliższego węzła siatki o zadanej wielkości"
                      >
                        ▦ Snap do siatki {snapToGrid ? 'ON' : 'OFF'}
                      </button>
                      {snapToGrid && (
                        <div className="flex items-center gap-2 px-3 py-1">
                          <label className="text-xs text-gray-600">Oczko:</label>
                          <input
                            type="number"
                            min="0.1"
                            max="50"
                            step="0.1"
                            value={gridSize}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              if (!isNaN(v) && v > 0) setGridSize(v);
                            }}
                            className="w-16 px-2 py-0.5 border border-gray-300 rounded text-sm"
                          />
                          <span className="text-xs text-gray-500">m</span>
                        </div>
                      )}
                      <button
                        onClick={() => setSnapToEdge(!snapToEdge)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          snapToEdge ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Najechanie blisko krawędzi innego polygonu zaczepia kursor do tej krawędzi"
                      >
                        ⊿ Snap do krawędzi {snapToEdge ? 'ON' : 'OFF'}
                      </button>

                      <div className="text-xs text-gray-500 font-semibold px-2 pt-2">Pomiar i kopiowanie</div>
                      <button
                        onClick={() => setShowLiveMeasure(!showLiveMeasure)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                          showLiveMeasure ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                        }`}
                        title="Podczas rysowania pokazuje pod kursorem długość, kąt i Δx/Δy od ostatniego punktu"
                      >
                        📐 Pomiar na żywo {showLiveMeasure ? 'ON' : 'OFF'}
                      </button>
                      <div className="text-xs text-gray-600 px-3 py-1 leading-relaxed">
                        Zaznacz polygon i naciśnij <span className="font-mono bg-gray-100 px-1 rounded">Ctrl+C</span> aby skopiować, <span className="font-mono bg-gray-100 px-1 rounded">Ctrl+V</span> aby wkleić w miejscu kursora.
                      </div>

                      <div className="text-xs text-gray-500 font-semibold px-2 pt-2">Widok</div>
                      <div className="flex items-center gap-2 px-3 py-1">
                        <span className="text-sm text-gray-700">Zoom:</span>
                        <span className="font-mono text-sm font-semibold text-brand-700 min-w-[50px]">
                          {Math.round(viewZoom * 100)}%
                        </span>
                        <button
                          onClick={() => { setViewZoom(1); setViewPanX(0); setViewPanY(0); }}
                          disabled={viewZoom === 1 && viewPanX === 0 && viewPanY === 0}
                          className={`ml-auto px-2 py-0.5 rounded text-xs ${
                            viewZoom === 1 && viewPanX === 0 && viewPanY === 0
                              ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                              : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                          }`}
                          title="Reset zoomu i przesunięcia widoku do 1:1"
                        >
                          Reset 1:1
                        </button>
                      </div>
                      <div className="text-xs text-gray-600 px-3 pb-1 leading-relaxed">
                        <span className="font-mono bg-gray-100 px-1 rounded">Ctrl+kółko</span> = zoom (centrowany na kursorze).<br/>
                        Przesuwanie: <span className="font-mono bg-gray-100 px-1 rounded">Space+drag</span>, środkowy klik, lub <span className="font-mono bg-gray-100 px-1 rounded">lewy+prawy klik</span> razem.
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setFullscreenMode(!fullscreenMode)}
                  className={`flex items-center gap-2 px-4 py-2 rounded ${
                    fullscreenMode
                      ? 'bg-brand-700 text-white hover:bg-brand-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                  }`}
                  title="Przełącz widok przekroju na pełny ekran"
                >
                  📺 {fullscreenMode ? 'Tryb normalny' : 'Pełny ekran'}
                </button>

                <button
                  onClick={() => setHandTool(!handTool)}
                  className={`flex items-center gap-2 px-4 py-2 rounded ${
                    handTool
                      ? 'bg-brand-700 text-white hover:bg-brand-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                  }`}
                  title="Łapka — lewy klik+drag = przesuń widok. Ctrl+scroll = zoom."
                >
                  ✋ Łapka {handTool ? 'ON' : ''}
                </button>

                <button
                  onClick={() => { setViewZoom(1); setViewPanX(0); setViewPanY(0); }}
                  disabled={viewZoom === 1 && viewPanX === 0 && viewPanY === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded ${
                    viewZoom === 1 && viewPanX === 0 && viewPanY === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                  }`}
                  title="Wyśrodkuj — wraca do pełnego widoku przekroju (zoom 1:1, pan 0/0)"
                >
                  🎯 Wyśrodkuj
                </button>

                <button
                  onClick={() => {
                    const isCurrentlyMeasuring = drawingMode === 'measure';
                    setDrawingMode(isCurrentlyMeasuring ? null : 'measure');
                    if (!isCurrentlyMeasuring) {
                      setInterpolationMode(false);
                      setManualConnectionMode(false);
                    }
                    setMeasureFirstPoint(null);
                    setMeasurePreview(null);
                    // Jeśli wyłączamy tryb pomiaru, usuń wszystkie tymczasowe pomiary
                    if (isCurrentlyMeasuring) {
                      setActiveMeasurements([]);
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded ${
                    drawingMode === 'measure'
                      ? 'bg-brand-700 text-white hover:bg-brand-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                  }`}
                  title="Zmierz odległość na przekroju"
                >
                  📏 Pomiar odległości
                </button>

                <button
                  onClick={() => {
                    if (window.confirm('Czy na pewno chcesz zresetować wszystkie połączenia i interpolacje do wartości domyślnych?\n\n• Usuną się wszystkie ręczne połączenia\n• Wyłączy się automatyczne łączenie\n• Przywrócą się usunięte automatyczne połączenia\n• Usuną się wszystkie punkty kontrolne\n• Usunie się pobrany profil terenu NMT')) {
                      // Reset do wartości domyślnych
                      setManualConnections([]);
                      setAutoConnectByName(false); // Zmieniono na false (OFF domyślnie)
                      setAutoConnectMode('byName'); // Reset trybu do domyślnego
                      setExcludedAutoConnections([]);
                      setInterpolationPoints({});
                      setConnectionPointAdjustments({});
                      setInterpolationMode(false);
                      setManualConnectionMode(false);
                      setFreeDrawMode(false);
                      setFreeDrawPoints([]);
                      setConnectLayers(false); // Zmieniono na false (OFF domyślnie)
                      // Reset NMT
                      setTerrainProfile(null);
                      setTerrainError(null);
                      setClipToTerrain(false);
                    }
                  }}
                  className="flex items-center gap-2 border border-brand-700 text-brand-700 bg-white px-4 py-2 rounded hover:bg-brand-50"
                  title="Zresetuj wszystkie połączenia i interpolacje do wartości domyślnych"
                >
                  ↺ Reset do domyślnych
                </button>

                {['a3', 'a4'].map((fmt) => {
                  const active = printSelectMode && printSelectFormat === fmt;
                  return (
                    <button
                      key={fmt}
                      onClick={() => {
                        if (active) {
                          setPrintSelectMode(false);
                          setPrintSelectRect(null);
                          setPrintDrag(null);
                          setShowPrintDialog(false);
                        } else {
                          setPrintSelectFormat(fmt);
                          setPrintSelectMode(true);
                          setPrintSelectRect(computePrintRectAround(fmt, printOrientation, getViewportCenter()));
                          setPrintDrag(null);
                          setShowPrintDialog(false);
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded ${
                        active
                          ? 'bg-brand-700 text-white hover:bg-brand-800'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-700 hover:text-brand-700'
                      }`}
                      title={`Pokaż prostokąt obszaru ${fmt.toUpperCase()} przy bieżących skalach wydruku — przeciągnij w odpowiednie miejsce, potem eksportuj do PDF`}
                    >
                      📄 Drukuj {fmt.toUpperCase()} {active ? 'ON' : ''}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className={`relative grid place-items-center ${fullscreenMode ? 'h-[calc(100vh-3.5rem)] w-full overflow-auto' : 'overflow-x-auto min-h-[calc(100vh-12rem)]'}`}
              style={!fullscreenMode ? { marginLeft: '-200px', marginRight: '-200px', paddingLeft: '200px', paddingRight: '200px' } : undefined}
            >
              {editingLayerDialog && (() => {
                const bh = boreholes.find(b => b.id === editingLayerDialog.boreholeId);
                const layers = bh?.layers || [];
                const idx = layers.findIndex(l => l.id === editingLayerDialog.layerId);
                const prev = idx > 0 ? layers[idx - 1] : null;
                const next = idx >= 0 && idx < layers.length - 1 ? layers[idx + 1] : null;
                const maxDepth = bh?.depth || 100;
                return (
                  <LayerEditDialog
                    dialog={editingLayerDialog}
                    setDialog={setEditingLayerDialog}
                    onSubmit={handleLayerDialogSubmit}
                    onCancel={handleLayerDialogCancel}
                    lithoDict={layerLithoDict}
                    genesisDict={layerGenesisDict}
                    rockDict={layerRockDict}
                    rockSubDict={layerRockSubDict}
                    numberDict={layerNumberDict}
                    prevLayer={prev}
                    nextLayer={next}
                    maxDepth={maxDepth}
                    nameInputRef={nameInputRef}
                  />
                );
              })()}

              {/* Dialog scalania warstw — wybór warstwy nadrzędnej */}
              {mergeLayerDialog && (() => {
                const bh = boreholes.find(b => b.id === mergeLayerDialog.boreholeId);
                if (!bh) return null;
                const groupLayers = mergeLayerDialog.layerIds
                  .map(id => bh.layers.find(l => l.id === id))
                  .filter(Boolean);
                const sumThickness = groupLayers.reduce((s, l) => s + (Number(l.thickness) || 0), 0);
                const sumRounded = Math.round(sumThickness * 100) / 100;
                return (
                  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1000]"
                       onClick={() => setMergeLayerDialog(null)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 w-[480px] max-w-[90vw]"
                         onClick={(e) => e.stopPropagation()}>
                      <h2 className="text-lg font-bold mb-2">🔗 Scal warstwy ({groupLayers.length})</h2>
                      <p className="text-xs text-gray-600 mb-3">
                        Wybierz <strong>warstwę nadrzędną</strong> — jej atrybuty (nazwa, kolor, szrafura, litologia, parametry) zostaną przypisane do nowej, scalonej warstwy. Łączna miąższość: <strong>{sumRounded} m</strong>.
                      </p>
                      <div className="border border-gray-200 rounded max-h-64 overflow-y-auto mb-3">
                        {groupLayers.map(l => (
                          <label key={l.id}
                            className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 ${mergeLayerDialog.masterId === l.id ? 'bg-emerald-50' : ''}`}>
                            <input type="radio" name="merge-master"
                              checked={mergeLayerDialog.masterId === l.id}
                              onChange={() => setMergeLayerDialog({ ...mergeLayerDialog, masterId: l.id })} />
                            <span className="w-5 h-5 border border-gray-300 rounded flex-shrink-0"
                                  style={{ backgroundColor: l.color }}></span>
                            <span className="flex-1 text-sm">{l.name || `(bez nazwy #${l.id})`}</span>
                            <span className="text-xs text-gray-500">{l.thickness} m</span>
                            {l.geotechnicalLayer && <span className="text-xs text-gray-400">{l.geotechnicalLayer}</span>}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                        ⚠ Pozostałe warstwy zostaną <strong>trwale usunięte</strong>. Można cofnąć przez Ctrl+Z.
                      </p>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setMergeLayerDialog(null)}
                          className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">
                          Anuluj
                        </button>
                        <button onClick={() => mergeLayers(mergeLayerDialog.boreholeId, mergeLayerDialog.layerIds, mergeLayerDialog.masterId)}
                          className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700">
                          🔗 Scal
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Dialog właściwości soczewki */}
              {editingLens && (
                <div
                  style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'white',
                    padding: '30px',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    zIndex: 1000,
                    maxWidth: '500px',
                    width: '90%',
                    maxHeight: '90vh',
                    overflow: 'auto'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-2xl font-bold mb-6 text-amber-800">Właściwości soczewki</h3>

                  <div className="space-y-4">
                    {/* Warstwa bazowa */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Warstwa bazowa:</label>
                      <select
                        value={editingLens.baseLayer || ''}
                        onChange={(e) => {
                          const selectedLayerName = e.target.value;
                          // Znajdź warstwę aby skopiować jej właściwości
                          let layerProps = { name: selectedLayerName, color: '#8B4513', pattern: 'none' };
                          boreholes.forEach(b => {
                            const found = b.layers.find(l => l.name === selectedLayerName);
                            if (found) {
                              layerProps = { name: found.name, color: found.color, pattern: found.pattern };
                            }
                          });
                          setEditingLens({
                            ...editingLens,
                            baseLayer: selectedLayerName,
                            name: layerProps.name,
                            color: layerProps.color,
                            pattern: layerProps.pattern
                          });
                        }}
                        className="border-2 border-gray-300 focus:border-amber-500 rounded px-3 py-2 w-full text-base outline-none cursor-pointer"
                      >
                        <option value="">-- Wybierz warstwę --</option>
                        {Array.from(new Set(boreholes.flatMap(b => b.layers.map(l => l.name)))).map(layerName => (
                          <option key={layerName} value={layerName}>{layerName}</option>
                        ))}
                      </select>
                    </div>

                    {/* Nazwa */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nazwa:</label>
                      <input
                        type="text"
                        value={editingLens.name}
                        onChange={(e) => setEditingLens({...editingLens, name: e.target.value})}
                        className="border-2 border-gray-300 focus:border-amber-500 rounded px-3 py-2 w-full text-base outline-none"
                        placeholder="Nazwa soczewki"
                      />
                    </div>

                    {/* Wzór */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Szrafur:</label>
                      <select
                        value={editingLens.pattern || 'none'}
                        onChange={(e) => setEditingLens({...editingLens, pattern: e.target.value})}
                        className="border-2 border-gray-300 focus:border-amber-500 rounded px-3 py-2 w-full text-base outline-none cursor-pointer"
                      >
                        <option value="none">Brak</option>
                        <option value="dots">Kropki (żwir)</option>
                        <option value="horizontal">Poziome (glina)</option>
                        <option value="diagonal">Ukośne (piasek)</option>
                        <option value="cross">Krzyżyk (kamień)</option>
                      </select>
                    </div>

                    {/* Kolor */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Kolor:</label>
                      <input
                        type="color"
                        value={editingLens.color}
                        onChange={(e) => setEditingLens({...editingLens, color: e.target.value})}
                        className="w-full h-12 cursor-pointer border-2 border-gray-300 rounded"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        saveLensProperties(editingLens);
                      }}
                      className="flex-1 bg-brand-700 text-white hover:bg-brand-800 px-4 py-3 rounded font-semibold text-base"
                    >
                      ✓ Zapisz
                    </button>
                    <button
                      onClick={() => {
                        setEditingLens(null);
                      }}
                      className="flex-1 bg-gray-400 text-white hover:bg-gray-500 px-4 py-3 rounded font-semibold text-base"
                    >
                      ✗ Anuluj
                    </button>
                  </div>

                  <div className="mt-4 text-xs text-gray-500 text-center">
                    Enter - zapisz • Esc - anuluj
                  </div>
                </div>
              )}

              {editingLens && (
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    zIndex: 999
                  }}
                  onClick={() => setEditingLens(null)}
                />
              )}

              <svg
                ref={svgRef}
                id="geological-profile-svg"
                width={getSvgWidth() + (showElevationScaleLeft ? 145 : 0) + (showElevationScale ? 95 : 0)}
                height={getSvgHeight()}
                viewBox={`${viewPanX - (showElevationScaleLeft ? 145 : 0)} ${viewPanY} ${(getSvgWidth() + (showElevationScaleLeft ? 145 : 0) + (showElevationScale ? 95 : 0)) / viewZoom} ${getSvgHeight() / viewZoom}`}
                preserveAspectRatio="xMidYMid meet"
                className="border"
                style={{ background: 'white', display: 'block', ...(blockTextSelection && { userSelect: 'none', WebkitUserSelect: 'none' }), cursor: isViewPanning ? 'grabbing' : ((spaceHeldRef.current || handTool) ? 'grab' : ((drawingMode || lensMode || freeDrawMode || lensInLayerMode || cutHoleMode || splitMode || polylineSplitMode || virtualBoreholeMode) ? 'crosshair' : (freeDrawExpandMode && editingFreeDraw !== null) ? 'copy' : 'default')) }}
                onMouseDown={(e) => {
                  // Tryb zaznaczania obszaru do druku — kliknięcie w tło nic nie robi
                  // (rect i jego rogi mają własne handlery onMouseDown).
                  if (printSelectMode && e.button === 0) {
                    return;
                  }
                  // Pan widoku — Space+lewy / middle-mouse / lewy+prawy razem / łapka (handTool) + lewy.
                  // e.buttons = bitmask: 1=lewy, 2=prawy, 4=środkowy. 3 = lewy+prawy.
                  const twoButtons = (e.buttons & 3) === 3;
                  if (e.button === 1 || (e.button === 0 && (spaceHeldRef.current || handTool)) || twoButtons) {
                    e.preventDefault();
                    setIsViewPanning(true);
                    viewPanStartRef.current = { clientX: e.clientX, clientY: e.clientY, panX: viewPanX, panY: viewPanY };
                    return;
                  }
                  if (drawingMode) handleDrawingStart(e.nativeEvent, { interpolationMode, resizingLayer, draggingBorehole });
                  if (lensMode) handleLensStart(e.nativeEvent);
                }}
                onTouchStart={(e) => {
                  if (drawingMode) handleDrawingStart(e.nativeEvent, { interpolationMode, resizingLayer, draggingBorehole });
                  if (lensMode) handleLensStart(e.nativeEvent);
                }}
                onClick={(e) => {
                  if (virtualBoreholeMode) {
                    const { x } = getSvgCoordinates(e.nativeEvent);
                    handleVirtualBoreholeClick(x);
                    return;
                  }
                  if (splitMode) {
                    const { x, y } = getSvgCoordinates(e.nativeEvent);
                    handleSplitClick(x, y);
                    return;
                  }
                  if (polylineSplitMode) {
                    const { x, y } = getSvgCoordinates(e.nativeEvent);
                    handlePolylineSplitClick(x, y);
                    return;
                  }
                  if ((freeDrawMode || lensInLayerMode || cutHoleMode) && !movePolygonMode) {
                    const { x, y } = getSvgCoordinates(e.nativeEvent);
                    handleFreeDrawClick(x, y, e.shiftKey);
                  }
                  if (freeDrawExpandMode && editingFreeDraw !== null) {
                    const { x, y } = getSvgCoordinates(e.nativeEvent);
                    expandFreeDrawBoundary(editingFreeDraw, x, y);
                    return;
                  }
                  if (selectedFreeDraw !== null) {
                    setSelectedFreeDraw(null);
                  }
                }}
                onDoubleClick={(e) => {
                  if (polylineSplitMode) {
                    e.preventDefault();
                    finishPolylineSplit();
                    return;
                  }
                  if ((freeDrawMode || lensInLayerMode || cutHoleMode) && !movePolygonMode) {
                    e.preventDefault();
                    const { x, y } = getSvgCoordinates(e.nativeEvent);
                    handleFreeDrawDoubleClick(x, y, e.shiftKey);
                  }
                }}
                onMouseMove={(e) => {
                  // Drag prostokąta zaznaczenia do druku — tylko przesuwanie (rozmiar zablokowany)
                  if (printDrag && printSelectRect) {
                    const { x, y } = getSvgCoordinates(e.nativeEvent);
                    const dx = x - printDrag.startSvg.x;
                    const dy = y - printDrag.startSvg.y;
                    setPrintSelectRect({
                      ...printDrag.origRect,
                      x: printDrag.origRect.x + dx,
                      y: printDrag.origRect.y + dy,
                    });
                    return;
                  }
                  // Pan widoku — przesuń viewBox o ruch myszy w pikselach przeskalowany
                  // do SVG-coords (uwzględnia zoom).
                  if (isViewPanning && viewPanStartRef.current) {
                    const svgEl = svgRef.current;
                    if (!svgEl) return;
                    const rect = svgEl.getBoundingClientRect();
                    const baseW = getSvgWidth();
                    const baseH = getSvgHeight();
                    const dx = (e.clientX - viewPanStartRef.current.clientX) * (baseW / viewZoom) / rect.width;
                    const dy = (e.clientY - viewPanStartRef.current.clientY) * (baseH / viewZoom) / rect.height;
                    setViewPanX(viewPanStartRef.current.panX - dx);
                    setViewPanY(viewPanStartRef.current.panY - dy);
                    return;
                  }
                  // Zapamiętuj pozycję kursora — używana przy Ctrl+V (wklejanie polygonu)
                  const svgPos = getSvgCoordinates(e.nativeEvent);
                  cursorSvgPosRef.current = svgPos;
                  if (freeDrawMode || lensInLayerMode || cutHoleMode) {
                    const ortho = applyOrthoSnap(svgPos.x, svgPos.y, e.shiftKey);
                    setFreeDrawPreview({ x: ortho.x, y: ortho.y });
                  }
                  if (splitMode) {
                    setSplitLinePreview({ x: svgPos.x, y: svgPos.y });
                  }
                  if (polylineSplitMode) {
                    setPolylineSplitPreview({ x: svgPos.x, y: svgPos.y });
                  }
                }}
                onMouseUp={(e) => {
                  // Koniec dragu prostokąta wydruku
                  if (printDrag) {
                    setPrintDrag(null);
                    return;
                  }
                  if (isViewPanning) {
                    setIsViewPanning(false);
                    viewPanStartRef.current = null;
                    justFinishedPanRef.current = true;
                    // Wyczyść flag po krótkim czasie (po obsłużeniu nadchodzącego context menu)
                    setTimeout(() => { justFinishedPanRef.current = false; }, 50);
                  }
                }}
                onMouseLeave={() => {
                  if (isViewPanning) {
                    setIsViewPanning(false);
                    viewPanStartRef.current = null;
                    justFinishedPanRef.current = true;
                    setTimeout(() => { justFinishedPanRef.current = false; }, 50);
                  }
                }}
                onContextMenu={(e) => {
                  // Jeśli właśnie panujemy (dwa przyciski) lub przed chwilą skończyliśmy pan — zablokuj context menu
                  if (isViewPanning || viewPanStartRef.current || justFinishedPanRef.current) {
                    e.preventDefault();
                    return;
                  }
                  // Shift+prawy klik → pływający panel narzędzi w miejscu kursora
                  if (e.shiftKey) {
                    e.preventDefault();
                    setQuickToolbar({ x: e.clientX, y: e.clientY });
                    return;
                  }
                  // Znajdź polygon freeDraw pod kursorem (po aktualnych SVG punktach)
                  if (freeDrawMode || splitMode || polylineSplitMode) return;
                  const { x, y } = getSvgCoordinates(e.nativeEvent);
                  const freeDrawConns = manualConnections.filter(
                    mc => mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3
                  );
                  const pointInPolygon = (px, py, poly) => {
                    let inside = false;
                    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
                      const xi = poly[i].x, yi = poly[i].y;
                      const xj = poly[j].x, yj = poly[j].y;
                      const intersect = ((yi > py) !== (yj > py)) &&
                        (px < ((xj - xi) * (py - yi)) / (yj - yi || 1e-9) + xi);
                      if (intersect) inside = !inside;
                    }
                    return inside;
                  };
                  // Sprawdź od najnowszego (na wierzchu)
                  const sorted = [...freeDrawConns].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                  for (const mc of sorted) {
                    const pts = mc.points.map(p => resolvePointSvg(p));
                    if (pointInPolygon(x, y, pts)) {
                      e.preventDefault();
                      setContextMenuFreeDraw({ clientX: e.clientX, clientY: e.clientY, connId: mc.id });
                      return;
                    }
                  }
                }}
              >
                {/* Definicje szrafur geologicznych */}
                <defs>
                  {/* ClipPath przycinający do linii terenu — zawsze definiowany gdy NMT jest wczytane.
                      Toggle clipToTerrain decyduje czy stosować do WSZYSTKICH polygonów; extendsToTerrain
                      polygony używają go zawsze (najwyższa strefa rozciągnięta nad teren + tnięta tutaj). */}
                  {terrainProfile && terrainProfile.length >= 2 && (() => {
                    const pixelsPerMeter = scale / 10;
                    const maxDepth = getMaxDepth();
                    const svgH = getSvgHeight();
                    const svgW = getSvgWidth();
                    const svgPoints = terrainProfile.map(pt => ({
                      x: 100 + pt.distance * horizontalScale,
                      y: 80 + (maxDepth - pt.elevation) * pixelsPerMeter,
                    }));
                    // Ekstrapolacja nachylenia na obu końcach
                    const first = svgPoints[0];
                    const second = svgPoints[1];
                    const slopeLeft = (second.y - first.y) / (second.x - first.x);
                    const leftEdgeY = first.y + slopeLeft * (0 - first.x);

                    const last = svgPoints[svgPoints.length - 1];
                    const prevLast = svgPoints[svgPoints.length - 2];
                    const slopeRight = (last.y - prevLast.y) / (last.x - prevLast.x);
                    const rightEdgeY = last.y + slopeRight * (svgW + 200 - last.x);

                    // Ścieżka: ekstrapolacja w lewo, wzdłuż terenu, ekstrapolacja w prawo, dół, zamknięcie
                    let clipD = `M -200 ${leftEdgeY + slopeLeft * (-200)}`;
                    clipD += ` L ${first.x} ${first.y}`;
                    for (let i = 1; i < svgPoints.length; i++) {
                      const prev = svgPoints[i - 1];
                      const curr = svgPoints[i];
                      const cpX = (prev.x + curr.x) / 2;
                      clipD += ` C ${cpX} ${prev.y}, ${cpX} ${curr.y}, ${curr.x} ${curr.y}`;
                    }
                    clipD += ` L ${svgW + 200} ${rightEdgeY}`;
                    clipD += ` L ${svgW + 200} ${svgH + 200}`;
                    clipD += ` L -200 ${svgH + 200} Z`;
                    return (
                      <clipPath id="terrain-clip">
                        <path d={clipD} />
                      </clipPath>
                    );
                  })()}
                  {/* Filtr cienia dla przeciąganych rysunków */}
                  <filter id="dragging-shadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                    <feOffset dx="2" dy="2" result="offsetblur"/>
                    <feComponentTransfer>
                      <feFuncA type="linear" slope="0.5"/>
                    </feComponentTransfer>
                    <feMerge>
                      <feMergeNode/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>

                  {/* Bez szrafury */}
                  <pattern id="pattern-none" width="1" height="1">
                    <rect width="1" height="1" fill="transparent"/>
                  </pattern>

                  {/* Piasek - kropki */}
                  <pattern id="pattern-sand" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                    <rect width="8" height="8" fill="white" opacity="0"/>
                    <circle cx="2" cy="2" r="0.8" fill="black"/>
                    <circle cx="6" cy="6" r="0.8" fill="black"/>
                  </pattern>

                  {/* Piasek pylasty - małe kropki */}
                  <pattern id="pattern-silty-sand" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                    <rect width="6" height="6" fill="white" opacity="0"/>
                    <circle cx="2" cy="2" r="0.5" fill="black"/>
                    <circle cx="5" cy="5" r="0.5" fill="black"/>
                  </pattern>

                  {/* Żwir - duże kropki */}
                  <pattern id="pattern-gravel" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                    <rect width="10" height="10" fill="white" opacity="0"/>
                    <circle cx="3" cy="3" r="1.5" fill="black"/>
                    <circle cx="8" cy="8" r="1.5" fill="black"/>
                  </pattern>

                  {/* Glina - linie poziome */}
                  <pattern id="pattern-clay" x="0" y="0" width="10" height="4" patternUnits="userSpaceOnUse">
                    <rect width="10" height="4" fill="white" opacity="0"/>
                    <line x1="0" y1="2" x2="10" y2="2" stroke="black" strokeWidth="0.5"/>
                  </pattern>

                  {/* Ił - linie poziome gęste */}
                  <pattern id="pattern-silt" x="0" y="0" width="10" height="3" patternUnits="userSpaceOnUse">
                    <rect width="10" height="3" fill="white" opacity="0"/>
                    <line x1="0" y1="1.5" x2="10" y2="1.5" stroke="black" strokeWidth="0.5"/>
                  </pattern>

                  {/* Gruz - nieregularne kształty */}
                  <pattern id="pattern-debris" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
                    <rect width="12" height="12" fill="white" opacity="0"/>
                    <path d="M2,3 L5,2 L6,5 L3,6 Z" fill="black"/>
                    <path d="M8,7 L11,8 L10,11 L7,9 Z" fill="black"/>
                  </pattern>

                  {/* Skała - linie krzyżowe */}
                  <pattern id="pattern-rock" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                    <rect width="8" height="8" fill="white" opacity="0"/>
                    <line x1="0" y1="0" x2="8" y2="8" stroke="black" strokeWidth="0.5"/>
                    <line x1="8" y1="0" x2="0" y2="8" stroke="black" strokeWidth="0.5"/>
                  </pattern>

                  {/* Torf - linie ukośne */}
                  <pattern id="pattern-peat" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                    <rect width="8" height="8" fill="white" opacity="0"/>
                    <line x1="0" y1="8" x2="8" y2="0" stroke="black" strokeWidth="0.5"/>
                  </pattern>

                  {/* Namył - linie faliste */}
                  <pattern id="pattern-alluvium" x="0" y="0" width="12" height="6" patternUnits="userSpaceOnUse">
                    <rect width="12" height="6" fill="white" opacity="0"/>
                    <path d="M0,3 Q3,1 6,3 T12,3" stroke="black" fill="none" strokeWidth="0.5"/>
                  </pattern>

                  {/* Żwir piaszczysty - kropki mieszane */}
                  <pattern id="pattern-sandy-gravel" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                    <rect width="10" height="10" fill="white" opacity="0"/>
                    <circle cx="2" cy="2" r="1.2" fill="black"/>
                    <circle cx="8" cy="8" r="1.2" fill="black"/>
                    <circle cx="5" cy="6" r="0.6" fill="black"/>
                    <circle cx="7" cy="3" r="0.6" fill="black"/>
                  </pattern>

                  {/* Glina piaszczysta - linie + kropki */}
                  <pattern id="pattern-sandy-clay" x="0" y="0" width="10" height="8" patternUnits="userSpaceOnUse">
                    <rect width="10" height="8" fill="white" opacity="0"/>
                    <line x1="0" y1="4" x2="10" y2="4" stroke="black" strokeWidth="0.4"/>
                    <circle cx="3" cy="2" r="0.5" fill="black"/>
                    <circle cx="7" cy="6" r="0.5" fill="black"/>
                  </pattern>

                  {/* Piasek gliniasty - kropki + linie rzadkie */}
                  <pattern id="pattern-clayey-sand" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                    <rect width="10" height="10" fill="white" opacity="0"/>
                    <circle cx="2" cy="3" r="0.7" fill="black"/>
                    <circle cx="6" cy="7" r="0.7" fill="black"/>
                    <line x1="0" y1="5" x2="10" y2="5" stroke="black" strokeWidth="0.3"/>
                  </pattern>

                  {/* Wzory dla soczewek - proste wzory */}
                  {/* Kropki (żwir) */}
                  <pattern id="pattern-dots" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                    <rect width="10" height="10" fill="white" opacity="0"/>
                    <circle cx="3" cy="3" r="1.5" fill="black"/>
                    <circle cx="8" cy="8" r="1.5" fill="black"/>
                  </pattern>

                  {/* Poziome (glina) */}
                  <pattern id="pattern-horizontal" x="0" y="0" width="10" height="4" patternUnits="userSpaceOnUse">
                    <rect width="10" height="4" fill="white" opacity="0"/>
                    <line x1="0" y1="2" x2="10" y2="2" stroke="black" strokeWidth="0.5"/>
                  </pattern>

                  {/* Ukośne (piasek) */}
                  <pattern id="pattern-diagonal" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                    <rect width="8" height="8" fill="white" opacity="0"/>
                    <line x1="0" y1="8" x2="8" y2="0" stroke="black" strokeWidth="0.5"/>
                  </pattern>

                  {/* Krzyżyk (kamień) */}
                  <pattern id="pattern-cross" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                    <rect width="8" height="8" fill="white" opacity="0"/>
                    <line x1="0" y1="0" x2="8" y2="8" stroke="black" strokeWidth="0.5"/>
                    <line x1="8" y1="0" x2="0" y2="8" stroke="black" strokeWidth="0.5"/>
                  </pattern>
                </defs>

                {showGrid && (
                  <g className="grid-layer">
                    {Array.from({ length: Math.ceil(getSvgWidth() / 50) }).map((_, i) => (
                      <line
                        key={`v-${i}`}
                        x1={i * 50}
                        y1={0}
                        x2={i * 50}
                        y2={getSvgHeight()}
                        stroke="#e0e0e0"
                        strokeWidth="1"
                        opacity="0.5"
                      />
                    ))}
                    {Array.from({ length: Math.ceil(getSvgHeight() / 50) }).map((_, i) => (
                      <line
                        key={`h-${i}`}
                        x1={0}
                        y1={i * 50}
                        x2={getSvgWidth()}
                        y2={i * 50}
                        stroke="#e0e0e0"
                        strokeWidth="1"
                        opacity="0.5"
                      />
                    ))}
                  </g>
                )}
                {/* Siatka precyzji (snap-grid) — w metrach, niebieska, lekka */}
                {snapToGrid && gridSize > 0 && (() => {
                  const pixelsPerMeter = scale / 10;
                  const stepX = gridSize * horizontalScale;
                  const stepY = gridSize * pixelsPerMeter;
                  if (stepX < 4 || stepY < 4) return null; // za gęsto — pomiń
                  const w = getSvgWidth();
                  const h = getSvgHeight();
                  // Linie pionowe: zaczynają się od X=100 (offset osi)
                  const vLines = [];
                  for (let x = 100; x <= w; x += stepX) {
                    vLines.push(x);
                  }
                  for (let x = 100 - stepX; x >= 0; x -= stepX) {
                    vLines.push(x);
                  }
                  // Linie poziome: zaczynają się od Y=80 (offset terenu)
                  const hLines = [];
                  for (let y = 80; y <= h; y += stepY) {
                    hLines.push(y);
                  }
                  for (let y = 80 - stepY; y >= 0; y -= stepY) {
                    hLines.push(y);
                  }
                  return (
                    <g className="snap-grid-layer" style={{ pointerEvents: 'none' }}>
                      {vLines.map((x, i) => (
                        <line key={`sg-v-${i}`} x1={x} y1={0} x2={x} y2={h} stroke="#3B82F6" strokeWidth="0.5" opacity="0.25" />
                      ))}
                      {hLines.map((y, i) => (
                        <line key={`sg-h-${i}`} x1={0} y1={y} x2={w} y2={y} stroke="#3B82F6" strokeWidth="0.5" opacity="0.25" />
                      ))}
                    </g>
                  );
                })()}
                {showElevationScale && renderVerticalScale('right')}
                {showElevationScaleLeft && renderVerticalScale('left')}
                {renderHorizontalScale()}
                {renderTerrainLine()}

                {/* Tytuł przekroju + skala (ruchomy blok) */}
                {showCrossSectionTitle && (() => {
                  const defaultPos = { x: 130, y: 30 };
                  const pos = crossSectionTitlePos || defaultPos;
                  const hasCustom = !!crossSectionTitlePos;
                  const scaleH = horizontalScaleToN(horizontalScale);
                  const scaleV = verticalScaleToN(scale);
                  const titleText = crossSectionTitle || 'PRZEKRÓJ GEOTECHNICZNY';
                  // Heurystyka szerokości tytułu (fontSize 20 bold ≈ 11 px na znak)
                  const titleW = titleText.length * 11;
                  const cx = pos.x + titleW / 2;
                  // Blok skali (SKALA + ułamek) wyśrodkowany pod tytułem
                  // SKALA → tekstem end na (cx - 28), ułamek (linia 50 px) wokół (cx + 5)
                  const skalaX = cx - 28;
                  const fracCx = cx + 5;
                  const blockW = Math.max(titleW + 40, 280);
                  const blockH = 64;
                  return (
                    <g
                      style={{ cursor: 'move' }}
                      onMouseDown={(e) => handleCrossSectionTitleDragStart(e.nativeEvent, defaultPos)}
                      onDoubleClick={(e) => { e.stopPropagation(); if (hasCustom) setCrossSectionTitlePos(null); }}
                    >
                      <rect
                        x={pos.x - 10}
                        y={pos.y - 22}
                        width={blockW}
                        height={blockH}
                        fill="transparent"
                      />
                      <text
                        x={pos.x}
                        y={pos.y}
                        fontSize={typography.crossSectionTitle.size}
                        fontWeight={typography.crossSectionTitle.weight}
                        fontFamily={typography.crossSectionTitle.family}
                        fill="#000"
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        {titleText}
                      </text>
                      <text
                        x={skalaX}
                        y={pos.y + 22}
                        textAnchor="end"
                        fontSize={typography.crossSectionScale.size}
                        fontWeight={typography.crossSectionScale.weight}
                        fontFamily={typography.crossSectionScale.family}
                        fill="#000"
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        SKALA
                      </text>
                      <text
                        x={fracCx}
                        y={pos.y + 16}
                        textAnchor="middle"
                        fontSize={typography.crossSectionScale.size}
                        fontWeight={typography.crossSectionScale.weight}
                        fontFamily={typography.crossSectionScale.family}
                        fill="#000"
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        1:{scaleH}
                      </text>
                      <line
                        x1={fracCx - 25}
                        y1={pos.y + 20}
                        x2={fracCx + 25}
                        y2={pos.y + 20}
                        stroke="#000"
                        strokeWidth="1"
                      />
                      <text
                        x={fracCx}
                        y={pos.y + 32}
                        textAnchor="middle"
                        fontSize={typography.crossSectionScale.size}
                        fontWeight={typography.crossSectionScale.weight}
                        fontFamily={typography.crossSectionScale.family}
                        fill="#000"
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        1:{scaleV}
                      </text>
                      <title>Przeciągnij aby przesunąć. Dwuklik = reset do domyślnej pozycji.</title>
                    </g>
                  );
                })()}

                <g clipPath={clipToTerrain && terrainProfile && terrainProfile.length >= 2 ? "url(#terrain-clip)" : undefined}>
                {/* Terrain-fill auto-connect wyłączony — patrz Interpolacja_2d.md sekcja „Wyłączenie terrain-fill" */}
                {connectLayers && findLayerConnections().map((conn, idx) => {
                  const pixelsPerMeter = scale / 10;
                  const x1 = 100 + conn.fromBorehole.distance * horizontalScale;
                  const x2 = 100 + conn.toBorehole.distance * horizontalScale;

                  // Klucze dla punktów połączeń (z connectionId)
                  const pointKeyFromTop = getConnectionPointKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, true, true, conn.connectionId);
                  const pointKeyFromBottom = getConnectionPointKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, false, true, conn.connectionId);
                  const pointKeyToTop = getConnectionPointKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, true, false, conn.connectionId);
                  const pointKeyToBottom = getConnectionPointKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, false, false, conn.connectionId);

                  // Pomocnicze funkcje do wyciągania przesunięć
                  const getAdjustment = (key, axis) => {
                    const adj = connectionPointAdjustments[key];
                    if (!adj) return 0;
                    return typeof adj === 'object' ? (adj[axis] || 0) : (axis === 'y' ? adj : 0);
                  };

                  // Oblicz pozycje z uwzględnieniem dostosowań X i Y
                  const x1TopAdjusted = x1 + getAdjustment(pointKeyFromTop, 'x');
                  const x1BottomAdjusted = x1 + getAdjustment(pointKeyFromBottom, 'x');
                  const x2TopAdjusted = x2 + getAdjustment(pointKeyToTop, 'x');
                  const x2BottomAdjusted = x2 + getAdjustment(pointKeyToBottom, 'x');

                  const y1Top = 80 + conn.fromTop * pixelsPerMeter + getAdjustment(pointKeyFromTop, 'y');
                  const y1Bottom = 80 + conn.fromBottom * pixelsPerMeter + getAdjustment(pointKeyFromBottom, 'y');
                  const y2Top = 80 + conn.toTop * pixelsPerMeter + getAdjustment(pointKeyToTop, 'y');
                  const y2Bottom = 80 + conn.toBottom * pixelsPerMeter + getAdjustment(pointKeyToBottom, 'y');

                  const connectionKeyTop = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'top', conn.connectionId);
                  const connectionKeyBottom = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'bottom', conn.connectionId);
                  const connectionKeyLeft = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'left', conn.connectionId);
                  const connectionKeyRight = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'right', conn.connectionId);

                  const controlPointTop = interpolationPoints[connectionKeyTop];
                  const controlPointBottom = interpolationPoints[connectionKeyBottom];
                  const controlPointLeft = interpolationPoints[connectionKeyLeft];
                  const controlPointRight = interpolationPoints[connectionKeyRight];

                  const isHovered = hoveredConnection === idx;

                  const defaultTopControl = { x: (x1TopAdjusted + x2TopAdjusted) / 2, y: (y1Top + y2Top) / 2 };
                  const defaultBottomControl = { x: (x1BottomAdjusted + x2BottomAdjusted) / 2, y: (y1Bottom + y2Bottom) / 2 };
                  const defaultLeftControl = { x: (x1TopAdjusted + x1BottomAdjusted) / 2, y: (y1Top + y1Bottom) / 2 };
                  const defaultRightControl = { x: (x2TopAdjusted + x2BottomAdjusted) / 2, y: (y2Top + y2Bottom) / 2 };

                  const pathTop = createBezierPath(x1TopAdjusted, y1Top, x2TopAdjusted, y2Top, controlPointTop || null);
                  const pathBottom = createBezierPath(x1BottomAdjusted, y1Bottom, x2BottomAdjusted, y2Bottom, controlPointBottom || null);
                  const pathLeft = createBezierPath(x1TopAdjusted, y1Top, x1BottomAdjusted, y1Bottom, controlPointLeft || null);
                  const pathRight = createBezierPath(x2TopAdjusted, y2Top, x2BottomAdjusted, y2Bottom, controlPointRight || null);

                  // === Buduj fillPath ===
                  let fillPath;
                  const xMid = (x1 + x2) / 2; // X w połowie drogi
                  // Flag: rozciągamy najwyższą strefę nad teren i przycinamy clipPath'em.
                  // Stosowane tylko dla normalnego trapezu (nie wedge, nie midPoint).
                  const extendsTopForRender = conn.extendsToTerrain && terrainProfile && terrainProfile.length >= 2 && !conn.wedgeEnd && !conn.midPoint;
                  // OVERLAP na górze i dole normalnego trapezu — zamyka antialiasingowe szczeliny
                  // między sąsiadującymi strefami (grunt↔zwietrzlina, zwietrzlina↔skała).
                  // Pomijane dla wedge/midPoint (specjalne kształty). Dla extendsTop górę zostawiamy
                  // na 0 — clipPath terenu i tak tnie.
                  const ZONE_OVERLAP_PX = 2;
                  const isNormalTrapez = !conn.wedgeEnd && !conn.midPoint;
                  const yTopOverlap = isNormalTrapez && !extendsTopForRender ? ZONE_OVERLAP_PX : 0;
                  const yBottomOverlap = isNormalTrapez ? ZONE_OVERLAP_PX : 0;
                  const y1TopF = y1Top - yTopOverlap;
                  const y2TopF = y2Top - yTopOverlap;
                  const y1BottomF = y1Bottom + yBottomOverlap;
                  const y2BottomF = y2Bottom + yBottomOverlap;
                  if (conn.wedgeEnd) {
                    // WYKLIN — trójkąt (fromTop===fromBottom) lub trapez (fromTop!==fromBottom)
                    // Punkt/krawędź w połowie drogi, pełna strefa na otworze
                    if (conn.wedgeEnd === 'left') {
                      // Lewy = punkt/krawędź w połowie, prawy = pełna strefa
                      const isTriangle = Math.abs(conn.fromTop - conn.fromBottom) < 0.01;
                      if (isTriangle) {
                        const wedgeYpx = 80 + conn.fromTop * pixelsPerMeter;
                        fillPath = `M ${xMid},${wedgeYpx} L ${x2TopAdjusted},${y2Top} L ${x2BottomAdjusted},${y2Bottom} Z`;
                      } else {
                        // Trapez: lewa krawędź w połowie (fromTop→fromBottom), prawa = pełna strefa
                        const midTopPx = 80 + conn.fromTop * pixelsPerMeter;
                        const midBottomPx = 80 + conn.fromBottom * pixelsPerMeter;
                        fillPath = `M ${xMid},${midTopPx} L ${x2TopAdjusted},${y2Top} L ${x2BottomAdjusted},${y2Bottom} L ${xMid},${midBottomPx} Z`;
                      }
                    } else {
                      // Prawy = punkt/krawędź w połowie, lewy = pełna strefa
                      const isTriangle = Math.abs(conn.toTop - conn.toBottom) < 0.01;
                      if (isTriangle) {
                        const wedgeYpx = 80 + conn.toTop * pixelsPerMeter;
                        fillPath = `M ${x1TopAdjusted},${y1Top} L ${xMid},${wedgeYpx} L ${x1BottomAdjusted},${y1Bottom} Z`;
                      } else {
                        const midTopPx = 80 + conn.toTop * pixelsPerMeter;
                        const midBottomPx = 80 + conn.toBottom * pixelsPerMeter;
                        fillPath = `M ${x1TopAdjusted},${y1Top} L ${xMid},${midTopPx} L ${xMid},${midBottomPx} L ${x1BottomAdjusted},${y1Bottom} Z`;
                      }
                    }
                  } else if (conn.midPoint) {
                    // 5-PUNKTOWY WIELOKĄT — grunt/skała z łamaniem w połowie drogi
                    const midTopY = 80 + conn.midPoint.topY * pixelsPerMeter;
                    const midBottomY = 80 + conn.midPoint.bottomY * pixelsPerMeter;

                    fillPath = `M ${x1TopAdjusted},${y1Top}`;
                    fillPath += ` L ${xMid},${midTopY}`;
                    fillPath += ` L ${x2TopAdjusted},${y2Top}`;
                    fillPath += ` L ${x2BottomAdjusted},${y2Bottom}`;
                    fillPath += ` L ${xMid},${midBottomY}`;
                    fillPath += ` L ${x1BottomAdjusted},${y1Bottom}`;
                    fillPath += ' Z';
                  } else {
                    // NORMALNY TRAPEZ — 4 krzywe Béziera (lub góra wypchnięta nad teren przy extendsToTerrain).
                    // Używamy y*F wariantów (top przesunięty o 0.5px w górę, bottom 0.5px w dół) żeby
                    // sąsiednie strefy zachodziły o 1px → znikają białe sierpy z antialiasingu.
                    const extendsTop = extendsTopForRender;
                    // Dla extendsToTerrain górę polygonu wypychamy do y=0 (top SVG), a clipPath terenu
                    // tnie nadmiar — w efekcie polygon idealnie dopasowuje się do krzywej terenu.
                    const topY1 = extendsTop ? 0 : y1TopF;
                    const topY2 = extendsTop ? 0 : y2TopF;
                    fillPath = `M ${x1TopAdjusted},${topY1}`;

                    // Góra (od lewego-góra do prawego-góra)
                    if (extendsTop) {
                      // Pozioma linia na y=0 — clipPath przytnie do krzywej terenu.
                      fillPath += ` L ${x2TopAdjusted},${topY2}`;
                    } else if (controlPointTop) {
                      const cp1x = x1TopAdjusted + ((controlPointTop.x - x1TopAdjusted) * 0.5);
                      const cp1y = y1TopF + ((controlPointTop.y - y1TopF) * 0.5);
                      const cp2x = x2TopAdjusted + ((controlPointTop.x - x2TopAdjusted) * 0.5);
                      const cp2y = y2TopF + ((controlPointTop.y - y2TopF) * 0.5);
                      fillPath += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${x2TopAdjusted},${y2TopF}`;
                    } else {
                      fillPath += ` L ${x2TopAdjusted},${y2TopF}`;
                    }

                    // Prawo (od prawego-góra do prawego-dół)
                    if (extendsTop) {
                      // Prosta linia w dół — clipPath obejmie nadmiar nad terenem.
                      fillPath += ` L ${x2BottomAdjusted},${y2BottomF}`;
                    } else if (controlPointRight) {
                      const cp1x = x2TopAdjusted + ((controlPointRight.x - x2TopAdjusted) * 0.5);
                      const cp1y = y2TopF + ((controlPointRight.y - y2TopF) * 0.5);
                      const cp2x = x2BottomAdjusted + ((controlPointRight.x - x2BottomAdjusted) * 0.5);
                      const cp2y = y2BottomF + ((controlPointRight.y - y2BottomF) * 0.5);
                      fillPath += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${x2BottomAdjusted},${y2BottomF}`;
                    } else {
                      fillPath += ` L ${x2BottomAdjusted},${y2BottomF}`;
                    }

                    // Dół (od prawego-dół do lewego-dół)
                    if (controlPointBottom) {
                      const cp1x = x2BottomAdjusted + ((controlPointBottom.x - x2BottomAdjusted) * 0.5);
                      const cp1y = y2BottomF + ((controlPointBottom.y - y2BottomF) * 0.5);
                      const cp2x = x1BottomAdjusted + ((controlPointBottom.x - x1BottomAdjusted) * 0.5);
                      const cp2y = y1BottomF + ((controlPointBottom.y - y1BottomF) * 0.5);
                      fillPath += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${x1BottomAdjusted},${y1BottomF}`;
                    } else {
                      fillPath += ` L ${x1BottomAdjusted},${y1BottomF}`;
                    }

                    // Lewo (od lewego-dół do lewego-góra)
                    if (extendsTop) {
                      // Prosta linia w górę do wypchniętego topY1=0 — clipPath obejmie nadmiar.
                      fillPath += ` L ${x1TopAdjusted},${topY1}`;
                    } else if (controlPointLeft) {
                      const cp1x = x1BottomAdjusted + ((controlPointLeft.x - x1BottomAdjusted) * 0.5);
                      const cp1y = y1BottomF + ((controlPointLeft.y - y1BottomF) * 0.5);
                      const cp2x = x1TopAdjusted + ((controlPointLeft.x - x1TopAdjusted) * 0.5);
                      const cp2y = y1TopF + ((controlPointLeft.y - y1TopF) * 0.5);
                      fillPath += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${x1TopAdjusted},${y1TopF}`;
                    } else {
                      fillPath += ` L ${x1TopAdjusted},${y1TopF}`;
                    }

                    fillPath += ' Z';
                  }

                  // Główny fill najwyższej strefy idzie w grupie z clipPath terenu
                  // (góra polygonu jest sztucznie wypchnięta nad teren — clip tnie do krzywej NMT).
                  const fillPathEls = (
                    <>
                      <path
                        d={fillPath}
                        fill={outlineOnly ? "none" : conn.color}
                        fillOpacity={isHovered && interpolationMode ? 0.7 : (conn.wedgeEnd ? 0.4 : 0.3)}
                        strokeOpacity={sharpenContours && !(isHovered && interpolationMode) && !(conn.wedgeEnd || conn.midPoint) ? 1 : (isHovered && interpolationMode ? 0.7 : (conn.wedgeEnd ? 0.4 : 0.3))}
                        stroke={isHovered && interpolationMode ? "#FF0000" : (outlineOnly ? toContourColor(conn.color) : ((conn.wedgeEnd || conn.midPoint) ? "none" : toContourColor(conn.color)))}
                        strokeWidth={isHovered && interpolationMode ? "2" : (outlineOnly ? (sharpenContours ? "2.5" : "1") : ((conn.wedgeEnd || conn.midPoint) ? "0" : (sharpenContours ? "2.5" : "1")))}
                      />
                      {!outlineOnly && conn.pattern && conn.pattern !== 'none' && (
                        <path
                          d={fillPath}
                          fill={`url(#pattern-${conn.pattern})`}
                          opacity={isHovered && interpolationMode ? "0.9" : "0.6"}
                          pointerEvents="none"
                        />
                      )}
                    </>
                  );

                  return (
                    <g
                      key={`conn-${idx}`}
                      onMouseEnter={() => interpolationMode && setHoveredConnection(idx)}
                      onMouseLeave={() => interpolationMode && setHoveredConnection(null)}
                    >
                      {extendsTopForRender ? (
                        <g clipPath="url(#terrain-clip)">{fillPathEls}</g>
                      ) : fillPathEls}

                      {!conn.wedgeEnd && !conn.midPoint && (
                        <>
                          <path d={pathTop} stroke={isHovered && interpolationMode ? "#FF0000" : "#000"} strokeWidth={isHovered && interpolationMode ? "2" : "0.5"} fill="none" opacity="0.3" />
                          <path d={pathBottom} stroke={isHovered && interpolationMode ? "#FF0000" : "#000"} strokeWidth={isHovered && interpolationMode ? "2" : "0.5"} fill="none" opacity="0.3" />
                        </>
                      )}

                      {interpolationMode && (
                        <>
                          <line x1={x1TopAdjusted} y1={y1Top} x2={(controlPointTop || defaultTopControl).x} y2={(controlPointTop || defaultTopControl).y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                          <line x1={(controlPointTop || defaultTopControl).x} y1={(controlPointTop || defaultTopControl).y} x2={x2TopAdjusted} y2={y2Top} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                          <line x1={x1BottomAdjusted} y1={y1Bottom} x2={(controlPointBottom || defaultBottomControl).x} y2={(controlPointBottom || defaultBottomControl).y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                          <line x1={(controlPointBottom || defaultBottomControl).x} y1={(controlPointBottom || defaultBottomControl).y} x2={x2BottomAdjusted} y2={y2Bottom} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                        </>
                      )}

                      {/* Przycisk usuwania dla połączeń w trybie interpolacji */}
                      {interpolationMode && (
                        <g transform={`translate(${(x1 + x2) / 2}, ${(y1Top + y1Bottom + y2Top + y2Bottom) / 4})`}>
                          <circle
                            r="15"
                            fill="#FF0000"
                            stroke="#FFF"
                            strokeWidth="2"
                            cursor="pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConnection(conn);
                            }}
                          />
                          <text
                            x="0"
                            y="0"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="#FFF"
                            fontSize="18"
                            fontWeight="bold"
                            style={{ pointerEvents: 'none' }}
                          >
                            ×
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
                </g>

                {boreholes.map((borehole, index) => {
                  // Otwory pośrednie z przekroju 3D (hidden) — istnieją w danych dla interpolacji
                  // warstw, ale nie rysujemy ich słupka/etykiet, żeby pokazać tylko realne końce A/B.
                  if (borehole.hidden) return null;
                  const x = 100 + (borehole.distance * horizontalScale);
                  const pixelsPerMeter = scale / 10;
                  const maxDepth = getMaxDepth();

                  const depthOffset = (maxDepth - borehole.depth) * pixelsPerMeter;
                  let currentY = 80 + depthOffset;
                  let prevSideBottomY = -Infinity;

                  // Wybór strony etykiet bocznych — patrzymy do którego sąsiada otwór ma dalej.
                  // Skrajne otwory zawsze "na zewnątrz" (pierwszy lewo, ostatni prawo).
                  const prevBhX = index > 0 ? 100 + (boreholes[index - 1].distance * horizontalScale) : null;
                  const nextBhX = index < boreholes.length - 1 ? 100 + (boreholes[index + 1].distance * horizontalScale) : null;
                  const leftGap = prevBhX !== null ? (x - prevBhX) : Infinity;
                  const rightGap = nextBhX !== null ? (nextBhX - x) : Infinity;
                  const sideLabelsOnLeft = leftGap >= rightGap;
                  
                  return (
                    <g key={borehole.id}>
                      <rect
                        x={x - 50}
                        y={currentY - 35}
                        width="100"
                        height="25"
                        fill="transparent"
                        style={{
                          cursor: interpolationMode ? 'default' : 'ew-resize',
                          touchAction: 'none',
                          WebkitTouchCallout: 'none',
                          WebkitUserSelect: 'none',
                          userSelect: 'none'
                        }}
                        onMouseDown={(e) => !interpolationMode && handleBoreholeHeaderDragStart(e.nativeEvent, borehole.id)}
                        onTouchStart={(e) => !interpolationMode && handleBoreholeHeaderDragStart(e.nativeEvent, borehole.id)}
                      />
                      
                      {(() => {
                        const isDirty = dirtyBoreholeIds?.has?.(borehole.id);
                        const canRefresh = !!borehole.sourceBoreholeId && !!onRevertBorehole;
                        const nameColor = borehole.isVirtual ? '#059669' : (isDirty ? '#DC2626' : '#000');
                        const handleRevertClick = async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!canRefresh) return;
                          const msg = isDirty
                            ? `Cofnąć zmiany w otworze "${borehole.name}" do wartości z bazy otworów?\n\nLokalne zmiany zostaną utracone.`
                            : `Pobrać świeże dane otworu "${borehole.name}" z bazy (m.in. metadane: nadzorujący wiercenie, data wykonania)?`;
                          if (!window.confirm(msg)) return;
                          const fresh = await onRevertBorehole(borehole);
                          if (fresh) {
                            setBoreholes(prev => prev.map(b => b.id === borehole.id ? fresh : b));
                          }
                        };
                        return (
                          <>
                            {showBoreholeMeta && (borehole.supervisingDrilling || borehole.dateExecution) && (() => {
                              const off = boreholeMetaOffsets[borehole.id] || { dx: 0, dy: 0 };
                              const hasOffset = off.dx !== 0 || off.dy !== 0;
                              return (
                                <g
                                  transform={`translate(${off.dx}, ${off.dy})`}
                                  style={{ cursor: 'move' }}
                                  onMouseDown={(e) => handleBoreholeMetaDragStart(e.nativeEvent, borehole.id)}
                                  onDoubleClick={(e) => { e.stopPropagation(); if (hasOffset) resetBoreholeMetaOffset(borehole.id); }}
                                >
                                  {/* Tło-łapka żeby było co chwycić nawet gdy tekst krótki */}
                                  <rect
                                    x={x - 80}
                                    y={currentY - 76}
                                    width="160"
                                    height="34"
                                    fill="transparent"
                                  />
                                  {borehole.supervisingDrilling && (
                                    <text
                                      x={x}
                                      y={currentY - 64}
                                      textAnchor="middle"
                                      fontSize={typography.boreholeMeta.size}
                                      fontWeight={typography.boreholeMeta.weight}
                                      fontFamily={typography.boreholeMeta.family}
                                      fill="#475569"
                                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                                    >
                                      👤 {borehole.supervisingDrilling}
                                    </text>
                                  )}
                                  {borehole.dateExecution && (
                                    <text
                                      x={x}
                                      y={currentY - 50}
                                      textAnchor="middle"
                                      fontSize={typography.boreholeMeta.size}
                                      fontWeight={typography.boreholeMeta.weight}
                                      fontFamily={typography.boreholeMeta.family}
                                      fill="#475569"
                                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                                    >
                                      📅 {formatDateExecution(borehole.dateExecution)}
                                    </text>
                                  )}
                                  <title>Przeciągnij aby przesunąć. Dwuklik = reset do domyślnej pozycji.</title>
                                </g>
                              );
                            })()}
                            <text
                              x={x}
                              y={currentY - 30}
                              textAnchor="middle"
                              fontWeight={typography.boreholeName.weight}
                              fontSize={typography.boreholeName.size}
                              fontFamily={typography.boreholeName.family}
                              fill={nameColor}
                              style={{ pointerEvents: canRefresh ? 'auto' : 'none', cursor: canRefresh ? 'context-menu' : 'default' }}
                              onContextMenu={handleRevertClick}
                            >
                              {isDirty ? '● ' : ''}{borehole.name}
                              {canRefresh && (
                                <title>
                                  {isDirty
                                    ? 'Zmiany w otworze nie są jeszcze zapisane w bazie. Prawy klik = cofnij do wartości z bazy.'
                                    : 'Prawy klik = pobierz świeże dane z bazy (m.in. metadane: nadzorujący wiercenie, data wykonania).'}
                                </title>
                              )}
                            </text>
                            <text
                              x={x}
                              y={currentY - 12}
                              textAnchor="middle"
                              fontSize={typography.boreholeElevation.size}
                              fontWeight={typography.boreholeElevation.weight}
                              fontFamily={typography.boreholeElevation.family}
                              fill={nameColor}
                              style={{ pointerEvents: 'none' }}
                            >
                              {borehole.depth.toFixed(2)}
                            </text>
                          </>
                        );
                      })()}

                      {showBoreholeRectangles && (
                        <line
                          x1={x}
                          y1={currentY}
                          x2={x}
                          y2={currentY + getTotalThickness(borehole.layers) * pixelsPerMeter}
                          stroke={borehole.isVirtual ? '#059669' : '#000'}
                          strokeWidth={borehole.isVirtual ? '1.5' : '2'}
                          strokeDasharray={borehole.isVirtual ? '6,4' : 'none'}
                        />
                      )}
                      
                      {borehole.layers.map((layer) => {
                        const height = layer.thickness * pixelsPerMeter;
                        const layerY = currentY;
                        const isResizing = resizingLayer?.boreholeId === borehole.id && resizingLayer?.layerId === layer.id;
                        currentY += height;

                        return (
                          <g key={layer.id}>
                            {/* Warstwa z kolorem */}
                            <rect
                              x={x - 18.75}
                              y={layerY}
                              width="37.5"
                              height={height}
                              fill={outlineOnly ? "none" : (showBoreholeRectangles ? toPastelColor(effectiveLayerColor(layer)) : "transparent")}
                              stroke={
                                (selectedLayerForConnection?.boreholeId === borehole.id && selectedLayerForConnection?.layerId === layer.id)
                                  ? "#00FF00"
                                  : isResizing ? "#FF0000" : (showBoreholeRectangles || outlineOnly ? (borehole.isVirtual ? '#059669' : '#000') : "transparent")
                              }
                              strokeWidth={
                                (selectedLayerForConnection?.boreholeId === borehole.id && selectedLayerForConnection?.layerId === layer.id)
                                  ? "4"
                                  : isResizing ? "3" : "1.5"
                              }
                              strokeDasharray={borehole.isVirtual && !isResizing && !(selectedLayerForConnection?.boreholeId === borehole.id && selectedLayerForConnection?.layerId === layer.id) ? '4,2' : 'none'}
                              style={{ cursor: manualConnectionMode ? 'pointer' : (interpolationMode ? 'default' : 'ns-resize'), touchAction: 'none' }}
                              onMouseDown={(e) => {
                                if (manualConnectionMode) {
                                  e.stopPropagation();
                                  handleLayerClickForConnection(borehole.id, layer.id);
                                } else if (!interpolationMode) {
                                  e.stopPropagation();
                                  handleResizeStart(e.nativeEvent, borehole.id, layer.id);
                                }
                              }}
                              onTouchStart={(e) => {
                                if (manualConnectionMode) {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleLayerClickForConnection(borehole.id, layer.id);
                                } else if (!interpolationMode) {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleResizeStart(e.nativeEvent, borehole.id, layer.id);
                                }
                              }}
                            />
                            {/* Warstwa z szrafurą - pełne pokrycie */}
                            {!outlineOnly && showBoreholeRectangles && layer.pattern && layer.pattern !== 'none' && (
                              <rect
                                x={x - 18.75}
                                y={layerY}
                                width="37.5"
                                height={height}
                                fill={`url(#pattern-${layer.pattern})`}
                                opacity="1"
                                pointerEvents="none"
                              />
                            )}
                            {(() => {
                              // Buduje listę pól etykiety w kolejności zadanej przez `labelFieldOrder`.
                              // Każde pole: { key, txt, fill, bold }. Niewidoczne / puste pomijane.
                              const buildPart = (key) => {
                                switch (key) {
                                  case 'geotechnicalLayer': {
                                    const v = layer.geotechnicalLayer;
                                    if (!showGeotechnicalLayer || !v || v === '-') return null;
                                    return { key, txt: v, fill: '#1976d2', bold: true };
                                  }
                                  case 'soilName': {
                                    if (!showSoilName) return null;
                                    const v = layer.compositeLithology || layer.name || '';
                                    if (!v) return null;
                                    return { key, txt: v, fill: '#000', bold: true };
                                  }
                                  case 'genesisAndStratigraphy': {
                                    if (!showGenesisAndStratigraphy || !layer.genesisAndStratigraphy) return null;
                                    return { key, txt: layer.genesisAndStratigraphy.split('-')[0].trim(), fill: '#555', bold: false };
                                  }
                                  case 'soilState': {
                                    if (!showSoilState || !layer.soilState) return null;
                                    return { key, txt: layer.soilState.split('-')[0].trim(), fill: '#555', bold: false };
                                  }
                                  case 'thickness': {
                                    if (!showThickness) return null;
                                    return { key, txt: `${Math.round((Number(layer.thickness) || 0) * 100) / 100}m`, fill: '#333', bold: false };
                                  }
                                  default:
                                    return null;
                                }
                              };

                              const parts = (labelFieldOrder || [])
                                .map(buildPart)
                                .filter(Boolean);

                              if (parts.length === 0) return null;

                              const renderTspans = () => parts.map((p, i) => (
                                <React.Fragment key={p.key}>
                                  {i > 0 && <tspan>{' '}</tspan>}
                                  <tspan fill={p.fill} fontWeight={p.bold ? 'bold' : 'normal'}>{p.txt}</tspan>
                                </React.Fragment>
                              ));

                              const center = layerY + height / 2;
                              const gap = 14;
                              const fitsInside = height >= gap + 2;

                              if (fitsInside) {
                                // — RENDER WEWNĄTRZ WARSTWY — jedna linia, pola wg labelFieldOrder —
                                return (
                                  <text
                                    x={x}
                                    y={center}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize="14"
                                    style={{ pointerEvents: 'none', whiteSpace: 'pre' }}
                                    xmlSpace="preserve"
                                  >
                                    {renderTspans()}
                                  </text>
                                );
                              }

                              // — RENDER BOCZNY — strona zależna od sideLabelsOnLeft (patrz wybór sąsiada wyżej) —
                              const lineSpacing = 15;
                              const sideCenterY = Math.max(center, prevSideBottomY + lineSpacing);
                              prevSideBottomY = sideCenterY;

                              // Przy prawej stronie omijamy kolumnę głębokości (x+27..~x+50).
                              const labelX = sideLabelsOnLeft ? (x - 22) : (x + 52);
                              const leaderStartX = sideLabelsOnLeft ? (x - 18.75) : (x + 18.75);
                              const leaderEndX = sideLabelsOnLeft ? (x - 20) : (x + 50);
                              const leaderColor = borehole.isVirtual ? '#059669' : '#888';

                              return (
                                <>
                                  <line
                                    x1={leaderStartX}
                                    y1={center}
                                    x2={leaderEndX}
                                    y2={sideCenterY}
                                    stroke={leaderColor}
                                    strokeWidth="0.75"
                                    style={{ pointerEvents: 'none' }}
                                  />
                                  <text
                                    x={labelX}
                                    y={sideCenterY}
                                    textAnchor={sideLabelsOnLeft ? "end" : "start"}
                                    dominantBaseline="middle"
                                    fontSize="13"
                                    style={{ pointerEvents: 'none', whiteSpace: 'pre' }}
                                    xmlSpace="preserve"
                                  >
                                    {renderTspans()}
                                  </text>
                                </>
                              );
                            })()}
                          </g>
                        );
                      })}

                      {/* Głębokości na końcu każdej warstwy (na przecięciu) */}
                      {showDepths && (() => {
                        let cumulativeDepth = 0;
                        let layerY = 80 + depthOffset;

                        return borehole.layers.map((layer, layerIndex) => {
                          cumulativeDepth += layer.thickness;
                          const height = layer.thickness * pixelsPerMeter;
                          layerY += height;

                          return (
                            <text
                              key={`depth-${layer.id}`}
                              x={x + 27} // 8px w lewo od pierwotnej pozycji (było x + 35)
                              y={layerY + 2} // 2 piksele niżej
                              textAnchor="start"
                              dominantBaseline="middle"
                              fontSize="11"
                              fontWeight="bold"
                              fill="#666"
                              style={{ pointerEvents: 'none' }}
                            >
                              {cumulativeDepth.toFixed(1)}
                            </text>
                          );
                        });
                      })()}

                      {/* Symbole poziomów wody - renderowane po lewej stronie słupka */}
                      {showWater && borehole.waterLevels && borehole.waterLevels.map((waterLevel) => {
                        if (waterLevel.type === 'stabilized-drilled') {
                          // Zwierciadło ustabilizowane/nawiercone (od-do z trójkątami)
                          const y1 = 80 + depthOffset + waterLevel.depthFrom * pixelsPerMeter;
                          const y2 = 80 + depthOffset + waterLevel.depthTo * pixelsPerMeter;
                          const leftX = x - 40 - 6; // Pozycja po lewej stronie słupka, odsunięta o 6px

                          return (
                            <g key={waterLevel.id}>
                              {/* Niewidoczny prostokąt wokół linii zygzakowatej - większy obszar klikalny */}
                              <rect
                                x={leftX - 2 - 5 - 5 - 3}
                                y={Math.min(y1, y2) + 6 - 10}
                                width={12 + 5 + 5 + 10 + 3}
                                height={Math.abs(y2 - y1) + 20}
                                fill="transparent"
                                style={{ cursor: 'grab' }}
                                onMouseDown={(e) => handleWaterLevelMouseDown(e, borehole.id, waterLevel.id, 'both')}
                                onTouchStart={(e) => handleWaterLevelMouseDown(e, borehole.id, waterLevel.id, 'both')}
                              />

                              {/* Łamana linia łącząca oba trójkąty - jedna ciągła ścieżka, przedłużona o połowę szerokości */}
                              <path
                                d={`M ${leftX - 2 - 5 - 3},${y1 + 6}
                                    L ${leftX + 5 - 3},${y1 + 6}
                                    L ${leftX + 5 - 3},${y2 + 6}
                                    L ${leftX + 12 + 5},${y2 + 6}`}
                                stroke="blue"
                                strokeWidth="1.33"
                                fill="none"
                                strokeLinejoin="miter"
                                style={{ cursor: 'grab', pointerEvents: 'none' }}
                              />

                              {/* Niewidoczny prostokąt wokół trójkąta ustabilizowanego - większy obszar klikalny */}
                              <rect
                                x={leftX - 2 - 8 - 5 - 3}
                                y={y1 + 6 - 16 - 0.67 - 5}
                                width={26}
                                height={26}
                                fill="transparent"
                                style={{ cursor: 'grab' }}
                                onMouseDown={(e) => handleWaterLevelMouseDown(e, borehole.id, waterLevel.id, 'stabilized')}
                                onTouchStart={(e) => handleWaterLevelMouseDown(e, borehole.id, waterLevel.id, 'stabilized')}
                              />

                              {/* Trójkąt wypełniony (ustabilizowane) - szczyt leży NA linii */}
                              <polygon
                                points={`${leftX - 2 - 3},${y1 + 6 - 0.67} ${leftX - 2 - 8 - 3},${y1 + 6 - 16 - 0.67} ${leftX - 2 + 8 - 3},${y1 + 6 - 16 - 0.67}`}
                                fill="blue"
                                stroke="blue"
                                strokeWidth="1.5"
                                style={{ cursor: 'grab', pointerEvents: 'none' }}
                              />

                              {/* Niewidoczny prostokąt wokół trójkąta nawierczonego - większy obszar klikalny */}
                              <rect
                                x={leftX + 12 - 8 - 5}
                                y={y2 + 6 - 16 - 0.67 - 5}
                                width={26}
                                height={26}
                                fill="transparent"
                                style={{ cursor: 'grab' }}
                                onMouseDown={(e) => handleWaterLevelMouseDown(e, borehole.id, waterLevel.id, 'drilled')}
                                onTouchStart={(e) => handleWaterLevelMouseDown(e, borehole.id, waterLevel.id, 'drilled')}
                              />

                              {/* Trójkąt przezroczysty (nawiercone) - szczyt leży NA linii */}
                              <polygon
                                points={`${leftX + 12},${y2 + 6 - 0.67} ${leftX + 12 - 8},${y2 + 6 - 16 - 0.67} ${leftX + 12 + 8},${y2 + 6 - 16 - 0.67}`}
                                fill="none"
                                stroke="blue"
                                strokeWidth="2"
                                style={{ cursor: 'grab', pointerEvents: 'none' }}
                              />

                              {/* Etykieta głębokości ustabilizowanego POD niebieskim trójkątem */}
                              <text
                                x={leftX - 2 - 3 - 1 - 1 - 5}
                                y={y1 + 6 + 18 - 3}
                                fontSize="10.8"
                                fontWeight="normal"
                                fill="blue"
                                textAnchor="middle"
                              >
                                {waterLevel.depthFrom.toFixed(1)}
                              </text>

                              {/* Etykieta głębokości nawierczonego POD białym trójkątem */}
                              <text
                                x={leftX + 12}
                                y={y2 + 6 + 18 - 3}
                                fontSize="10.8"
                                fontWeight="normal"
                                fill="blue"
                                textAnchor="middle"
                              >
                                {waterLevel.depthTo.toFixed(1)}
                              </text>
                            </g>
                          );
                        } else if (waterLevel.type === 'seepage') {
                          // Sączenie (linia falista - kolor niebieski, z głębokością)
                          const y = 80 + depthOffset + waterLevel.depth * pixelsPerMeter;
                          const leftX = x - 40 - 6; // Pozycja po lewej stronie słupka, odsunięta o 6px

                          return (
                            <g key={waterLevel.id}>
                              {/* Niewidoczny prostokąt wokół linii sączenia - większy obszar klikalny */}
                              <rect
                                x={leftX - 15 - 5}
                                y={y - 6 - 5}
                                width={40}
                                height={20}
                                fill="transparent"
                                style={{ cursor: 'grab' }}
                                onMouseDown={(e) => handleWaterLevelMouseDown(e, borehole.id, waterLevel.id, 'seepage')}
                                onTouchStart={(e) => handleWaterLevelMouseDown(e, borehole.id, waterLevel.id, 'seepage')}
                              />

                              {/* Linia falista - wyższa amplituda, kolor niebieski */}
                              <path
                                d={`M ${leftX - 15},${y} Q ${leftX - 12},${y - 6} ${leftX - 9},${y} T ${leftX - 3},${y} T ${leftX + 3},${y} T ${leftX + 9},${y} T ${leftX + 15},${y}`}
                                stroke="blue"
                                strokeWidth="2"
                                fill="none"
                                style={{ cursor: 'grab', pointerEvents: 'none' }}
                              />

                              {/* Głębokość pod linią sączenia */}
                              <text
                                x={leftX}
                                y={y + 12 + 4}
                                fontSize="12"
                                fontWeight="normal"
                                fill="blue"
                                textAnchor="middle"
                              >
                                {waterLevel.depth.toFixed(1)}
                              </text>
                            </g>
                          );
                        }
                        return null;
                      })}
                    </g>
                  );
                })}

                {/* Linie zwierciadła wody — swobodne (ustabilizowane) i napięte (nawiercone) */}
                {showWater && boreholes.length >= 2 && (showFreeWaterLine || showConfinedWaterLine) && (() => {
                  const pixelsPerMeter = scale / 10;
                  const maxDepth = getMaxDepth();

                  // getDepth: zwraca głębokość lub null (brak wody)
                  const buildWaterPath = (getDepth) => {
                    // Tylko otwory z wodą — pomijamy te bez
                    const boreholePoints = boreholes
                      .map(borehole => {
                        const depth = getDepth(borehole);
                        if (depth === null) return null;
                        const waterElevation = borehole.depth - depth;
                        return {
                          x: 100 + borehole.distance * horizontalScale,
                          y: 80 + (maxDepth - waterElevation) * pixelsPerMeter,
                        };
                      })
                      .filter(p => p !== null);

                    if (smoothWaterLine && boreholePoints.length >= 2) {
                      // Gładka krzywa Catmull-Rom przez punkty otworów
                      let d = `M ${boreholePoints[0].x} ${boreholePoints[0].y}`;

                      for (let i = 0; i < boreholePoints.length - 1; i++) {
                        const p0 = boreholePoints[Math.max(0, i - 1)];
                        const p1 = boreholePoints[i];
                        const p2 = boreholePoints[i + 1];
                        const p3 = boreholePoints[Math.min(boreholePoints.length - 1, i + 2)];

                        // Catmull-Rom → cubic Bézier control points
                        const cp1x = p1.x + (p2.x - p0.x) / 6;
                        const cp1y = p1.y + (p2.y - p0.y) / 6;
                        const cp2x = p2.x - (p3.x - p1.x) / 6;
                        const cp2y = p2.y - (p3.y - p1.y) / 6;

                        d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
                      }
                      return d;
                    }

                    // Prosta linia
                    return boreholePoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
                  };

                  return (
                    <g>
                      {/* Zwierciadło swobodne — najwyższe ustabilizowane (depthFrom) */}
                      {showFreeWaterLine && (
                        <path
                          d={buildWaterPath(bh => {
                            const stabilized = (bh.waterLevels || [])
                              .filter(w => w.type === 'stabilized-drilled' && w.depthFrom != null);
                            if (stabilized.length > 0) return Math.min(...stabilized.map(w => w.depthFrom));
                            // Fallback: pierwsze sączenie
                            const seepage = (bh.waterLevels || [])
                              .filter(w => w.type === 'seepage' && w.depth != null);
                            return seepage.length > 0 ? Math.min(...seepage.map(w => w.depth)) : null;
                          })}
                          stroke="blue"
                          strokeWidth="1.5"
                          strokeDasharray="8,4"
                          fill="none"
                          pointerEvents="none"
                          opacity="0.7"
                        />
                      )}
                      {/* Zwierciadło napięte — najwyższe nawiercone (depthTo) */}
                      {showConfinedWaterLine && (
                        <path
                          d={buildWaterPath(bh => {
                            const stabilized = (bh.waterLevels || [])
                              .filter(w => w.type === 'stabilized-drilled' && w.depthTo != null);
                            if (stabilized.length > 0) return Math.min(...stabilized.map(w => w.depthTo));
                            // Fallback: pierwsze sączenie
                            const seepage = (bh.waterLevels || [])
                              .filter(w => w.type === 'seepage' && w.depth != null);
                            return seepage.length > 0 ? Math.min(...seepage.map(w => w.depth)) : null;
                          })}
                          stroke="blue"
                          strokeWidth="1.5"
                          strokeDasharray="4,3"
                          fill="none"
                          pointerEvents="none"
                          opacity="0.5"
                        />
                      )}
                    </g>
                  );
                })()}

                {/* Niebieskie punkty połączeń - renderowane NA WIERZCHU wszystkich warstw i otworów */}
                {interpolationMode && findLayerConnections().map((conn, idx) => {
                  const pixelsPerMeter = scale / 10;
                  const x1 = 100 + conn.fromBorehole.distance * horizontalScale;
                  const x2 = 100 + conn.toBorehole.distance * horizontalScale;

                  // Klucze dla punktów połączeń (z connectionId)
                  const pointKeyFromTop = getConnectionPointKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, true, true, conn.connectionId);
                  const pointKeyFromBottom = getConnectionPointKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, false, true, conn.connectionId);
                  const pointKeyToTop = getConnectionPointKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, true, false, conn.connectionId);
                  const pointKeyToBottom = getConnectionPointKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, false, false, conn.connectionId);

                  // Pomocnicze funkcje do wyciągania przesunięć
                  const getAdjustment = (key, axis) => {
                    const adj = connectionPointAdjustments[key];
                    if (!adj) return 0;
                    return typeof adj === 'object' ? (adj[axis] || 0) : (axis === 'y' ? adj : 0);
                  };

                  // Oblicz pozycje z uwzględnieniem dostosowań X i Y
                  const x1TopAdjusted = x1 + getAdjustment(pointKeyFromTop, 'x');
                  const x1BottomAdjusted = x1 + getAdjustment(pointKeyFromBottom, 'x');
                  const x2TopAdjusted = x2 + getAdjustment(pointKeyToTop, 'x');
                  const x2BottomAdjusted = x2 + getAdjustment(pointKeyToBottom, 'x');

                  const y1Top = 80 + conn.fromTop * pixelsPerMeter + getAdjustment(pointKeyFromTop, 'y');
                  const y1Bottom = 80 + conn.fromBottom * pixelsPerMeter + getAdjustment(pointKeyFromBottom, 'y');
                  const y2Top = 80 + conn.toTop * pixelsPerMeter + getAdjustment(pointKeyToTop, 'y');
                  const y2Bottom = 80 + conn.toBottom * pixelsPerMeter + getAdjustment(pointKeyToBottom, 'y');

                  return (
                    <g key={`conn-points-${idx}`}>
                      {/* Punkty połączeń warstw - klikalne kółka */}
                      {/* Lewy górny punkt */}
                      <circle
                        cx={x1TopAdjusted}
                        cy={y1Top}
                        r="8"
                        fill={connectionPointAdjustments[pointKeyFromTop] ? "#FF6B00" : conn.color}
                        stroke="#FFF"
                        strokeWidth="2"
                        cursor="move"
                        onMouseDown={(e) => handleConnectionPointMouseDown(e, pointKeyFromTop, x1TopAdjusted, y1Top)}
                        onTouchStart={(e) => handleConnectionPointMouseDown(e, pointKeyFromTop, x1TopAdjusted, y1Top)}
                      />
                      {/* Lewy dolny punkt */}
                      <circle
                        cx={x1BottomAdjusted}
                        cy={y1Bottom}
                        r="8"
                        fill={connectionPointAdjustments[pointKeyFromBottom] ? "#FF6B00" : conn.color}
                        stroke="#FFF"
                        strokeWidth="2"
                        cursor="move"
                        onMouseDown={(e) => handleConnectionPointMouseDown(e, pointKeyFromBottom, x1BottomAdjusted, y1Bottom)}
                        onTouchStart={(e) => handleConnectionPointMouseDown(e, pointKeyFromBottom, x1BottomAdjusted, y1Bottom)}
                      />
                      {/* Prawy górny punkt */}
                      <circle
                        cx={x2TopAdjusted}
                        cy={y2Top}
                        r="8"
                        fill={connectionPointAdjustments[pointKeyToTop] ? "#FF6B00" : conn.color}
                        stroke="#FFF"
                        strokeWidth="2"
                        cursor="move"
                        onMouseDown={(e) => handleConnectionPointMouseDown(e, pointKeyToTop, x2TopAdjusted, y2Top)}
                        onTouchStart={(e) => handleConnectionPointMouseDown(e, pointKeyToTop, x2TopAdjusted, y2Top)}
                      />
                      {/* Prawy dolny punkt */}
                      <circle
                        cx={x2BottomAdjusted}
                        cy={y2Bottom}
                        r="8"
                        fill={connectionPointAdjustments[pointKeyToBottom] ? "#FF6B00" : conn.color}
                        stroke="#FFF"
                        strokeWidth="2"
                        cursor="move"
                        onMouseDown={(e) => handleConnectionPointMouseDown(e, pointKeyToBottom, x2BottomAdjusted, y2Bottom)}
                        onTouchStart={(e) => handleConnectionPointMouseDown(e, pointKeyToBottom, x2BottomAdjusted, y2Bottom)}
                      />

                      {/* Przyciski resetowania dla zmodyfikowanych punktów */}
                      {connectionPointAdjustments[pointKeyFromTop] && (
                        <g transform={`translate(${x1TopAdjusted + 12}, ${y1Top - 12})`}>
                          <circle r="8" fill="#FF0000" cursor="pointer" onClick={() => resetConnectionPoint(pointKeyFromTop)} />
                          <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12" fontWeight="bold" cursor="pointer" onClick={() => resetConnectionPoint(pointKeyFromTop)}>×</text>
                        </g>
                      )}
                      {connectionPointAdjustments[pointKeyFromBottom] && (
                        <g transform={`translate(${x1BottomAdjusted + 12}, ${y1Bottom + 12})`}>
                          <circle r="8" fill="#FF0000" cursor="pointer" onClick={() => resetConnectionPoint(pointKeyFromBottom)} />
                          <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12" fontWeight="bold" cursor="pointer" onClick={() => resetConnectionPoint(pointKeyFromBottom)}>×</text>
                        </g>
                      )}
                      {connectionPointAdjustments[pointKeyToTop] && (
                        <g transform={`translate(${x2TopAdjusted - 12}, ${y2Top - 12})`}>
                          <circle r="8" fill="#FF0000" cursor="pointer" onClick={() => resetConnectionPoint(pointKeyToTop)} />
                          <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12" fontWeight="bold" cursor="pointer" onClick={() => resetConnectionPoint(pointKeyToTop)}>×</text>
                        </g>
                      )}
                      {connectionPointAdjustments[pointKeyToBottom] && (
                        <g transform={`translate(${x2BottomAdjusted - 12}, ${y2Bottom + 12})`}>
                          <circle r="8" fill="#FF0000" cursor="pointer" onClick={() => resetConnectionPoint(pointKeyToBottom)} />
                          <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12" fontWeight="bold" cursor="pointer" onClick={() => resetConnectionPoint(pointKeyToBottom)}>×</text>
                        </g>
                      )}
                    </g>
                  );
                })}

                {/* Punkty interpolacji renderowane na końcu - zawsze na wierzchu */}
                {interpolationMode && connectLayers && findLayerConnections().map((conn, idx) => {
                  const pixelsPerMeter = scale / 10;
                  const x1 = 100 + conn.fromBorehole.distance * horizontalScale;
                  const x2 = 100 + conn.toBorehole.distance * horizontalScale;
                  const y1Top = 80 + conn.fromTop * pixelsPerMeter;
                  const y1Bottom = 80 + conn.fromBottom * pixelsPerMeter;
                  const y2Top = 80 + conn.toTop * pixelsPerMeter;
                  const y2Bottom = 80 + conn.toBottom * pixelsPerMeter;

                  const connectionKeyTop = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'top', conn.connectionId);
                  const connectionKeyBottom = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'bottom', conn.connectionId);
                  const connectionKeyLeft = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'left', conn.connectionId);
                  const connectionKeyRight = getConnectionKey(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'right', conn.connectionId);

                  const controlPointTop = interpolationPoints[connectionKeyTop];
                  const controlPointBottom = interpolationPoints[connectionKeyBottom];
                  const controlPointLeft = interpolationPoints[connectionKeyLeft];
                  const controlPointRight = interpolationPoints[connectionKeyRight];

                  const isHovered = hoveredConnection === idx;

                  const defaultTopControl = { x: (x1 + x2) / 2, y: (y1Top + y2Top) / 2 };
                  const defaultBottomControl = { x: (x1 + x2) / 2, y: (y1Bottom + y2Bottom) / 2 };
                  const defaultLeftControl = { x: x1, y: (y1Top + y1Bottom) / 2 };
                  const defaultRightControl = { x: x2, y: (y2Top + y2Bottom) / 2 };

                  return (
                    <g key={`interp-points-${idx}`}>
                      <circle
                        cx={(controlPointTop || defaultTopControl).x}
                        cy={(controlPointTop || defaultTopControl).y}
                        r="16"
                        fill={controlPointTop ? "#FF6600" : "#4CAF50"}
                        stroke="#FFF"
                        strokeWidth="3"
                        style={{ cursor: 'move', touchAction: 'none' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleInterpolationPointDrag(e.nativeEvent, connectionKeyTop);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleInterpolationPointDrag(e.nativeEvent, connectionKeyTop);
                        }}
                      />

                      <circle
                        cx={(controlPointBottom || defaultBottomControl).x}
                        cy={(controlPointBottom || defaultBottomControl).y}
                        r="16"
                        fill={controlPointBottom ? "#FF6600" : "#4CAF50"}
                        stroke="#FFF"
                        strokeWidth="3"
                        style={{ cursor: 'move', touchAction: 'none' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleInterpolationPointDrag(e.nativeEvent, connectionKeyBottom);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleInterpolationPointDrag(e.nativeEvent, connectionKeyBottom);
                        }}
                      />

                      <circle
                        cx={(controlPointLeft || defaultLeftControl).x}
                        cy={(controlPointLeft || defaultLeftControl).y}
                        r="16"
                        fill={controlPointLeft ? "#FF6600" : "#4CAF50"}
                        stroke="#FFF"
                        strokeWidth="3"
                        style={{ cursor: 'move', touchAction: 'none' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleInterpolationPointDrag(e.nativeEvent, connectionKeyLeft);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleInterpolationPointDrag(e.nativeEvent, connectionKeyLeft);
                        }}
                      />

                      <circle
                        cx={(controlPointRight || defaultRightControl).x}
                        cy={(controlPointRight || defaultRightControl).y}
                        r="16"
                        fill={controlPointRight ? "#FF6600" : "#4CAF50"}
                        stroke="#FFF"
                        strokeWidth="3"
                        style={{ cursor: 'move', touchAction: 'none' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleInterpolationPointDrag(e.nativeEvent, connectionKeyRight);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleInterpolationPointDrag(e.nativeEvent, connectionKeyRight);
                        }}
                      />

                      {(controlPointTop || controlPointBottom || controlPointLeft || controlPointRight) && isHovered && (
                        <g>
                          {controlPointTop && (
                            <g>
                              <circle cx={(controlPointTop || defaultTopControl).x + 15} cy={(controlPointTop || defaultTopControl).y - 15} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetInterpolationPoint(connectionKeyTop); }} />
                              <text x={(controlPointTop || defaultTopControl).x + 15} y={(controlPointTop || defaultTopControl).y - 15} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                            </g>
                          )}
                          {controlPointBottom && (
                            <g>
                              <circle cx={(controlPointBottom || defaultBottomControl).x + 15} cy={(controlPointBottom || defaultBottomControl).y + 15} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetInterpolationPoint(connectionKeyBottom); }} />
                              <text x={(controlPointBottom || defaultBottomControl).x + 15} y={(controlPointBottom || defaultBottomControl).y + 15} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                            </g>
                          )}
                          {controlPointLeft && (
                            <g>
                              <circle cx={(controlPointLeft || defaultLeftControl).x - 15} cy={(controlPointLeft || defaultLeftControl).y} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetInterpolationPoint(connectionKeyLeft); }} />
                              <text x={(controlPointLeft || defaultLeftControl).x - 15} y={(controlPointLeft || defaultLeftControl).y} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                            </g>
                          )}
                          {controlPointRight && (
                            <g>
                              <circle cx={(controlPointRight || defaultRightControl).x + 15} cy={(controlPointRight || defaultRightControl).y} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetInterpolationPoint(connectionKeyRight); }} />
                              <text x={(controlPointRight || defaultRightControl).x + 15} y={(controlPointRight || defaultRightControl).y} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                            </g>
                          )}
                        </g>
                      )}
                    </g>
                  );
                })}

                {/* Rysunki użytkownika */}
                {/* Renderowanie soczewek geologicznych */}
                {showLenses && (
                <g className="geological-lenses" style={{ userSelect: 'none' }}>
                  {lenses.map((lens) => (
                    <g
                      key={lens.id}
                      style={{ cursor: lensMoveMode ? 'move' : 'pointer' }}
                      onMouseDown={(e) => {
                        if (lensMoveMode) {
                          handleLensDragStart(e.nativeEvent, lens.id);
                        }
                      }}
                      onDoubleClick={(e) => handleLensDoubleClick(e, lens)}
                      onTouchStart={(e) => {
                        if (lensMoveMode) {
                          handleLensDragStart(e.nativeEvent, lens.id);
                        }
                      }}
                    >
                      {renderLens(lens)}

                      {/* Nazwa gruntu w środku soczewki */}
                      {(() => {
                        let centerX, centerY;

                        if (lens.shape === 'circle') {
                          centerX = lens.centerX;
                          centerY = lens.centerY;
                        } else if (lens.shape === 'rectangle') {
                          const rectWidth = Math.abs(lens.width);
                          const rectHeight = Math.abs(lens.height);
                          const rectX = lens.width < 0 ? lens.x + lens.width : lens.x;
                          const rectY = lens.height < 0 ? lens.y + lens.height : lens.y;
                          centerX = rectX + rectWidth / 2;
                          centerY = rectY + rectHeight / 2;
                        } else if (lens.shape === 'polygon' && lens.points && lens.points.length >= 3) {
                          // Środek ciężkości wielokąta
                          const sumX = lens.points.reduce((sum, p) => sum + p.x, 0);
                          const sumY = lens.points.reduce((sum, p) => sum + p.y, 0);
                          centerX = sumX / lens.points.length;
                          centerY = sumY / lens.points.length;
                        }

                        if (centerX !== undefined && centerY !== undefined && lens.name) {
                          return (
                            <text
                              x={centerX}
                              y={centerY}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="#000"
                              fontSize="14"
                              fontWeight="bold"
                              style={{ pointerEvents: 'none', userSelect: 'none' }}
                            >
                              {lens.name}
                            </text>
                          );
                        }
                        return null;
                      })()}

                      {/* Punkty kontrolne w trybie edycji */}
                      {lensEditMode && lens.shape === 'rectangle' && (() => {
                        const { x, y, width, height, id } = lens;
                        const rectWidth = Math.abs(width);
                        const rectHeight = Math.abs(height);
                        const rectX = width < 0 ? x + width : x;
                        const rectY = height < 0 ? y + height : y;

                        const defaultTop = { x: rectX + rectWidth / 2, y: rectY };
                        const defaultRight = { x: rectX + rectWidth, y: rectY + rectHeight / 2 };
                        const defaultBottom = { x: rectX + rectWidth / 2, y: rectY + rectHeight };
                        const defaultLeft = { x: rectX, y: rectY + rectHeight / 2 };

                        const cpTop = lensControlPoints[`${id}-top`] || defaultTop;
                        const cpRight = lensControlPoints[`${id}-right`] || defaultRight;
                        const cpBottom = lensControlPoints[`${id}-bottom`] || defaultBottom;
                        const cpLeft = lensControlPoints[`${id}-left`] || defaultLeft;

                        const topLeft = { x: rectX, y: rectY };
                        const topRight = { x: rectX + rectWidth, y: rectY };
                        const bottomRight = { x: rectX + rectWidth, y: rectY + rectHeight };
                        const bottomLeft = { x: rectX, y: rectY + rectHeight };

                        return (
                          <g>
                            {/* Linie pomocnicze */}
                            <line x1={topLeft.x} y1={topLeft.y} x2={cpTop.x} y2={cpTop.y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                            <line x1={cpTop.x} y1={cpTop.y} x2={topRight.x} y2={topRight.y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                            <line x1={topRight.x} y1={topRight.y} x2={cpRight.x} y2={cpRight.y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                            <line x1={cpRight.x} y1={cpRight.y} x2={bottomRight.x} y2={bottomRight.y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                            <line x1={bottomRight.x} y1={bottomRight.y} x2={cpBottom.x} y2={cpBottom.y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                            <line x1={cpBottom.x} y1={cpBottom.y} x2={bottomLeft.x} y2={bottomLeft.y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                            <line x1={bottomLeft.x} y1={bottomLeft.y} x2={cpLeft.x} y2={cpLeft.y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                            <line x1={cpLeft.x} y1={cpLeft.y} x2={topLeft.x} y2={topLeft.y} stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />

                            {/* Punkty kontrolne */}
                            <circle
                              cx={cpTop.x}
                              cy={cpTop.y}
                              r="12"
                              fill={lensControlPoints[`${id}-top`] ? "#FF6600" : "#4CAF50"}
                              stroke="#FFF"
                              strokeWidth="2"
                              style={{ cursor: 'move' }}
                              onMouseDown={(e) => handleLensControlMouseDown(e, id, 'top', defaultTop.x, defaultTop.y)}
                            />
                            <circle
                              cx={cpRight.x}
                              cy={cpRight.y}
                              r="12"
                              fill={lensControlPoints[`${id}-right`] ? "#FF6600" : "#4CAF50"}
                              stroke="#FFF"
                              strokeWidth="2"
                              style={{ cursor: 'move' }}
                              onMouseDown={(e) => handleLensControlMouseDown(e, id, 'right', defaultRight.x, defaultRight.y)}
                            />
                            <circle
                              cx={cpBottom.x}
                              cy={cpBottom.y}
                              r="12"
                              fill={lensControlPoints[`${id}-bottom`] ? "#FF6600" : "#4CAF50"}
                              stroke="#FFF"
                              strokeWidth="2"
                              style={{ cursor: 'move' }}
                              onMouseDown={(e) => handleLensControlMouseDown(e, id, 'bottom', defaultBottom.x, defaultBottom.y)}
                            />
                            <circle
                              cx={cpLeft.x}
                              cy={cpLeft.y}
                              r="12"
                              fill={lensControlPoints[`${id}-left`] ? "#FF6600" : "#4CAF50"}
                              stroke="#FFF"
                              strokeWidth="2"
                              style={{ cursor: 'move' }}
                              onMouseDown={(e) => handleLensControlMouseDown(e, id, 'left', defaultLeft.x, defaultLeft.y)}
                            />

                            {/* Przyciski reset */}
                            {lensControlPoints[`${id}-top`] && (
                              <g>
                                <circle cx={cpTop.x + 15} cy={cpTop.y - 15} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetLensControlPoint(id, 'top'); }} />
                                <text x={cpTop.x + 15} y={cpTop.y - 15} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            )}
                            {lensControlPoints[`${id}-right`] && (
                              <g>
                                <circle cx={cpRight.x + 15} cy={cpRight.y} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetLensControlPoint(id, 'right'); }} />
                                <text x={cpRight.x + 15} y={cpRight.y} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            )}
                            {lensControlPoints[`${id}-bottom`] && (
                              <g>
                                <circle cx={cpBottom.x + 15} cy={cpBottom.y + 15} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetLensControlPoint(id, 'bottom'); }} />
                                <text x={cpBottom.x + 15} y={cpBottom.y + 15} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            )}
                            {lensControlPoints[`${id}-left`] && (
                              <g>
                                <circle cx={cpLeft.x - 15} cy={cpLeft.y} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetLensControlPoint(id, 'left'); }} />
                                <text x={cpLeft.x - 15} y={cpLeft.y} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            )}
                          </g>
                        );
                      })()}

                      {/* Wierzchołki w trybie edycji */}
                      {lensEditMode && lens.shape === 'rectangle' && (() => {
                        const { x, y, width, height, id } = lens;
                        const rectWidth = Math.abs(width);
                        const rectHeight = Math.abs(height);
                        const rectX = width < 0 ? x + width : x;
                        const rectY = height < 0 ? y + height : y;

                        // Domyślne pozycje wierzchołków
                        const defaultTopLeft = { x: rectX, y: rectY };
                        const defaultTopRight = { x: rectX + rectWidth, y: rectY };
                        const defaultBottomRight = { x: rectX + rectWidth, y: rectY + rectHeight };
                        const defaultBottomLeft = { x: rectX, y: rectY + rectHeight };

                        // Pobierz niestandardowe wierzchołki lub użyj domyślnych
                        const vertices = lensVertices[id] || {};
                        const topLeft = vertices.topLeft || defaultTopLeft;
                        const topRight = vertices.topRight || defaultTopRight;
                        const bottomRight = vertices.bottomRight || defaultBottomRight;
                        const bottomLeft = vertices.bottomLeft || defaultBottomLeft;

                        return (
                          <g>
                            {/* Wierzchołki */}
                            <circle
                              cx={topLeft.x}
                              cy={topLeft.y}
                              r="8"
                              fill={vertices.topLeft ? "#9C27B0" : "#2196F3"}
                              stroke="#FFF"
                              strokeWidth="2"
                              style={{ cursor: 'move' }}
                              onMouseDown={(e) => handleLensVertexMouseDown(e, id, 'topLeft')}
                            />
                            <circle
                              cx={topRight.x}
                              cy={topRight.y}
                              r="8"
                              fill={vertices.topRight ? "#9C27B0" : "#2196F3"}
                              stroke="#FFF"
                              strokeWidth="2"
                              style={{ cursor: 'move' }}
                              onMouseDown={(e) => handleLensVertexMouseDown(e, id, 'topRight')}
                            />
                            <circle
                              cx={bottomRight.x}
                              cy={bottomRight.y}
                              r="8"
                              fill={vertices.bottomRight ? "#9C27B0" : "#2196F3"}
                              stroke="#FFF"
                              strokeWidth="2"
                              style={{ cursor: 'move' }}
                              onMouseDown={(e) => handleLensVertexMouseDown(e, id, 'bottomRight')}
                            />
                            <circle
                              cx={bottomLeft.x}
                              cy={bottomLeft.y}
                              r="8"
                              fill={vertices.bottomLeft ? "#9C27B0" : "#2196F3"}
                              stroke="#FFF"
                              strokeWidth="2"
                              style={{ cursor: 'move' }}
                              onMouseDown={(e) => handleLensVertexMouseDown(e, id, 'bottomLeft')}
                            />

                            {/* Przyciski reset dla wierzchołków */}
                            {vertices.topLeft && (
                              <g>
                                <circle cx={topLeft.x - 15} cy={topLeft.y - 15} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetLensVertex(id, 'topLeft'); }} />
                                <text x={topLeft.x - 15} y={topLeft.y - 15} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            )}
                            {vertices.topRight && (
                              <g>
                                <circle cx={topRight.x + 15} cy={topRight.y - 15} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetLensVertex(id, 'topRight'); }} />
                                <text x={topRight.x + 15} y={topRight.y - 15} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            )}
                            {vertices.bottomRight && (
                              <g>
                                <circle cx={bottomRight.x + 15} cy={bottomRight.y + 15} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetLensVertex(id, 'bottomRight'); }} />
                                <text x={bottomRight.x + 15} y={bottomRight.y + 15} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            )}
                            {vertices.bottomLeft && (
                              <g>
                                <circle cx={bottomLeft.x - 15} cy={bottomLeft.y + 15} r="10" fill="#FF0000" stroke="#FFF" strokeWidth="2" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); resetLensVertex(id, 'bottomLeft'); }} />
                                <text x={bottomLeft.x - 15} y={bottomLeft.y + 15} textAnchor="middle" dominantBaseline="middle" fill="#FFF" fontSize="12" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            )}
                          </g>
                        );
                      })()}

                      {/* Wierzchołki wielokątów w trybie edycji */}
                      {lensEditMode && lens.shape === 'polygon' && lens.points && lens.points.length >= 3 && (() => {
                        const { id } = lens;
                        const vertices = lensVertices[id] || {};

                        return (
                          <g>
                            {lens.points.map((point, idx) => {
                              const vertex = vertices[`vertex${idx}`] || point;
                              const isModified = vertices[`vertex${idx}`] !== undefined;

                              return (
                                <g key={idx}>
                                  {/* Wierzchołek */}
                                  <circle
                                    cx={vertex.x}
                                    cy={vertex.y}
                                    r="8"
                                    fill={isModified ? "#9C27B0" : "#2196F3"}
                                    stroke="#FFF"
                                    strokeWidth="2"
                                    style={{ cursor: 'move' }}
                                    onMouseDown={(e) => handleLensVertexMouseDown(e, id, `vertex${idx}`)}
                                  />
                                  {/* Przycisk reset */}
                                  {isModified && (
                                    <g>
                                      <circle
                                        cx={vertex.x + 15}
                                        cy={vertex.y - 15}
                                        r="10"
                                        fill="#FF0000"
                                        stroke="#FFF"
                                        strokeWidth="2"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => { e.stopPropagation(); resetLensVertex(id, `vertex${idx}`); }}
                                      />
                                      <text
                                        x={vertex.x + 15}
                                        y={vertex.y - 15}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fill="#FFF"
                                        fontSize="12"
                                        fontWeight="bold"
                                        style={{ pointerEvents: 'none' }}
                                      >
                                        ×
                                      </text>
                                    </g>
                                  )}
                                </g>
                              );
                            })}
                          </g>
                        );
                      })()}

                      {/* Punkty kontrolne krawędzi wielokątów w trybie edycji */}
                      {lensEditMode && lens.shape === 'polygon' && lens.points && lens.points.length >= 3 && (() => {
                        const { id } = lens;
                        const vertices = lensVertices[id] || {};

                        // Pobierz rzeczywiste pozycje wierzchołków
                        const actualPoints = lens.points.map((p, idx) => {
                          return vertices[`vertex${idx}`] || p;
                        });

                        const n = actualPoints.length;

                        return (
                          <g>
                            {actualPoints.map((startPoint, idx) => {
                              const endPoint = actualPoints[(idx + 1) % n];

                              // Domyślny punkt kontrolny (środek krawędzi)
                              const defaultControlX = (startPoint.x + endPoint.x) / 2;
                              const defaultControlY = (startPoint.y + endPoint.y) / 2;

                              const controlKey = `${id}-edge${idx}`;
                              const controlPoint = lensControlPoints[controlKey] || { x: defaultControlX, y: defaultControlY };
                              const isModified = lensControlPoints[controlKey] !== undefined;

                              return (
                                <g key={idx}>
                                  {/* Linie pomocnicze */}
                                  <line
                                    x1={startPoint.x}
                                    y1={startPoint.y}
                                    x2={controlPoint.x}
                                    y2={controlPoint.y}
                                    stroke="#999"
                                    strokeWidth="1"
                                    strokeDasharray="3,3"
                                    opacity="0.5"
                                  />
                                  <line
                                    x1={controlPoint.x}
                                    y1={controlPoint.y}
                                    x2={endPoint.x}
                                    y2={endPoint.y}
                                    stroke="#999"
                                    strokeWidth="1"
                                    strokeDasharray="3,3"
                                    opacity="0.5"
                                  />
                                  {/* Punkt kontrolny */}
                                  <circle
                                    cx={controlPoint.x}
                                    cy={controlPoint.y}
                                    r="12"
                                    fill={isModified ? "#FF6600" : "#4CAF50"}
                                    stroke="#FFF"
                                    strokeWidth="2"
                                    style={{ cursor: 'move' }}
                                    onMouseDown={(e) => handleLensControlMouseDown(e, id, `edge${idx}`, defaultControlX, defaultControlY)}
                                  />
                                  {/* Przycisk reset */}
                                  {isModified && (
                                    <g>
                                      <circle
                                        cx={controlPoint.x + 15}
                                        cy={controlPoint.y - 15}
                                        r="10"
                                        fill="#FF0000"
                                        stroke="#FFF"
                                        strokeWidth="2"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => { e.stopPropagation(); resetLensControlPoint(id, `edge${idx}`); }}
                                      />
                                      <text
                                        x={controlPoint.x + 15}
                                        y={controlPoint.y - 15}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fill="#FFF"
                                        fontSize="12"
                                        fontWeight="bold"
                                        style={{ pointerEvents: 'none' }}
                                      >
                                        ×
                                      </text>
                                    </g>
                                  )}
                                </g>
                              );
                            })}
                          </g>
                        );
                      })()}
                    </g>
                  ))}
                  {/* Podgląd rysowanej soczewki */}
                  {currentLens && (
                    <g opacity="0.5">
                      {currentLens.shape === 'circle' && currentLens.radius > 0 && (
                        <circle
                          cx={currentLens.centerX}
                          cy={currentLens.centerY}
                          r={currentLens.radius}
                          fill="none"
                          stroke="#FF6B00"
                          strokeWidth="3"
                          strokeDasharray="5,5"
                        />
                      )}
                      {currentLens.shape === 'rectangle' && (
                        <rect
                          x={currentLens.width < 0 ? currentLens.x + currentLens.width : currentLens.x}
                          y={currentLens.height < 0 ? currentLens.y + currentLens.height : currentLens.y}
                          width={Math.abs(currentLens.width)}
                          height={Math.abs(currentLens.height)}
                          fill="none"
                          stroke="#FF6B00"
                          strokeWidth="3"
                          strokeDasharray="5,5"
                        />
                      )}
                    </g>
                  )}
                  {/* Podgląd punktów wielokąta */}
                  {lensMode && lensShape === 'polygon' && lensPolygonPoints.length > 0 && (
                    <g>
                      {lensPolygonPoints.map((point, index) => (
                        <circle
                          key={index}
                          cx={point.x}
                          cy={point.y}
                          r="5"
                          fill="#FF6B00"
                          stroke="#000"
                          strokeWidth="2"
                        />
                      ))}
                      {lensPolygonPoints.length >= 2 && (
                        <polyline
                          points={lensPolygonPoints.map(p => `${p.x},${p.y}`).join(' ')}
                          fill="none"
                          stroke="#FF6B00"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                        />
                      )}
                    </g>
                  )}
                </g>
                )}

                <g className="user-drawings">
                  {drawings.map((drawing) => (
                    <g
                      key={drawing.id}
                      style={{ cursor: moveDrawingMode ? 'move' : 'default' }}
                      opacity={draggingDrawing && draggingDrawing.id === drawing.id ? 0.5 : 1}
                      filter={draggingDrawing && draggingDrawing.id === drawing.id ? 'url(#dragging-shadow)' : 'none'}
                      onMouseDown={(e) => {
                        if (moveDrawingMode) {
                          handleDrawingDragStart(e.nativeEvent, drawing.id);
                        }
                      }}
                      onTouchStart={(e) => {
                        if (moveDrawingMode) {
                          handleDrawingDragStart(e.nativeEvent, drawing.id);
                        }
                      }}
                    >
                      {renderDrawing(drawing)}
                    </g>
                  ))}
                  {currentDrawing && (
                    <g className="current-drawing" opacity="0.7">
                      {renderDrawing(currentDrawing)}
                    </g>
                  )}
                  {/* Podgląd pomiaru podczas ruchu myszy po pierwszym kliknięciu */}
                  {measurePreview && (
                    <g className="measure-preview" opacity="0.7">
                      {renderDrawing({
                        ...measurePreview,
                        type: 'measure',
                        color: drawingColor,
                        originalScale: scale,
                        originalHorizontalScale: horizontalScale
                      })}
                    </g>
                  )}
                  {/* Tymczasowe pomiary (widoczne tylko gdy tryb pomiaru jest aktywny) */}
                  {activeMeasurements.map((measurement) => (
                    <g key={measurement.id}>
                      {renderDrawing(measurement)}
                    </g>
                  ))}

                  {/* Gotowe freeDraw polygony - geometria już przycięta na poziomie danych */}
                  <g clipPath={clipToTerrain && terrainProfile && terrainProfile.length >= 2 ? "url(#terrain-clip)" : undefined}>
                  {/* Terrain-fill freeDraw wyłączony — patrz Interpolacja_2d.md sekcja „Wyłączenie terrain-fill" */}
                  {connectLayers && (() => {
                    const freeDrawConns = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points);
                    const sorted = [...freeDrawConns].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

                    // Oblicz SVG punkty dla każdego polygonu
                    const resolvedPolygons = sorted.map(mc => ({
                      mc,
                      pts: mc.points.map(p => resolvePointSvg(p))
                    }));

                    return (
                      <g>
                        {resolvedPolygons.map((poly) => {
                          const { mc, pts } = poly;
                          const svgPoints = pts.map(p => `${p.x},${p.y}`).join(' ');
                          const isSelected = selectedFreeDraw === mc.id;
                          const isMultiSelected = (selectedFreeDrawIds || []).includes(mc.id);
                          const isEditing = editingFreeDraw === mc.id;
                          const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
                          const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;

                          const isMoved = !!mc.originalPoints;
                          // Zablokowana warstwa parent dla sticky layer lock (Rysuj tylko w warstwie) — żółta ramka.
                          const isLockedAsParent = stickyLayerLock && lensInLayerParentId === mc.id;
                          // Sharpen: kontur ma być wyraźny, więc strokeOpacity = 1 zamiast dziedziczyć
                          // półprzezroczystość fill (0.3). Robimy fillOpacity + strokeOpacity osobno.
                          const baseAlpha = isSelected ? 0.5 : (isMultiSelected ? 0.5 : (isMoved ? 0.45 : 0.3));
                          const sharpenDefaultEdge = sharpenContours && !isEditing && !isSelected && !isMultiSelected && !isMoved && !isLockedAsParent;
                          const shapeProps = {
                            fill: outlineOnly ? "none" : (mc.color || '#CCCCCC'),
                            fillOpacity: baseAlpha,
                            strokeOpacity: sharpenDefaultEdge ? 1 : (isLockedAsParent ? 1 : baseAlpha),
                            stroke: isEditing ? '#9333EA' : (isSelected ? '#FF0000' : (isLockedAsParent ? '#EAB308' : (isMultiSelected ? '#2563EB' : (isMoved ? '#F59E0B' : toContourColor(mc.color || '#CCCCCC'))))),
                            strokeWidth: isEditing ? "2.5" : (isSelected ? "3" : (isLockedAsParent ? "3.5" : (isMultiSelected ? "2.5" : (isMoved ? "2" : (sharpenContours ? "2.5" : "1"))))),
                            strokeDasharray: isEditing ? "none" : (isSelected ? "6,3" : (isLockedAsParent ? "8,4" : (isMultiSelected ? "4,2" : (isMoved ? "5,3" : "none")))),
                            style: { cursor: movePolygonMode ? 'move' : ((freeDrawMode || lensInLayerMode || cutHoleMode || splitMode || polylineSplitMode) ? 'crosshair' : 'pointer'), pointerEvents: (freeDrawMode || lensInLayerMode || cutHoleMode || splitMode || polylineSplitMode || altKeyHeld) ? 'none' : 'auto' },
                            onMouseDown: movePolygonMode ? (e) => handleFreeDrawPolygonDragStart(e.nativeEvent, mc.id) : undefined,
                            onClick: (e) => {
                              if (movePolygonMode) return;
                              if (freeDrawMode || lensInLayerMode || cutHoleMode || splitMode) return;
                              e.stopPropagation();
                              // Sticky layer lock — klik na warstwie wybiera ją jako parent
                              // i włącza zwykły tryb ✏️ Rysowanie; klipowanie do parenta
                              // dzieje się w handleFreeDrawDoubleClick.
                              if (stickyLayerLock) {
                                setLensInLayerParentId(mc.id);
                                setFreeDrawMode(true);
                                setFreeDrawEditVerticesMode(false);
                                setFreeDrawExpandMode(false);
                                setLensInLayerMode(false);
                                setConnectLayers(true);
                                setSelectedFreeDraw(mc.id);
                                setFreeDrawPoints([]);
                                setFreeDrawPreview(null);
                                return;
                              }
                              if (splitPickMode) {
                                setSplittingConnId(mc.id);
                                setSplitMode(true);
                                setSplitLinePoints([]);
                                setSplitLinePreview(null);
                                setSplitPickMode(false);
                                return;
                              }
                              if (mergePolygonsMode) {
                                // Klik 1: wybiera pierwszy polygon. Klik 2: scala z drugim.
                                if (mergeFromConnId == null) {
                                  setMergeFromConnId(mc.id);
                                  setSelectedFreeDraw(mc.id);
                                } else if (mergeFromConnId === mc.id) {
                                  // Klik w ten sam — anulowanie wyboru
                                  setMergeFromConnId(null);
                                  setSelectedFreeDraw(null);
                                } else {
                                  const newId = mergeFreeDrawPolygons(mergeFromConnId, mc.id);
                                  setMergeFromConnId(null);
                                  setSelectedFreeDraw(newId);
                                }
                                return;
                              }
                              if (freeDrawExpandMode) {
                                if (editingFreeDraw !== null) {
                                  // Już jest wybrany polygon — klik (na własny lub inny) przesuwa punkty edytowanego
                                  const svgCoords = getSvgCoordinates(e.nativeEvent);
                                  expandFreeDrawBoundary(editingFreeDraw, svgCoords.x, svgCoords.y);
                                  return;
                                }
                                // Brak wybranego — wybierz ten polygon
                                setEditingFreeDraw(mc.id);
                                setSelectedFreeDraw(mc.id);
                              } else if (freeDrawEditVerticesMode) {
                                if (editingFreeDraw === mc.id) return;
                                if (editingFreeDraw !== null) {
                                  applyFreeDrawEditClipping(editingFreeDraw);
                                }
                                setEditingFreeDraw(mc.id);
                                setSelectedFreeDraw(mc.id);
                              } else {
                                // Ctrl/Cmd/Shift+klik → multi-select (toggle w grupie do wspólnego dragu)
                                if (e.ctrlKey || e.metaKey || e.shiftKey) {
                                  const cur = selectedFreeDrawIds || [];
                                  const next = cur.includes(mc.id)
                                    ? cur.filter(id => id !== mc.id)
                                    : [...cur, mc.id];
                                  setSelectedFreeDrawIds(next);
                                  // Główne zaznaczenie podążą za ostatnio dodanym; gdy ostatni usunięty → null
                                  if (next.length === 0) setSelectedFreeDraw(null);
                                  else if (!next.includes(selectedFreeDraw)) setSelectedFreeDraw(next[next.length - 1]);
                                  return;
                                }
                                // Zwykły klik — pojedyncze zaznaczenie, kasuje grupę
                                setSelectedFreeDraw(isSelected ? null : mc.id);
                                setSelectedFreeDrawIds(isSelected ? [] : [mc.id]);
                                if (isEditing) setEditingFreeDraw(null);
                              }
                            },
                            onDoubleClick: (e) => {
                              if (movePolygonMode) return;
                              if (freeDrawMode || freeDrawEditVerticesMode || freeDrawExpandMode) return;
                              e.stopPropagation();
                              setEditingFreeDraw(isEditing ? null : mc.id);
                              setSelectedFreeDraw(mc.id);
                            },
                            onContextMenu: (e) => {
                              if (freeDrawMode || splitMode) return;
                              e.preventDefault();
                              e.stopPropagation();
                              setContextMenuFreeDraw({ clientX: e.clientX, clientY: e.clientY, connId: mc.id });
                            },
                          };

                          // Buduj path — proste linie lub krzywe Q gdy jest punkt kontrolny.
                          // Dla extendsToTerrain (4-pt trapez najwyższej strefy) wypychamy górę do y=0;
                          // clipPath terenu nakładany niżej tnie polygon do krzywej NMT.
                          // Overlap 0.5px na górze/dole zamyka antialiasingowe szczeliny między strefami.
                          const fdControlPoints = mc.controlPoints || {};
                          const pixelsPerM = scale / 10;
                          const isExtendsTerrain = mc.extendsToTerrain && terrainProfile && terrainProfile.length >= 2 && pts.length === 4;
                          const isConvertedTrapez = mc.convertedFromAuto && pts.length === 4;
                          const FD_OVERLAP_PX = 2;
                          let pathPts;
                          if (isExtendsTerrain) {
                            // top → y=0, bottom przesunięty 0.5px niżej (overlap z sąsiadem poniżej).
                            pathPts = [
                              { ...pts[0], y: 0 },
                              { ...pts[1], y: 0 },
                              { ...pts[2], y: pts[2].y + FD_OVERLAP_PX },
                              { ...pts[3], y: pts[3].y + FD_OVERLAP_PX },
                            ];
                          } else if (isConvertedTrapez) {
                            // top o 0.5px wyżej, bottom o 0.5px niżej → 1px overlap z sąsiadami.
                            pathPts = [
                              { ...pts[0], y: pts[0].y - FD_OVERLAP_PX },
                              { ...pts[1], y: pts[1].y - FD_OVERLAP_PX },
                              { ...pts[2], y: pts[2].y + FD_OVERLAP_PX },
                              { ...pts[3], y: pts[3].y + FD_OVERLAP_PX },
                            ];
                          } else {
                            pathPts = pts;
                          }
                          let pathD = `M ${pathPts[0].x},${pathPts[0].y}`;
                          for (let i = 0; i < pathPts.length; i++) {
                            const next = pathPts[(i + 1) % pathPts.length];
                            const cp = fdControlPoints[`edge${i}`];
                            if (cp) {
                              const curr = pathPts[i];
                              const midX = (curr.x + next.x) / 2;
                              const midY = (curr.y + next.y) / 2;
                              const cpSvgX = midX + (cp.offsetDistanceM || 0) * horizontalScale;
                              const cpSvgY = midY + (cp.offsetDepthM || 0) * pixelsPerM;
                              pathD += ` Q ${cpSvgX},${cpSvgY} ${next.x},${next.y}`;
                            } else {
                              pathD += ` L ${next.x},${next.y}`;
                            }
                          }
                          pathD += ' Z';

                          // Dziury (mc.holes) — każda jako osobny subpath. Z fillRule="evenodd" tworzą wycięcia.
                          if (mc.holes && mc.holes.length > 0) {
                            for (const hole of mc.holes) {
                              const holePts = hole.map(p => resolvePointSvg(p));
                              if (holePts.length < 3) continue;
                              pathD += ` M ${holePts[0].x},${holePts[0].y}`;
                              for (let i = 1; i < holePts.length; i++) {
                                pathD += ` L ${holePts[i].x},${holePts[i].y}`;
                              }
                              pathD += ' Z';
                            }
                          }

                          // Oblicz clipPath na podstawie otworów snap-owanych — nie wychodź poza otwory
                          const boreholeXs = [...new Set(mc.points
                            .filter(p => p.boreholeId)
                            .map(p => {
                              const bh = boreholes.find(b => b.id === p.boreholeId);
                              return bh ? 100 + bh.distance * horizontalScale : null;
                            })
                            .filter(x => x !== null))];
                          const hasBoreholeClip = boreholeXs.length >= 2;
                          const clipMinX = hasBoreholeClip ? Math.min(...boreholeXs) : 0;
                          const clipMaxX = hasBoreholeClip ? Math.max(...boreholeXs) : 99999;
                          const fdClipId = `fd-bh-clip-${mc.id}`;

                          // Dla najwyższej strefy (extendsToTerrain) owijamy fill + szrafurę w clipPath terenu —
                          // wypchniety nad teren polygon jest tnięty do dokładnej krzywej NMT.
                          const fillPathsEl = (
                            <>
                              <path
                                d={pathD}
                                fillRule="evenodd"
                                {...shapeProps}
                                clipPath={hasBoreholeClip ? `url(#${fdClipId})` : undefined}
                              />
                              {!outlineOnly && mc.pattern && mc.pattern !== 'none' && (
                                <path
                                  d={pathD}
                                  fillRule="evenodd"
                                  fill={`url(#pattern-${mc.pattern})`}
                                  opacity={isSelected ? "0.7" : "0.5"}
                                  stroke="none"
                                  style={{ pointerEvents: 'none' }}
                                  clipPath={hasBoreholeClip ? `url(#${fdClipId})` : undefined}
                                />
                              )}
                            </>
                          );

                          return (
                            <g key={`fd-${mc.id}`}>
                              {hasBoreholeClip && (
                                <defs>
                                  <clipPath id={fdClipId}>
                                    <rect x={clipMinX} y={-10000} width={clipMaxX - clipMinX} height={20000} />
                                  </clipPath>
                                </defs>
                              )}
                              {isExtendsTerrain ? (
                                <g clipPath="url(#terrain-clip)">{fillPathsEl}</g>
                              ) : fillPathsEl}
                              {/* Tryb "Tylko kontury" — edycja krawędzi: drag całej krawędzi + punkt kontrolny Béziera */}
                              {outlineOnly && !freeDrawMode && !freeDrawEditVerticesMode && !freeDrawExpandMode && pts.map((p, i) => {
                                const next = pts[(i + 1) % pts.length];
                                const cp = fdControlPoints[`edge${i}`];
                                const defaultX = (p.x + next.x) / 2;
                                const defaultY = (p.y + next.y) / 2;
                                const cpX = cp ? defaultX + (cp.offsetDistanceM || 0) * horizontalScale : defaultX;
                                const cpY = cp ? defaultY + (cp.offsetDepthM || 0) * pixelsPerM : defaultY;
                                const isMoved = !!cp;
                                return (
                                  <g key={`fd-outline-edge-${mc.id}-${i}`}>
                                    {/* Niewidoczna szeroka linia do łapania krawędzi — drag równoległy */}
                                    <line
                                      x1={p.x} y1={p.y} x2={next.x} y2={next.y}
                                      stroke="transparent" strokeWidth="40"
                                      style={{ cursor: 'move' }}
                                      onMouseEnter={(e) => e.target.setAttribute('stroke', 'rgba(59,130,246,0.4)')}
                                      onMouseLeave={(e) => e.target.setAttribute('stroke', 'transparent')}
                                      onMouseDown={(e) => handleFreeDrawEdgeDragStart(e.nativeEvent, mc.id, i)}
                                      onTouchStart={(e) => { e.preventDefault(); handleFreeDrawEdgeDragStart(e.nativeEvent, mc.id, i); }}
                                    />
                                    {/* Punkt kontrolny Béziera — wyginanie krawędzi */}
                                    <circle
                                      cx={cpX} cy={cpY} r={5}
                                      fill={isMoved ? '#FF6600' : '#3B82F6'}
                                      stroke="#fff" strokeWidth="1.5"
                                      style={{ cursor: 'grab' }}
                                      onMouseDown={(e) => handleFreeDrawControlDragStart(e.nativeEvent, mc.id, i)}
                                      onTouchStart={(e) => { e.preventDefault(); handleFreeDrawControlDragStart(e.nativeEvent, mc.id, i); }}
                                    />
                                    {/* Reset punktu kontrolnego */}
                                    {isMoved && (
                                      <g style={{ cursor: 'pointer' }}
                                         onClick={(e) => { e.stopPropagation(); resetFreeDrawControlPoint(mc.id, i); }}>
                                        <circle cx={cpX + 12} cy={cpY - 12} r={7} fill="#FF0000" opacity="0.8" />
                                        <text x={cpX + 12} y={cpY - 8} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                                      </g>
                                    )}
                                  </g>
                                );
                              })}
                              {/* Krawędzie w trybie rozszerzania granic — podświetlone tealem */}
                              {isEditing && freeDrawExpandMode && pts.map((p, i) => {
                                const next = pts[(i + 1) % pts.length];
                                return (
                                  <line
                                    key={`fdx-${mc.id}-${i}`}
                                    x1={p.x} y1={p.y} x2={next.x} y2={next.y}
                                    stroke="#0D9488" strokeWidth="2" strokeDasharray="6,3" opacity="0.6"
                                    style={{ pointerEvents: 'none' }}
                                  />
                                );
                              })}
                              {/* Klikalne krawędzie w trybie edycji wierzchołków — dodaje punkt */}
                              {isEditing && freeDrawEditVerticesMode && pts.map((p, i) => {
                                const next = pts[(i + 1) % pts.length];
                                return (
                                  <g key={`fde-${mc.id}-${i}`}>
                                    {/* Widoczna krawędź — przerywana, podświetla się na hover */}
                                    <line
                                      x1={p.x} y1={p.y} x2={next.x} y2={next.y}
                                      stroke="#9333EA" strokeWidth="2" strokeDasharray="4,4" opacity="0.4"
                                      style={{ pointerEvents: 'none' }}
                                    />
                                    {/* Niewidoczna szeroka linia do klikania */}
                                    <line
                                      x1={p.x} y1={p.y} x2={next.x} y2={next.y}
                                      stroke="transparent" strokeWidth="16"
                                      style={{ cursor: 'copy' }}
                                      onMouseEnter={(e) => e.target.setAttribute('stroke', 'rgba(147,51,234,0.3)')}
                                      onMouseLeave={(e) => e.target.setAttribute('stroke', 'transparent')}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const svgCoords = getSvgCoordinates(e.nativeEvent);
                                        addFreeDrawVertex(mc.id, i, svgCoords.x, svgCoords.y);
                                      }}
                                    />
                                  </g>
                                );
                              })}
                              {/* Wierzchołki w trybie edycji */}
                              {isEditing && pts.map((p, i) => (
                                <circle
                                  key={`fdv-${mc.id}-${i}`}
                                  cx={p.x} cy={p.y} r={6}
                                  fill="#9333EA" stroke="#fff" strokeWidth="2"
                                  style={{ cursor: 'grab' }}
                                  onMouseDown={(e) => handleFreeDrawPointDragStart(e.nativeEvent, mc.id, i)}
                                  onTouchStart={(e) => { e.preventDefault(); handleFreeDrawPointDragStart(e.nativeEvent, mc.id, i); }}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (mc.points.length <= 3) {
                                      alert('Polygon musi mieć minimum 3 wierzchołki — nie można usunąć więcej.');
                                      return;
                                    }
                                    removeFreeDrawVertex(mc.id, i);
                                  }}
                                >
                                  <title>Lewy przycisk: przeciągnij. Prawy przycisk: usuń wierzchołek.</title>
                                </circle>
                              ))}
                              {/* Punkty kontrolne krawędzi w trybie edycji */}
                              {isEditing && freeDrawEditVerticesMode && pts.map((p, i) => {
                                const next = pts[(i + 1) % pts.length];
                                const cp = fdControlPoints[`edge${i}`];
                                const defaultX = (p.x + next.x) / 2;
                                const defaultY = (p.y + next.y) / 2;
                                const cpX = cp ? defaultX + (cp.offsetDistanceM || 0) * horizontalScale : defaultX;
                                const cpY = cp ? defaultY + (cp.offsetDepthM || 0) * pixelsPerM : defaultY;
                                const isMoved = !!cp;

                                return (
                                  <g key={`fdc-${mc.id}-${i}`}>
                                    {/* Linia pomocnicza od wierzchołków do punktu kontrolnego */}
                                    {isMoved && (
                                      <g>
                                        <line x1={p.x} y1={p.y} x2={cpX} y2={cpY}
                                          stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                                        <line x1={next.x} y1={next.y} x2={cpX} y2={cpY}
                                          stroke="#999" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                                      </g>
                                    )}
                                    {/* Punkt kontrolny — zielony (domyślny) / pomarańczowy (przesunięty) */}
                                    <circle
                                      cx={cpX} cy={cpY} r={5}
                                      fill={isMoved ? '#FF6600' : '#4CAF50'}
                                      stroke="#fff" strokeWidth="1.5"
                                      style={{ cursor: 'grab' }}
                                      onMouseDown={(e) => handleFreeDrawControlDragStart(e.nativeEvent, mc.id, i)}
                                    />
                                    {/* Reset — czerwony × */}
                                    {isMoved && (
                                      <g style={{ cursor: 'pointer' }}
                                         onClick={(e) => { e.stopPropagation(); resetFreeDrawControlPoint(mc.id, i); }}>
                                        <circle cx={cpX + 12} cy={cpY - 12} r={7} fill="#FF0000" opacity="0.8" />
                                        <text x={cpX + 12} y={cpY - 8} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold" style={{ pointerEvents: 'none' }}>×</text>
                                      </g>
                                    )}
                                  </g>
                                );
                              })}
                              {isSelected && !isEditing && (
                                <g style={{ cursor: 'pointer' }}
                                   onClick={(e) => { e.stopPropagation(); deleteFreeDraw(mc.id); }}>
                                  <circle cx={cx} cy={cy} r={12} fill="#FF0000" opacity="0.9" />
                                  <text x={cx} y={cy + 5} textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold" pointerEvents="none">×</text>
                                </g>
                              )}
                            </g>
                          );
                        })}
                      </g>
                    );
                  })()}

                  {/* Etykiety poligonów freeDraw — globalny toggle + per-polygon override (mc.showLabel) */}
                  {connectLayers && (() => {
                    const freeDrawConns = manualConnections.filter(mc =>
                      mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3 && mc.name
                    );
                    const labeled = freeDrawConns.filter(mc => mc.showLabel !== undefined ? mc.showLabel : showPolygonLabels);
                    if (labeled.length === 0) return null;

                    // Pole of inaccessibility (uproszczony polylabel) — punkt wewnątrz poligonu
                    // najdalszy od dowolnej krawędzi. Daje wizualnie najlepszy "środek" nawet
                    // dla wklęsłych kształtów (gdzie centroid masy może wypaść poza obrys).
                    const labelPos = (pts) => {
                      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                      for (const p of pts) {
                        if (p.x < minX) minX = p.x;
                        if (p.x > maxX) maxX = p.x;
                        if (p.y < minY) minY = p.y;
                        if (p.y > maxY) maxY = p.y;
                      }
                      const w = maxX - minX, h = maxY - minY;
                      if (w <= 0 || h <= 0) return { x: minX, y: minY };

                      const isInside = (x, y) => {
                        let inside = false;
                        for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
                          const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
                          if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inside = !inside;
                        }
                        return inside;
                      };

                      const distToEdge = (x, y) => {
                        let minD = Infinity;
                        for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
                          const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
                          const dx = xj - xi, dy = yj - yi;
                          const len2 = dx * dx + dy * dy;
                          let t = len2 > 0 ? ((x - xi) * dx + (y - yi) * dy) / len2 : 0;
                          if (t < 0) t = 0; else if (t > 1) t = 1;
                          const px = xi + t * dx, py = yi + t * dy;
                          const d = Math.hypot(x - px, y - py);
                          if (d < minD) minD = d;
                        }
                        return minD;
                      };

                      // Grid search 40x40 — dla ~20 wierzchołków: ~32k ops/poligon, akceptowalne
                      const N = 40;
                      const sx = w / N, sy = h / N;
                      let best = { x: (minX + maxX) / 2, y: (minY + maxY) / 2, d: -Infinity };
                      for (let i = 0; i < N; i++) {
                        for (let k = 0; k < N; k++) {
                          const x = minX + (i + 0.5) * sx;
                          const y = minY + (k + 0.5) * sy;
                          if (!isInside(x, y)) continue;
                          const d = distToEdge(x, y);
                          if (d > best.d) best = { x, y, d };
                        }
                      }
                      return { x: best.x, y: best.y };
                    };

                    const pixelsPerM = scale / 10;
                    return (
                      <g>
                        {labeled.map(mc => {
                          let x, y;
                          if (mc.labelPos) {
                            // Ręcznie ustawiona pozycja (po dragu)
                            x = 100 + (mc.labelPos.distanceM || 0) * horizontalScale;
                            y = 80 + (mc.labelPos.depthM || 0) * pixelsPerM;
                          } else {
                            const pts = mc.points.map(p => resolvePointSvg(p));
                            const pos = labelPos(pts);
                            x = pos.x;
                            y = pos.y;
                          }
                          return (
                            <text key={`fdlbl-${mc.id}`}
                              x={x} y={y}
                              textAnchor="middle" dominantBaseline="central"
                              fontSize="14" fontWeight="bold" fill="#000"
                              stroke="#fff" strokeWidth="3" paintOrder="stroke"
                              onMouseDown={(e) => handlePolygonLabelDragStart(e.nativeEvent, mc.id, x, y)}
                              onDoubleClick={(e) => { e.stopPropagation(); if (mc.labelPos) resetPolygonLabelPos(mc.id); }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setLabelContextMenu({ mcId: mc.id, clientX: e.clientX, clientY: e.clientY });
                              }}
                              style={{ cursor: 'move', userSelect: 'none' }}
                            >
                              <title>LPM + drag = przesuń. 2× klik = reset do auto. PPM = menu.</title>
                              {mc.name}
                            </text>
                          );
                        })}
                      </g>
                    );
                  })()}
                  </g>

                  {/* Wizualizacja snap — żółte kółka na wierzchołkach + żółte krawędzie */}
                  {freeDrawSnapVertices && (freeDrawEditVerticesMode || freeDrawMode) && connectLayers && (() => {
                    const freeDrawConns = manualConnections.filter(mc =>
                      mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3 &&
                      mc.id !== editingFreeDraw
                    );
                    return freeDrawConns.map(mc => {
                      const pts = mc.points.map(pt => resolvePointSvg(pt));
                      return (
                        <g key={`snap-vis-${mc.id}`} data-print-overlay="true" style={{ pointerEvents: 'none' }}>
                          {/* Krawędzie — żółte przerywane */}
                          {pts.map((p, i) => {
                            const next = pts[(i + 1) % pts.length];
                            return (
                              <line key={`snap-edge-${mc.id}-${i}`}
                                x1={p.x} y1={p.y} x2={next.x} y2={next.y}
                                stroke="#EAB308" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.4" />
                            );
                          })}
                          {/* Wierzchołki — żółte kółka */}
                          {pts.map((p, i) => (
                            <circle key={`snap-vert-${mc.id}-${i}`}
                              cx={p.x} cy={p.y} r={5}
                              fill="none" stroke="#EAB308" strokeWidth="2" opacity="0.6" />
                          ))}
                        </g>
                      );
                    });
                  })()}

                  {/* Podgląd rysowania ręcznego - krzywa łamana */}
                  {(freeDrawMode || lensInLayerMode || cutHoleMode) && (
                    <g data-print-overlay="true">
                      {/* Linie między narysowanymi punktami */}
                      {freeDrawPoints.length >= 2 && freeDrawPoints.map((p, i) => {
                        if (i === 0) return null;
                        const prev = freeDrawPoints[i - 1];
                        return (
                          <line key={`fdl-${i}`}
                                x1={prev.x} y1={prev.y} x2={p.x} y2={p.y}
                                stroke="#9333EA" strokeWidth="2" />
                        );
                      })}
                      {/* Narysowane punkty */}
                      {freeDrawPoints.map((p, i) => (
                        <circle key={`fdp-${i}`} cx={p.x} cy={p.y} r={5}
                                fill={p.boreholeId ? '#9333EA' : '#D946EF'}
                                stroke="#fff" strokeWidth="1.5" />
                      ))}
                      {/* Podgląd snap point / wolnego punktu pod kursorem.
                          Priorytet: otwór (X+Y na osi/krawędzi) → wierzchołek polygonu → krawędź polygonu → wolny punkt. */}
                      {freeDrawPreview && (() => {
                        const boreholeSnap = snapToLayerBoundary(freeDrawPreview.x, freeDrawPreview.y);
                        const polygonSnap = !boreholeSnap ? snapToFreeDrawVertex(freeDrawPreview.x, freeDrawPreview.y, editingFreeDraw) : null;
                        const previewPt = boreholeSnap || polygonSnap || { x: freeDrawPreview.x, y: freeDrawPreview.y };
                        const isLayerEdge = boreholeSnap && boreholeSnap.edge;
                        const onBorehole = boreholeSnap && !boreholeSnap.edge;
                        const isPolyVertex = polygonSnap && polygonSnap.snappedVertex;
                        const isPolyEdge = polygonSnap && polygonSnap.snappedEdge;
                        return (
                          <g>
                            {isLayerEdge && (
                              <g>
                                <circle cx={boreholeSnap.x} cy={boreholeSnap.y} r={9} fill="none" stroke="#10B981" strokeWidth="2.5" opacity={0.9} />
                                <line x1={boreholeSnap.x - 12} y1={boreholeSnap.y} x2={boreholeSnap.x + 12} y2={boreholeSnap.y} stroke="#10B981" strokeWidth="1.5" opacity={0.8} />
                                <line x1={boreholeSnap.x} y1={boreholeSnap.y - 12} x2={boreholeSnap.x} y2={boreholeSnap.y + 12} stroke="#10B981" strokeWidth="1.5" opacity={0.8} />
                                <circle cx={boreholeSnap.x} cy={boreholeSnap.y} r={3} fill="#10B981" />
                              </g>
                            )}
                            {onBorehole && (
                              <g>
                                <circle cx={boreholeSnap.x} cy={boreholeSnap.y} r={7} fill="none" stroke="#9333EA" strokeWidth="2" opacity={0.7} />
                                <circle cx={boreholeSnap.x} cy={boreholeSnap.y} r={3} fill="#9333EA" opacity={0.7} />
                              </g>
                            )}
                            {isPolyVertex && (
                              // Wierzchołek polygonu: kwadracik pomarańczowy
                              <g>
                                <rect x={polygonSnap.x - 7} y={polygonSnap.y - 7} width={14} height={14}
                                      fill="none" stroke="#F59E0B" strokeWidth="2.5" opacity={0.9} />
                                <circle cx={polygonSnap.x} cy={polygonSnap.y} r={3} fill="#F59E0B" />
                              </g>
                            )}
                            {isPolyEdge && (
                              // Krawędź polygonu: romb żółty
                              <g>
                                <polygon points={`${polygonSnap.x},${polygonSnap.y - 9} ${polygonSnap.x + 9},${polygonSnap.y} ${polygonSnap.x},${polygonSnap.y + 9} ${polygonSnap.x - 9},${polygonSnap.y}`}
                                         fill="none" stroke="#EAB308" strokeWidth="2.5" opacity={0.9} />
                                <circle cx={polygonSnap.x} cy={polygonSnap.y} r={3} fill="#EAB308" />
                              </g>
                            )}
                            {!boreholeSnap && !polygonSnap && (
                              <circle cx={freeDrawPreview.x} cy={freeDrawPreview.y} r={5}
                                      fill="none" stroke="#D946EF" strokeWidth="2" strokeDasharray="3,3" opacity={0.6} />
                            )}
                            {/* Linia od ostatniego punktu do podglądu */}
                            {freeDrawPoints.length > 0 && (
                              <line
                                x1={freeDrawPoints[freeDrawPoints.length - 1].x}
                                y1={freeDrawPoints[freeDrawPoints.length - 1].y}
                                x2={previewPt.x} y2={previewPt.y}
                                stroke="#9333EA" strokeWidth="2" strokeDasharray="5,5" opacity={0.5}
                              />
                            )}
                          </g>
                        );
                      })()}
                      {/* Info */}
                      {freeDrawPreview && (() => {
                        const boreholeSnap = snapToLayerBoundary(freeDrawPreview.x, freeDrawPreview.y);
                        const polygonSnap = !boreholeSnap ? snapToFreeDrawVertex(freeDrawPreview.x, freeDrawPreview.y, editingFreeDraw) : null;
                        let label, color;
                        if (boreholeSnap?.edge) { label = 'Snap: krawędź warstwy'; color = '#10B981'; }
                        else if (boreholeSnap) { label = 'Snap: wzdłuż otworu'; color = '#9333EA'; }
                        else if (polygonSnap?.snappedVertex) { label = 'Snap: wierzchołek polygonu'; color = '#F59E0B'; }
                        else if (polygonSnap?.snappedEdge) { label = 'Snap: krawędź polygonu'; color = '#EAB308'; }
                        else if (freeDrawPoints.length === 0) { label = 'Kliknij (dowolny punkt)'; color = '#9333EA'; }
                        else if (freeDrawPoints.length === 1) { label = 'Kliknij kolejny punkt'; color = '#9333EA'; }
                        else { label = '2×klik = zamknij kształt'; color = '#9333EA'; }
                        return (
                          <text x={freeDrawPreview.x + 15} y={freeDrawPreview.y - 10}
                                fontSize="11" fill={color} fontWeight="bold" pointerEvents="none">
                            {label}
                          </text>
                        );
                      })()}
                      {/* Pomiar na żywo — Δx, Δy, długość, kąt od ostatniego punktu */}
                      {showLiveMeasure && freeDrawPreview && freeDrawPoints.length > 0 && (() => {
                        const last = freeDrawPoints[freeDrawPoints.length - 1];
                        const ppm = scale / 10;
                        const dxM = (freeDrawPreview.x - last.x) / horizontalScale;
                        const dyM = (freeDrawPreview.y - last.y) / ppm;
                        const len = Math.sqrt(dxM * dxM + dyM * dyM);
                        const angleDeg = Math.atan2(dyM, dxM) * 180 / Math.PI;
                        const lines = [
                          `L: ${len.toFixed(2)} m`,
                          `α: ${angleDeg.toFixed(1)}°`,
                          `Δx: ${dxM.toFixed(2)} m`,
                          `Δy: ${dyM.toFixed(2)} m`,
                        ];
                        const boxX = freeDrawPreview.x + 18;
                        const boxY = freeDrawPreview.y + 8;
                        const boxW = 120;
                        const boxH = 14 * lines.length + 6;
                        return (
                          <g pointerEvents="none">
                            <rect x={boxX} y={boxY} width={boxW} height={boxH}
                                  fill="white" stroke="#3B82F6" strokeWidth="1" opacity={0.95} rx={3} />
                            {lines.map((t, i) => (
                              <text key={`live-m-${i}`} x={boxX + 6} y={boxY + 14 + i * 14}
                                    fontSize="11" fill="#1F2937" fontFamily="monospace">{t}</text>
                            ))}
                          </g>
                        );
                      })()}
                    </g>
                  )}

                  {/* Prostokąt wydruku — wymiary obliczone z realnych wymiarów papieru × skala wydruku.
                      Można go tylko przesuwać; rozmiar zmienia się dynamicznie wraz ze skalami / orientacją / formatem. */}
                  {printSelectMode && printSelectRect && (() => {
                    const ppm = scale / 10;
                    const rect = printSelectRect;
                    if (rect.w === 0 || rect.h === 0) return null;
                    const widthM = rect.w / horizontalScale;
                    const heightM = rect.h / ppm;
                    const fmtLabel = printSelectFormat.toUpperCase();
                    const orientLabel = printOrientation === 'landscape' ? 'poziomo' : 'pionowo';
                    const startMove = (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const { x, y } = getSvgCoordinates(e.nativeEvent);
                      setPrintDrag({ mode: 'move', startSvg: { x, y }, origRect: { ...rect } });
                    };
                    return (
                      <g className="print-overlay" data-print-overlay="true">
                        <rect x={rect.x} y={rect.y} width={rect.w} height={rect.h}
                              fill="rgba(34,197,94,0.10)" stroke="#22C55E" strokeWidth="2"
                              strokeDasharray="6,3"
                              style={{ cursor: 'move' }}
                              onMouseDown={startMove} />
                        <text x={rect.x + 6} y={rect.y - 6}
                              fontSize="12" fontWeight="bold" fill="#15803D"
                              fontFamily="monospace" pointerEvents="none">
                          {fmtLabel} {orientLabel} · 1:{horizontalScaleToN(horizontalScale)}H / 1:{verticalScaleToN(scale)}V · {widthM.toFixed(1)}m × {heightM.toFixed(1)}m
                        </text>
                      </g>
                    );
                  })()}

                  {/* Podgląd linii podziału (split mode) */}
                  {splitMode && (
                    <g data-print-overlay="true" pointerEvents="none">
                      {splitLinePoints.map((p, i) => (
                        <circle key={`split-pt-${i}`} cx={p.x} cy={p.y} r={5} fill="#DC2626" stroke="#fff" strokeWidth="2" />
                      ))}
                      {splitLinePoints.length === 1 && splitLinePreview && (
                        <line
                          x1={splitLinePoints[0].x} y1={splitLinePoints[0].y}
                          x2={splitLinePreview.x} y2={splitLinePreview.y}
                          stroke="#DC2626" strokeWidth="2" strokeDasharray="6,4" opacity="0.8"
                        />
                      )}
                      {splitLinePreview && splitLinePoints.length === 0 && (
                        <circle cx={splitLinePreview.x} cy={splitLinePreview.y} r={6}
                                fill="none" stroke="#DC2626" strokeWidth="2" strokeDasharray="3,3" opacity="0.6" />
                      )}
                      {splitLinePreview && (
                        <text x={splitLinePreview.x + 12} y={splitLinePreview.y - 10}
                              fontSize="11" fill="#DC2626" fontWeight="bold">
                          {splitLinePoints.length === 0 ? 'Klik: początek linii podziału' : 'Klik: koniec linii (ESC anuluje)'}
                        </text>
                      )}
                    </g>
                  )}

                  {/* Podgląd polilinii tnącej wiele warstw */}
                  {polylineSplitMode && (
                    <g data-print-overlay="true" pointerEvents="none">
                      {polylineSplitPoints.length >= 2 && (
                        <polyline
                          points={polylineSplitPoints.map(p => `${p.x},${p.y}`).join(' ')}
                          fill="none" stroke="#DC2626" strokeWidth="2"
                        />
                      )}
                      {polylineSplitPoints.length >= 1 && polylineSplitPreview && (
                        <line
                          x1={polylineSplitPoints[polylineSplitPoints.length - 1].x}
                          y1={polylineSplitPoints[polylineSplitPoints.length - 1].y}
                          x2={polylineSplitPreview.x} y2={polylineSplitPreview.y}
                          stroke="#DC2626" strokeWidth="2" strokeDasharray="6,4" opacity="0.8"
                        />
                      )}
                      {polylineSplitPoints.map((p, i) => (
                        <circle key={`pl-split-pt-${i}`} cx={p.x} cy={p.y} r={5}
                                fill="#DC2626" stroke="#fff" strokeWidth="2" />
                      ))}
                      {polylineSplitPreview && polylineSplitPoints.length === 0 && (
                        <circle cx={polylineSplitPreview.x} cy={polylineSplitPreview.y} r={6}
                                fill="none" stroke="#DC2626" strokeWidth="2" strokeDasharray="3,3" opacity="0.6" />
                      )}
                      {polylineSplitPreview && (
                        <text x={polylineSplitPreview.x + 12} y={polylineSplitPreview.y - 10}
                              fontSize="11" fill="#DC2626" fontWeight="bold">
                          {polylineSplitPoints.length === 0
                            ? 'Klik: pierwszy punkt polilinii'
                            : polylineSplitPoints.length === 1
                              ? 'Klik: kolejny punkt (dbl-klik kończy)'
                              : `Klik: kolejny / dbl-klik: tnij ${polylineSplitPoints.length} pkt (ESC anuluje)`}
                        </text>
                      )}
                    </g>
                  )}

                </g>
              </svg>
            </div>

            {polylineSplitStatus && (() => {
              const { cut, skipped, autoConnectSkipped } = polylineSplitStatus;
              const total = cut + skipped + autoConnectSkipped;
              const ok = cut > 0;
              return (
                <div
                  className={`fixed top-20 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-lg shadow-lg text-sm font-medium border ${
                    ok ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-amber-50 border-amber-300 text-amber-800'
                  }`}
                  onClick={() => setPolylineSplitStatus(null)}
                  style={{ cursor: 'pointer' }}
                  title="Kliknij, aby zamknąć"
                >
                  {ok ? '✂️' : '⚠️'} Pocięto <b>{cut}</b> poligonów
                  {skipped > 0 && <>, pominięto <b>{skipped}</b> (polilinia minęła / cięcie zbyt płytkie)</>}
                  {autoConnectSkipped > 0 && <>, <b>{autoConnectSkipped}</b> auto-connect (najpierw 🔄 Konwertuj warstwy)</>}
                  {!ok && total === 0 && <> — żaden poligon nie pasował</>}
                </div>
              );
            })()}

            {/* Pływający panel akcji w trybie wyboru obszaru wydruku.
                Skale 1:N pochodzą z dolnych pól "Skala pionowa/pozioma" — tu jest tylko orientacja papieru. */}
            {printSelectMode && printSelectRect && !showPrintDialog && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[55] bg-white border border-brand-700 rounded-lg shadow-lg px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 max-w-[calc(100vw-2rem)]">
                <span className="text-sm font-medium text-gray-700">
                  📄 Wydruk <span className="font-bold">{printSelectFormat.toUpperCase()}</span>
                </span>
                <div className="flex items-center gap-1 text-sm text-gray-700">
                  <span>Orientacja:</span>
                  <button
                    onClick={() => setPrintOrientation('landscape')}
                    className={`px-2 py-1 rounded text-xs ${printOrientation === 'landscape' ? 'bg-brand-700 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                  >Pozioma</button>
                  <button
                    onClick={() => setPrintOrientation('portrait')}
                    className={`px-2 py-1 rounded text-xs ${printOrientation === 'portrait' ? 'bg-brand-700 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                  >Pionowa</button>
                </div>
                <span className="text-xs text-gray-500">
                  Skale 1:{verticalScaleToN(scale)} V / 1:{horizontalScaleToN(horizontalScale)} H — zmień w dolnym panelu
                </span>
                <button
                  onClick={() => setShowPrintDialog(true)}
                  className="px-3 py-1.5 rounded bg-brand-700 text-white hover:bg-brand-800 text-sm font-medium"
                >
                  Eksportuj PDF
                </button>
                <button
                  onClick={() => {
                    setPrintSelectMode(false);
                    setPrintSelectRect(null);
                    setPrintDrag(null);
                  }}
                  className="px-3 py-1.5 rounded text-sm text-gray-600 hover:bg-gray-100"
                >
                  Anuluj
                </button>
              </div>
            )}

            {/* Dialog potwierdzenia eksportu wycinka do PDF (A3/A4) */}
            {showPrintDialog && printSelectRect && (() => {
              const ppm = scale / 10;
              const widthM = printSelectRect.w / horizontalScale;
              const heightM = printSelectRect.h / ppm;
              const fmt = printSelectFormat;
              const fmtLabel = fmt.toUpperCase();
              const orientLabel = printOrientation === 'landscape' ? 'poziomo' : 'pionowo';
              const printScaleH_N = horizontalScaleToN(horizontalScale);
              const printScaleV_N = verticalScaleToN(scale);
              const handleExport = async () => {
                try {
                  await exportToPDF({
                    pageSize: fmt,
                    orientation: printOrientation,
                    filename: `przekroj_${fmt}_1-${printScaleH_N}H_1-${printScaleV_N}V_${new Date().toISOString().slice(0,10)}.pdf`,
                    customViewBox: {
                      x: printSelectRect.x,
                      y: printSelectRect.y,
                      width: printSelectRect.w,
                      height: printSelectRect.h,
                    },
                    margin: PRINT_MARGIN_MM,
                  });
                  setShowPrintDialog(false);
                  setPrintSelectMode(false);
                  setPrintSelectRect(null);
                } catch (err) {
                  alert('Błąd eksportu PDF: ' + (err?.message || err));
                }
              };
              return (
                <div className="fixed inset-0 z-[60] bg-black bg-opacity-40 flex items-center justify-center"
                     onClick={() => setShowPrintDialog(false)}>
                  <div className="bg-white rounded-lg shadow-xl p-6 min-w-[360px] max-w-md"
                       onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-lg font-bold mb-3">📄 Eksport wycinka do PDF {fmtLabel}</h3>
                    <div className="text-sm text-gray-700 mb-4 space-y-1">
                      <div>Format: <span className="font-semibold">{fmtLabel} {orientLabel}</span></div>
                      <div>Skala wydruku: <span className="font-semibold">1:{printScaleH_N}</span> poziomo, <span className="font-semibold">1:{printScaleV_N}</span> pionowo</div>
                      <div className="pt-1">Wymiary obszaru w terenie:</div>
                      <div className="font-mono text-base text-brand-700">
                        {widthM.toFixed(1)} m × {heightM.toFixed(1)} m
                      </div>
                      <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                        ⚠️ Aby skala 1:N była dokładna na papierze, w dialogu drukarki wybierz <b>„Rzeczywisty rozmiar"</b> / <b>„100%"</b> (NIE „Dopasuj do strony").
                      </div>
                    </div>
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={handleExport}
                        className="flex-1 px-4 py-2 rounded bg-brand-700 text-white hover:bg-brand-800 font-medium"
                      >
                        Eksportuj {fmtLabel} ({orientLabel})
                      </button>
                    </div>
                    <button
                      onClick={() => setShowPrintDialog(false)}
                      className="w-full px-3 py-1.5 rounded text-sm text-gray-600 hover:bg-gray-100"
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Dialog walidacji scalania polygonów */}
            {mergeWarning && (() => {
              const isInfo = mergeWarning.tone === 'info';
              const accent = isInfo
                ? { ring: 'ring-brand-200', bar: 'bg-brand-700', bg: 'bg-brand-50', icon: '🧬', text: 'text-brand-800' }
                : { ring: 'ring-amber-200', bar: 'bg-amber-500', bg: 'bg-amber-50', icon: '⚠️', text: 'text-amber-800' };
              return (
                <div className="fixed inset-0 z-[70] bg-black bg-opacity-40 flex items-center justify-center"
                     onClick={() => setMergeWarning(null)}>
                  <div className={`bg-white rounded-xl shadow-2xl overflow-hidden ring-1 ${accent.ring} min-w-[380px] max-w-md`}
                       onClick={(e) => e.stopPropagation()}>
                    <div className={`h-1.5 ${accent.bar}`} />
                    <div className="p-6">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${accent.bg} text-xl`}>
                          {accent.icon}
                        </div>
                        <div className="flex-1 pt-0.5">
                          <h3 className="text-lg font-bold text-gray-900">{mergeWarning.title}</h3>
                          <p className={`text-xs mt-0.5 ${accent.text}`}>
                            Zaznaczonych polygonów: <span className="font-semibold">{mergeWarning.selected}</span> &middot; potrzebne: <span className="font-semibold">2</span>
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed mb-5">
                        {mergeWarning.message}
                      </p>
                      <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 mb-5">
                        <div className="font-semibold text-gray-700 mb-1">Jak zaznaczyć?</div>
                        <div>1. Przytrzymaj <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-[10px] font-mono">Ctrl</kbd> (lub <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-[10px] font-mono">Shift</kbd>).</div>
                        <div>2. Kliknij <span className="font-semibold">dokładnie 2</span> polygony — licznik pokaże się przy przycisku „Połącz zaznaczone".</div>
                        <div>3. Otwórz menu 🧬 Scal/Podziel i kliknij „Połącz zaznaczone".</div>
                      </div>
                      <button
                        onClick={() => setMergeWarning(null)}
                        className={`w-full px-4 py-2 rounded font-medium text-white ${accent.bar} hover:opacity-90`}
                      >
                        Rozumiem
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Dialog konfiguracji eksportu DXF v2 */}
            {showDxfV2Dialog && (
              <div className="fixed inset-0 z-[60] bg-black bg-opacity-40 flex items-center justify-center"
                   onClick={() => setShowDxfV2Dialog(false)}>
                <div className="bg-white rounded-lg shadow-xl p-6 min-w-[400px] max-w-md"
                     onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-bold mb-4">📐 Eksport DXF v2</h3>

                  <label className="flex items-start gap-3 p-3 rounded border-2 border-gray-200 hover:border-purple-300 cursor-pointer mb-2 transition"
                         style={{ borderColor: dxfV2UseHatch ? '#a855f7' : undefined, backgroundColor: dxfV2UseHatch ? '#faf5ff' : undefined }}>
                    <input
                      type="checkbox"
                      checked={dxfV2UseHatch}
                      onChange={(e) => setDxfV2UseHatch(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-purple-600"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">Szrafury (wzory wypełnień)</div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        Kropki, linie, krzyżyki — wzór wg `pattern` warstwy. Wyłącz, jeśli wolisz tylko jednolite kolorowe wypełnienia (SOLID).
                      </div>
                    </div>
                  </label>

                  <div className="flex gap-2 mt-5">
                    <button
                      onClick={() => {
                        setShowDxfV2Dialog(false);
                        exportToDxfV2({
                          boreholes, scale, horizontalScale,
                          connectLayers, findLayerConnections,
                          interpolationPoints, connectionPointAdjustments,
                          lenses, drawings, terrainProfile,
                          manualConnections, resolvePointSvg,
                          showGrid, showLenses, showBoreholeRectangles,
                          showDepths, showSoilName, showSoilState,
                          showGeotechnicalLayer, showThickness, showTerrain,
                          showWater, showFreeWaterLine, showConfinedWaterLine,
                          useHatchPatterns: dxfV2UseHatch,
                        });
                      }}
                      className="flex-1 px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700 font-medium"
                    >
                      Eksportuj DXF
                    </button>
                    <button
                      onClick={() => setShowDxfV2Dialog(false)}
                      className="px-4 py-2 rounded text-sm text-gray-600 hover:bg-gray-100"
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Menu kontekstowe dla etykiety nazwy poligonu (PPM na nazwie) */}
            {labelContextMenu && (() => {
              const closeLabelMenu = () => setLabelContextMenu(null);
              const mc = manualConnections.find(m => m.id === labelContextMenu.mcId);
              if (!mc) return null;
              const maxLeft = Math.min(labelContextMenu.clientX, window.innerWidth - 220);
              const maxTop = Math.min(labelContextMenu.clientY, window.innerHeight - 120);
              return (
                <>
                  <div className="fixed inset-0 z-40" onClick={closeLabelMenu} onContextMenu={(e) => { e.preventDefault(); closeLabelMenu(); }} />
                  <div className="fixed z-50 bg-white border border-gray-300 rounded shadow-xl py-1 min-w-[200px]"
                       style={{ left: maxLeft, top: maxTop }}>
                    <div className="px-3 py-1.5 text-xs text-gray-500 border-b border-gray-200 truncate">
                      Etykieta: <span className="font-semibold text-gray-700">{mc.name}</span>
                    </div>
                    {mc.labelPos && (
                      <button
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                        onClick={() => { resetPolygonLabelPos(mc.id); closeLabelMenu(); }}
                      >
                        ↺ Resetuj pozycję do auto
                      </button>
                    )}
                    <button
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setManualConnections(prev => prev.map(m =>
                          m.id === mc.id ? { ...m, name: '' } : m
                        ));
                        closeLabelMenu();
                      }}
                    >
                      🗑️ Usuń nazwę
                    </button>
                  </div>
                </>
              );
            })()}

            {/* Menu kontekstowe dla polygonu freeDraw */}
            {contextMenuFreeDraw && (() => {
              const currentMc = manualConnections.find(m => m.id === contextMenuFreeDraw.connId);
              const closeMenu = () => { setContextMenuFreeDraw(null); setContextSubmenu(null); };
              return (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={closeMenu}
                    onContextMenu={(e) => { e.preventDefault(); closeMenu(); }}
                  />
                  <div
                    className="fixed z-50 bg-white border border-gray-300 rounded shadow-lg py-1 min-w-[220px]"
                    style={{ left: contextMenuFreeDraw.clientX, top: contextMenuFreeDraw.clientY }}
                  >
                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        setEditPropsDialog({ connId: contextMenuFreeDraw.connId, clientX: contextMenuFreeDraw.clientX, clientY: contextMenuFreeDraw.clientY });
                        closeMenu();
                      }}
                    >
                      ✏️ Edytuj właściwości…
                    </button>

                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        setShowAttributeTable(true);
                        setAttrTableFocusId(contextMenuFreeDraw.connId);
                        setSelectedFreeDraw(contextMenuFreeDraw.connId);
                        closeMenu();
                      }}
                    >
                      📋 Tabela atrybutów
                    </button>

                    <div className="border-t border-gray-200 my-1" />

                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        copyFreeDrawProps(contextMenuFreeDraw.connId);
                        closeMenu();
                      }}
                    >
                      📋 Kopiuj właściwości
                    </button>
                    {copiedFreeDrawProps && (
                      <button
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                        onMouseEnter={() => setContextSubmenu(null)}
                        onClick={() => {
                          pasteFreeDrawProps(contextMenuFreeDraw.connId);
                          closeMenu();
                        }}
                      >
                        📥 Wklej właściwości
                        <span className="ml-2 inline-flex items-center gap-1 align-middle">
                          <span className="inline-block w-3 h-3 rounded border border-gray-300" style={{ background: copiedFreeDrawProps.color }} />
                          <span className="text-xs text-gray-500">{copiedFreeDrawProps.name || '(bez nazwy)'}</span>
                        </span>
                      </button>
                    )}

                    <div className="border-t border-gray-200 my-1" />

                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        setSplittingConnId(contextMenuFreeDraw.connId);
                        setSplitMode(true);
                        setSplitLinePoints([]);
                        setSplitLinePreview(null);
                        closeMenu();
                      }}
                    >
                      ✂️ Podziel warstwę
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        setLensInLayerParentId(contextMenuFreeDraw.connId);
                        setLensInLayerMode(true);
                        setFreeDrawPoints([]);
                        setFreeDrawPreview(null);
                        closeMenu();
                      }}
                    >
                      💎 Narysuj soczewkę w tej warstwie
                    </button>
                    {(() => {
                      const isLockedHere = stickyLayerLock && lensInLayerParentId === contextMenuFreeDraw.connId;
                      return (
                        <button
                          className={isLockedHere
                            ? "w-full text-left px-3 py-2 text-sm bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold"
                            : "w-full text-left px-3 py-2 text-sm hover:bg-amber-50 text-amber-700 font-medium"}
                          onMouseEnter={() => setContextSubmenu(null)}
                          onClick={() => {
                            if (isLockedHere) {
                              // Toggle OFF: odblokuj sticky lock dla tej warstwy
                              setStickyLayerLock(false);
                              setLensInLayerParentId(null);
                              setFreeDrawMode(false);
                              setFreeDrawPoints([]);
                              setFreeDrawPreview(null);
                            } else {
                              // Toggle ON: sticky lock + ✏️ Rysowanie do tej warstwy.
                              // Każdy rysunek klipowany do parenta, mode zostaje aktywny. ESC wychodzi.
                              setStickyLayerLock(true);
                              setLensInLayerParentId(contextMenuFreeDraw.connId);
                              setFreeDrawMode(true);
                              setFreeDrawEditVerticesMode(false);
                              setFreeDrawExpandMode(false);
                              setLensInLayerMode(false);
                              setConnectLayers(true);
                              setSelectedFreeDraw(contextMenuFreeDraw.connId);
                              setFreeDrawPoints([]);
                              setFreeDrawPreview(null);
                            }
                            closeMenu();
                          }}
                        >
                          {isLockedHere
                            ? '🔓 Odblokuj rysowanie do tej warstwy (sticky aktywny)'
                            : '🔒 Zablokuj rysowanie do tej warstwy (sticky)'}
                        </button>
                      );
                    })()}
                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        setCutHoleParentId(contextMenuFreeDraw.connId);
                        setCutHoleMode(true);
                        setFreeDrawPoints([]);
                        setFreeDrawPreview(null);
                        closeMenu();
                      }}
                    >
                      🕳️ Wytnij dziurę
                    </button>
                    <button
                      disabled
                      className="w-full text-left px-3 py-2 text-sm text-gray-400 opacity-50 cursor-not-allowed"
                      title="Funkcja tymczasowo wyłączona"
                    >
                      〰️ Wygładź krawędzie (krzywe)
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        simplifyFreeDrawPolygon(contextMenuFreeDraw.connId, 0.5);
                        closeMenu();
                      }}
                      title="Usuwa zbędne wierzchołki (algorytm Douglas-Peucker, tolerancja 0.5 m). Wywołuj wielokrotnie aby uprościć bardziej."
                    >
                      🪶 Uprość kontur (mniej wierzchołków)
                    </button>
                    {(() => {
                      const hasAttachedPts = !!currentMc?.points?.some(p => p.boreholeId);
                      if (hasAttachedPts) {
                        return (
                          <button
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                            onClick={() => {
                              detachFreeDrawFromBoreholes(contextMenuFreeDraw.connId);
                              closeMenu();
                            }}
                            title="Zamienia punkty zaczepione w otworach na absolutne współrzędne — polygon nie będzie się ruszał z otworem."
                          >
                            🔓 Odetnij od otworów
                          </button>
                        );
                      }
                      return (
                        <button
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                          onClick={() => {
                            reattachFreeDrawToBoreholes(contextMenuFreeDraw.connId);
                            closeMenu();
                          }}
                          title="Próbuje przypiąć punkty z powrotem do najbliższych wierzchołków otworów (tolerancja 5 px). Punkty które nie trafią w żaden wierzchołek zostaną absolutne."
                        >
                          🔗 Przypnij do otworów
                        </button>
                      );
                    })()}

                    <div className="border-t border-gray-200 my-1" />

                    <button
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${movePolygonMode ? 'text-purple-700 font-medium' : ''}`}
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        setMovePolygonMode(!movePolygonMode);
                        closeMenu();
                      }}
                      title={movePolygonMode
                        ? 'Wyłącz tryb przesuwania polygonu'
                        : 'Włącz tryb przesuwania — chwyć i przeciągnij polygon (pozycję można przywrócić)'}
                    >
                      ✋ {movePolygonMode ? 'Wyłącz przesuwanie' : 'Przesuń polygon'}
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        const ids = (selectedFreeDrawIds && selectedFreeDrawIds.length > 0)
                          ? selectedFreeDrawIds
                          : [contextMenuFreeDraw.connId];
                        const ans = window.prompt(
                          `Przesuń ${ids.length} ${ids.length === 1 ? 'poligon' : 'poligony'} w pionie [m] (+ w dół, - w górę):`,
                          '1.0'
                        );
                        closeMenu();
                        if (ans == null) return;
                        const dy = parseFloat(ans.replace(',', '.'));
                        if (!isFinite(dy) || dy === 0) return;
                        shiftFreeDrawsByMeters(ids, 0, dy);
                      }}
                      title="Przesuwa zaznaczone poligony o zadaną liczbę metrów w pionie. Skrót: ↑/↓ (0.5 m), Shift+↑/↓ (2 m), Alt+↑/↓ (0.1 m)."
                    >
                      ↕️ Przesuń o... (m)
                    </button>
                    {currentMc?.originalPoints && (
                      <button
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 text-emerald-700 font-medium"
                        onMouseEnter={() => setContextSubmenu(null)}
                        onClick={() => {
                          restoreFreeDrawPolygonPosition(contextMenuFreeDraw.connId);
                          closeMenu();
                        }}
                      >
                        ↩️ Przywróć pozycję
                      </button>
                    )}

                    <div className="border-t border-gray-200 my-1" />

                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        bringFreeDrawToFront(contextMenuFreeDraw.connId);
                        closeMenu();
                      }}
                    >
                      ⬆️ Przenieś na wierzch
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        sendFreeDrawToBack(contextMenuFreeDraw.connId);
                        closeMenu();
                      }}
                    >
                      ⬇️ Przenieś na spód
                    </button>

                    <div className="border-t border-gray-200 my-1" />

                    <button
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      onMouseEnter={() => setContextSubmenu(null)}
                      onClick={() => {
                        if (window.confirm(`Usunąć tę warstwę${currentMc?.name ? ` (${currentMc.name})` : ''}?`)) {
                          deleteFreeDraw(contextMenuFreeDraw.connId);
                          closeMenu();
                        }
                      }}
                    >
                      🗑️ Usuń warstwę
                    </button>
                  </div>
                </>
              );
            })()}

            {/* Dialog edycji właściwości polygonu */}
            {editPropsDialog && (() => {
              const mc = manualConnections.find(m => m.id === editPropsDialog.connId);
              if (!mc) return null;
              const dialogPatterns = [
                { value: 'none', label: 'Brak' },
                { value: 'sand', label: 'Piasek' },
                { value: 'clay', label: 'Glina' },
                { value: 'gravel', label: 'Żwir' },
                { value: 'rock', label: 'Skała' },
                { value: 'dots', label: 'Kropki' },
                { value: 'horizontal', label: 'Poziome' },
                { value: 'diagonal', label: 'Ukośne' },
                { value: 'cross', label: 'Krzyżyk' },
              ];
              // Pozycjonowanie: obok kliknięcia, ale nie wychodź za ekran
              const maxLeft = Math.min(editPropsDialog.clientX, window.innerWidth - 340);
              const maxTop = Math.min(editPropsDialog.clientY, window.innerHeight - 360);
              return (
                <>
                  <div
                    className="fixed inset-0 z-40 bg-black bg-opacity-20"
                    onClick={() => setEditPropsDialog(null)}
                  />
                  <div
                    className="fixed z-50 bg-white border border-gray-300 rounded shadow-xl p-4 w-[320px]"
                    style={{ left: maxLeft, top: maxTop }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-800">Edytuj właściwości warstwy</h3>
                      <button onClick={() => setEditPropsDialog(null)} className="text-gray-400 hover:text-gray-700 text-lg leading-none">×</button>
                    </div>

                    <label className="block text-xs font-semibold text-gray-600 mb-1">Nazwa</label>
                    <input
                      type="text"
                      value={mc.name || ''}
                      onChange={(e) => updateFreeDrawProps(editPropsDialog.connId, { name: e.target.value })}
                      placeholder="np. Gp, SM(Li)"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm mb-3"
                      autoFocus
                    />

                    <label className="block text-xs font-semibold text-gray-600 mb-1">Kolor</label>
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="color"
                        value={mc.color || '#CCCCCC'}
                        onChange={(e) => updateFreeDrawProps(editPropsDialog.connId, { color: e.target.value })}
                        className="w-10 h-9 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={mc.color || ''}
                        onChange={(e) => updateFreeDrawProps(editPropsDialog.connId, { color: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                      />
                    </div>

                    <label className="block text-xs font-semibold text-gray-600 mb-1">Wzór</label>
                    <select
                      value={mc.pattern || 'none'}
                      onChange={(e) => updateFreeDrawProps(editPropsDialog.connId, { pattern: e.target.value })}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm mb-3"
                    >
                      {dialogPatterns.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>

                    <div className="flex gap-2 justify-end pt-2 border-t border-gray-200">
                      <button
                        onClick={() => { copyFreeDrawProps(editPropsDialog.connId); }}
                        className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                        title="Skopiuj te właściwości do bufora"
                      >
                        📋 Kopiuj
                      </button>
                      <button
                        onClick={() => setEditPropsDialog(null)}
                        className="px-3 py-1 text-sm bg-violet-600 text-white rounded hover:bg-violet-700 font-semibold"
                      >
                        Gotowe
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Pasek informacyjny trybu podziału */}
            {splitMode && (
              <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-4 py-2 rounded shadow-lg flex items-center gap-3">
                <span className="font-semibold">
                  ✂️ Tryb podziału: {splitLinePoints.length === 0 ? 'kliknij początek linii' : 'kliknij koniec linii'}
                </span>
                <button
                  onClick={cancelSplit}
                  className="bg-white text-red-600 px-2 py-0.5 rounded text-sm font-bold hover:bg-gray-100"
                >
                  Anuluj (ESC)
                </button>
              </div>
            )}

            {/* Pasek informacyjny trybu rysowania soczewki w warstwie */}
            {lensInLayerMode && (
              <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-purple-600 text-white px-4 py-2 rounded shadow-lg flex items-center gap-3">
                <span className="font-semibold">
                  💎 Soczewka w warstwie: {freeDrawPoints.length < 2 ? `klikaj punkty (${freeDrawPoints.length}/min 3)` : '2×klik = zamknij kształt'}
                </span>
                <button
                  onClick={cancelLensInLayer}
                  className="bg-white text-purple-600 px-2 py-0.5 rounded text-sm font-bold hover:bg-gray-100"
                >
                  Anuluj (ESC)
                </button>
              </div>
            )}

            {/* Pasek informacyjny trybu wycinania dziury */}
            {cutHoleMode && (
              <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-orange-600 text-white px-4 py-2 rounded shadow-lg flex items-center gap-3">
                <span className="font-semibold">
                  🕳️ Wytnij dziurę: {freeDrawPoints.length < 2 ? `klikaj punkty (${freeDrawPoints.length}/min 3)` : '2×klik = zamknij kształt'}
                </span>
                <button
                  onClick={cancelCutHole}
                  className="bg-white text-orange-600 px-2 py-0.5 rounded text-sm font-bold hover:bg-gray-100"
                >
                  Anuluj (ESC)
                </button>
              </div>
            )}

            <div className="mt-4 text-sm text-gray-600">
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex items-center gap-2">
                    <label className="text-gray-700">Skala pionowa 1:</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={verticalScaleToN(scale)}
                      onChange={(e) => setScale(nToVerticalScale(parseInt(e.target.value) || 200))}
                      className="border rounded px-3 py-1 w-24"
                      title="Skala wydruku pionowa: 1 m terenu pionowo = (1000/N) mm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-700">Skala pozioma 1:</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={horizontalScaleToN(horizontalScale)}
                      onChange={(e) => setHorizontalScale(nToHorizontalScale(parseInt(e.target.value) || 200))}
                      className="border rounded px-3 py-1 w-24"
                      title="Skala wydruku pozioma: 1 m terenu poziomo = (1000/N) mm"
                    />
                  </div>
                </div>

                <div className="mt-3 p-3 bg-gray-50 rounded border-2 border-gray-300">
                  <p className="font-bold text-gray-700 mb-2">🎯 Wyświetlaj na przekroju:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showSoilName}
                        onChange={(e) => setShowSoilName(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Nazwa gruntu</span>
                      {renderLabelOrderArrows('soilName')}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showSoilState}
                        onChange={(e) => setShowSoilState(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Stan gruntu</span>
                      {renderLabelOrderArrows('soilState')}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showGeotechnicalLayer}
                        onChange={(e) => setShowGeotechnicalLayer(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Warstwa geotechn.</span>
                      {renderLabelOrderArrows('geotechnicalLayer')}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showGenesisAndStratigraphy}
                        onChange={(e) => setShowGenesisAndStratigraphy(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Geneza/stratygrafia</span>
                      {renderLabelOrderArrows('genesisAndStratigraphy')}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showThickness}
                        onChange={(e) => setShowThickness(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Miąższość</span>
                      {renderLabelOrderArrows('thickness')}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showDepths}
                        onChange={(e) => setShowDepths(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Głębokości</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Pokaż nadzorującego wiercenie i datę wykonania nad nazwą otworu na przekroju">
                      <input
                        type="checkbox"
                        checked={showBoreholeMeta}
                        onChange={(e) => setShowBoreholeMeta(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Metadane otworu (nadzór, data)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showTerrain}
                        onChange={(e) => setShowTerrain(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Topografia NMT</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Pokaż prawą skalę pionową z numerami rzędnych (m n.p.m.)">
                      <input
                        type="checkbox"
                        checked={showElevationScale}
                        onChange={(e) => setShowElevationScale(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Skala wysokościowa (prawa)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Pokaż lewą skalę pionową z numerami rzędnych (m n.p.m.)">
                      <input
                        type="checkbox"
                        checked={showElevationScaleLeft}
                        onChange={(e) => setShowElevationScaleLeft(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Skala wysokościowa (lewa)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Tytuł przekroju + skala (1:H / 1:V). Przeciągnij myszką aby przesunąć, dwuklik = reset.">
                      <input
                        type="checkbox"
                        checked={showCrossSectionTitle}
                        onChange={(e) => setShowCrossSectionTitle(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Tytuł + skala (ruchome)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={outlineOnly}
                        onChange={(e) => setOutlineOnly(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Tylko kontury</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Pogrubia kontur i nadaje mu ciemniejszy odcień koloru wypełnienia — lepsza czytelność polygonów na wydruku.">
                      <input
                        type="checkbox"
                        checked={sharpenContours}
                        onChange={(e) => setSharpenContours(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Wyostrz kontury</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Pokaż nazwy poligonów freeDraw na ich środku. Per-poligon override w Tabeli atrybutów (Auto/Tak/Nie).">
                      <input
                        type="checkbox"
                        checked={showPolygonLabels}
                        onChange={(e) => setShowPolygonLabels(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Nazwy poligonów</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Przeciągaj cały polygon freeDraw na bok — pozycję można potem przywrócić.">
                      <input
                        type="checkbox"
                        checked={movePolygonMode}
                        onChange={(e) => setMovePolygonMode(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Przesuwanie polygonu</span>
                    </label>
                    {manualConnections.some(mc => mc.fromLayerId === 'freeDraw' && mc.originalPoints) && (
                      <button
                        onClick={() => {
                          if (window.confirm('Przywrócić wszystkie przesunięte polygony do oryginalnych pozycji?')) {
                            restoreAllFreeDrawPolygonPositions();
                          }
                        }}
                        className="px-2 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                        title="Przywróć wszystkie przesunięte polygony"
                      >
                        ↩️ Przywróć pozycje
                      </button>
                    )}
                  </div>
                  {showCrossSectionTitle && (
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600 whitespace-nowrap">Tytuł:</label>
                      <input
                        type="text"
                        value={crossSectionTitle}
                        onChange={(e) => setCrossSectionTitle(e.target.value)}
                        placeholder="PRZEKRÓJ GEOTECHNICZNY G-1.scr"
                        className="flex-1 border rounded px-2 py-1 text-sm"
                      />
                      {crossSectionTitlePos && (
                        <button
                          onClick={() => setCrossSectionTitlePos(null)}
                          className="px-2 py-1 rounded text-xs bg-gray-200 hover:bg-gray-300"
                          title="Reset pozycji tytułu do domyślnej"
                        >
                          ↺ Reset poz.
                        </button>
                      )}
                    </div>
                  )}

                  {/* Menadżer typografii */}
                  <details className="mt-3 border-t pt-2">
                    <summary className="cursor-pointer text-sm font-semibold text-gray-700 select-none">
                      🔤 Typografia (rozmiar / czcionka)
                    </summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="text-gray-500 border-b">
                            <th className="text-left py-1 pr-2">Element</th>
                            <th className="text-left py-1 pr-2 w-16">Rozmiar</th>
                            <th className="text-left py-1 pr-2">Czcionka</th>
                            <th className="text-left py-1 pr-2 w-20">Waga</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(DEFAULT_TYPOGRAPHY).map((key) => {
                            const t = typography[key] || DEFAULT_TYPOGRAPHY[key];
                            const isDefault =
                              t.size === DEFAULT_TYPOGRAPHY[key].size &&
                              t.family === DEFAULT_TYPOGRAPHY[key].family &&
                              t.weight === DEFAULT_TYPOGRAPHY[key].weight;
                            const update = (field, value) => {
                              setTypography((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], [field]: value },
                              }));
                            };
                            const resetOne = () => {
                              setTypography((prev) => ({
                                ...prev,
                                [key]: { ...DEFAULT_TYPOGRAPHY[key] },
                              }));
                            };
                            return (
                              <tr key={key} className="border-b last:border-b-0">
                                <td className="py-1 pr-2">{TYPOGRAPHY_LABELS[key] || key}</td>
                                <td className="py-1 pr-2">
                                  <input
                                    type="number"
                                    min="6"
                                    max="48"
                                    value={t.size}
                                    onChange={(e) => update('size', Number(e.target.value) || 12)}
                                    className="w-14 border rounded px-1 py-0.5"
                                  />
                                </td>
                                <td className="py-1 pr-2">
                                  <select
                                    value={t.family}
                                    onChange={(e) => update('family', e.target.value)}
                                    className="border rounded px-1 py-0.5 max-w-[180px]"
                                    style={{ fontFamily: t.family }}
                                  >
                                    {FONT_FAMILY_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value} style={{ fontFamily: opt.value }}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="py-1 pr-2">
                                  <select
                                    value={t.weight}
                                    onChange={(e) => update('weight', e.target.value)}
                                    className="border rounded px-1 py-0.5"
                                  >
                                    <option value="normal">Normal</option>
                                    <option value="bold">Bold</option>
                                  </select>
                                </td>
                                <td className="py-1">
                                  {!isDefault && (
                                    <button
                                      onClick={resetOne}
                                      className="text-gray-500 hover:text-gray-700"
                                      title="Reset do domyślnych"
                                    >
                                      ↺
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div className="mt-2 flex justify-end">
                        <button
                          onClick={() => setTypography({ ...DEFAULT_TYPOGRAPHY })}
                          className="px-2 py-1 rounded text-xs bg-gray-200 hover:bg-gray-300"
                          title="Reset wszystkich do domyślnych"
                        >
                          ↺ Reset wszystkich
                        </button>
                      </div>
                    </div>
                  </details>
                  <div className="mt-2">
                    {terrainLoading && (
                      <span className="text-sm text-gray-500">Pobieranie NMT...</span>
                    )}
                    {terrainProfile && (
                      <span className="text-xs text-green-600">({terrainProfile.length} pkt)</span>
                    )}
                    {terrainProfile && (
                      <button
                        onClick={() => setClipToTerrain(!clipToTerrain)}
                        className={`ml-2 px-3 py-1.5 rounded text-sm font-medium ${
                          clipToTerrain
                            ? 'bg-brand-700 text-white hover:bg-brand-800'
                            : 'bg-yellow-500 text-white hover:bg-yellow-600'
                        }`}
                      >
                        {clipToTerrain ? 'Cofnij przycięcie' : 'Przytnij do terenu'}
                      </button>
                    )}
                    {terrainError && (
                      <p className="text-xs text-red-500 mt-1">{terrainError}</p>
                    )}
                  </div>
                </div>

                {!interpolationMode && !drawingMode && (
                  <div className="mt-2 p-2 bg-blue-50 rounded">
                    <p>✨ <strong>Sterowanie:</strong></p>
                    <p>• Przeciągnij nazwę otworu (↔) aby zmienić odległość</p>
                    <p>• Przeciągnij warstwę w pionie (↕) aby zmienić miąższość</p>
                    <p>• Podwójne kliknięcie warstwy = edycja nazwy</p>
                  </div>
                )}
                {drawingMode && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border-2 border-blue-400">
                    <p className="font-bold text-blue-700">{drawingMode === 'measure' ? '📏 TRYB POMIARU:' : '🎨 TRYB RYSOWANIA:'}</p>
                    <p className="text-blue-600">• Kliknij i przeciągnij na przekroju aby narysować</p>
                    <p className="text-blue-600">• Widzisz dynamiczny podgląd podczas przeciągania</p>
                    <p className="text-blue-600">• Rysunki są dodawane do historii po zakończeniu</p>
                    <p className="text-blue-600">• Możesz usuwać pojedyncze rysunki z listy historii</p>
                  </div>
                )}
                {interpolationMode && (
                  <div className="mt-2 p-3 bg-orange-50 border-2 border-orange-300 rounded">
                    <p className="font-bold text-orange-700">⛰️ TRYB EDYCJI INTERPOLACJI:</p>
                    <p className="text-orange-600">• Najedź na warstwę aby zobaczyć punkty kontrolne</p>
                    <p className="text-orange-600">• 🟢 Zielony punkt na górze i dole warstwy (środek krzywej)</p>
                    <p className="text-orange-600">• 🔵 Niebieskie punkty = wierzchołki warstw (można przesuwać w pionie)</p>
                    <p className="text-orange-600">• 🟠 Pomarańczowy punkt = zmodyfikowany</p>
                    <p className="text-orange-600">• Przeciągnij punkty aby wygiąć linie lub zmienić wysokość połączeń</p>
                    <p className="text-orange-600">• 🔴 Czerwony przycisk × resetuje punkt do pozycji domyślnej</p>
                    <p className="text-orange-600 mt-1 font-semibold">Interpolacja: {Object.keys(interpolationPoints).length} | Wierzchołki: {Object.keys(connectionPointAdjustments).length}</p>
                  </div>
                )}

                {moveDrawingMode && (
                  <div className="mt-2 p-3 bg-green-50 border-2 border-green-400 rounded">
                    <p className="font-bold text-green-700">✋ TRYB PRZESUWANIA RYSUNKÓW:</p>
                    <p className="text-green-600">• Kliknij i przeciągnij dowolny rysunek lub tekst aby go przesunąć</p>
                    <p className="text-green-600">• Przeciągane elementy są półprzezroczyste z cieniem</p>
                    <p className="text-green-600">• Kursor zmienia się na ✋ nad elementami do przesunięcia</p>
                    <p className="text-green-600">• Kliknij przycisk "✋ Przesuń" ponownie aby wyłączyć tryb</p>
                  </div>
                )}

                {manualConnectionMode && (
                  <div className="bg-green-50 border-2 border-green-500 rounded p-3 text-sm">
                    <p className="text-green-700 font-bold mb-2">🔗 Tryb łączenia warstw jest aktywny</p>
                    <p className="text-green-600">• Kliknij warstwę w pierwszym otworze</p>
                    <p className="text-green-600">• Następnie kliknij warstwę w sąsiednim otworze</p>
                    <p className="text-green-600">• Wybrana warstwa ma zieloną ramkę</p>
                    <p className="text-green-600">• Kliknij ponownie to samo połączenie aby je usunąć</p>
                    {selectedLayerForConnection && (
                      <p className="text-green-700 mt-1 font-semibold">✓ Wybrano warstwę - kliknij warstwę docelową w sąsiednim otworze</p>
                    )}
                    <p className="text-green-700 mt-1 font-semibold">Liczba ręcznych połączeń: {manualConnections.length}</p>
                  </div>
                )}
              </div>


    </div>
        </>

      {/* ========== MODAL: Tabela atrybutów poligonów ========== */}
      {showAttributeTable && (() => {
        const freeDrawConns = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points && mc.points.length >= 3);
        const sorted = [...freeDrawConns].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        const pixelsPerM = scale / 10;

        const allPatterns = [
          { value: 'none', label: 'Brak' },
          { value: 'sand', label: 'Piasek' },
          { value: 'clay', label: 'Glina' },
          { value: 'gravel', label: 'Żwir' },
          { value: 'rock', label: 'Skała' },
          { value: 'dots', label: 'Kropki' },
          { value: 'horizontal', label: 'Poziome' },
          { value: 'diagonal', label: 'Ukośne' },
          { value: 'cross', label: 'Krzyżyk' },
        ];

        const renderPatternSvg = (patternValue, uid, size = 32) => {
          const pId = `attrpat-${uid}-${patternValue}`;
          return (
            <svg width={size} height={size} style={{ display: 'block' }}>
              <defs>
                <pattern id={pId} x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                  {patternValue === 'sand' && <><circle cx="1.5" cy="1.5" r="1" fill="#333"/><circle cx="4.5" cy="4.5" r="1" fill="#333"/></>}
                  {patternValue === 'clay' && <><line x1="0" y1="2" x2="6" y2="2" stroke="#333" strokeWidth="0.8"/><line x1="0" y1="5" x2="6" y2="5" stroke="#333" strokeWidth="0.8"/></>}
                  {patternValue === 'gravel' && <><circle cx="2" cy="2" r="1.8" fill="#333"/><circle cx="5" cy="5" r="1.8" fill="#333"/></>}
                  {patternValue === 'rock' && <><line x1="0" y1="0" x2="6" y2="6" stroke="#333" strokeWidth="0.8"/><line x1="6" y1="0" x2="0" y2="6" stroke="#333" strokeWidth="0.8"/></>}
                  {patternValue === 'dots' && <><circle cx="2" cy="2" r="1.8" fill="#333"/><circle cx="5" cy="5" r="1.8" fill="#333"/></>}
                  {patternValue === 'horizontal' && <><line x1="0" y1="2" x2="6" y2="2" stroke="#333" strokeWidth="0.8"/><line x1="0" y1="5" x2="6" y2="5" stroke="#333" strokeWidth="0.8"/></>}
                  {patternValue === 'diagonal' && <line x1="0" y1="6" x2="6" y2="0" stroke="#333" strokeWidth="0.8"/>}
                  {patternValue === 'cross' && <><line x1="0" y1="0" x2="6" y2="6" stroke="#333" strokeWidth="0.8"/><line x1="6" y1="0" x2="0" y2="6" stroke="#333" strokeWidth="0.8"/></>}
                </pattern>
              </defs>
              <rect width={size} height={size} fill={patternValue && patternValue !== 'none' ? `url(#${pId})` : '#f9f9f9'} stroke="#999" strokeWidth="1.5" rx="3"/>
            </svg>
          );
        };

        // Oblicz atrybuty geometryczne dla poligonu
        const calcGeometry = (mc) => {
          const pts = mc.points.map(p => resolvePointSvg(p));
          if (pts.length < 3) return { area: 0, perimeter: 0, cx: 0, cy: 0, minDepth: 0, maxDepth: 0, width: 0, pointCount: 0 };

          // Konwersja punktów na metry
          const ptsM = mc.points.map(p => {
            if (p.boreholeId) {
              const bh = boreholes.find(b => b.id === p.boreholeId);
              return { distM: bh ? bh.distance : (p.distanceM || 0), depthM: p.depthM || 0 };
            }
            return {
              distM: (p.x != null ? (p.x - 100) / horizontalScale : (p.distanceM || 0)),
              depthM: (p.y != null ? (p.y - 80) / pixelsPerM : (p.depthM || 0)),
            };
          });

          // Pole (Shoelace)
          let area = 0;
          for (let i = 0; i < ptsM.length; i++) {
            const j = (i + 1) % ptsM.length;
            area += ptsM[i].distM * ptsM[j].depthM;
            area -= ptsM[j].distM * ptsM[i].depthM;
          }
          area = Math.abs(area) / 2;

          // Obwód
          let perimeter = 0;
          for (let i = 0; i < ptsM.length; i++) {
            const j = (i + 1) % ptsM.length;
            const dx = ptsM[j].distM - ptsM[i].distM;
            const dy = ptsM[j].depthM - ptsM[i].depthM;
            perimeter += Math.sqrt(dx * dx + dy * dy);
          }

          // Centroid
          const cx = ptsM.reduce((s, p) => s + p.distM, 0) / ptsM.length;
          const cy = ptsM.reduce((s, p) => s + p.depthM, 0) / ptsM.length;

          // Zakresy
          const depths = ptsM.map(p => p.depthM);
          const distances = ptsM.map(p => p.distM);

          return {
            area: area.toFixed(2),
            perimeter: perimeter.toFixed(2),
            cx: cx.toFixed(2),
            cy: cy.toFixed(2),
            minDepth: Math.min(...depths).toFixed(2),
            maxDepth: Math.max(...depths).toFixed(2),
            width: (Math.max(...distances) - Math.min(...distances)).toFixed(2),
            pointCount: pts.length,
          };
        };

        const updateAttr = (id, field, value) => {
          setManualConnections(prev => prev.map(mc =>
            mc.id === id ? { ...mc, [field]: value } : mc
          ));
        };

        const toRoman = (n) => {
          const vals = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
          const syms = ['M','CM','D','CD','C','XC','L','XL','X','IX','V','IV','I'];
          let result = '';
          for (let i = 0; i < vals.length; i++) {
            while (n >= vals[i]) { result += syms[i]; n -= vals[i]; }
          }
          return result;
        };

        const colorToHex = (c) => {
          if (!c) return '#CCCCCC';
          if (c.startsWith('#') && (c.length === 7 || c.length === 4)) return c;
          const tmp = document.createElement('div');
          tmp.style.color = c;
          document.body.appendChild(tmp);
          const rgb = getComputedStyle(tmp).color;
          document.body.removeChild(tmp);
          const m = rgb.match(/(\d+)/g);
          if (m) return '#' + m.slice(0, 3).map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
          return '#CCCCCC';
        };

        // Auto-nadaj nazwy rzymskie poligonom bez nazwy
        const autoName = () => {
          const usedNames = new Set(sorted.filter(mc => mc.name).map(mc => mc.name));
          let counter = 1;
          sorted.forEach(mc => {
            if (!mc.name) {
              while (usedNames.has(toRoman(counter))) counter++;
              updateAttr(mc.id, 'name', toRoman(counter));
              usedNames.add(toRoman(counter));
              counter++;
            }
          });
        };

        return (
          <div className="fixed z-[9999] bg-white rounded-lg shadow-2xl border border-gray-300"
               style={{
                 left: attrTablePos.x,
                 top: attrTablePos.y,
                 maxWidth: 'calc(95vw - 40px)',
                 maxHeight: 'calc(85vh - 40px)',
                 minWidth: '700px',
                 display: 'flex',
                 flexDirection: 'column',
                 resize: 'both',
                 overflow: 'hidden',
               }}
               onMouseMove={(e) => {
                 if (!attrTableDrag) return;
                 setAttrTablePos({
                   x: Math.max(0, attrTableDrag.origX + (e.clientX - attrTableDrag.startX)),
                   y: Math.max(0, attrTableDrag.origY + (e.clientY - attrTableDrag.startY)),
                 });
               }}
               onMouseUp={() => setAttrTableDrag(null)}
               onMouseLeave={() => setAttrTableDrag(null)}
          >
              <div className="flex items-center justify-between px-4 py-2 border-b bg-violet-50 rounded-t-lg select-none"
                   style={{ cursor: attrTableDrag ? 'grabbing' : 'grab' }}
                   onMouseDown={(e) => {
                     if (e.target.tagName === 'BUTTON') return;
                     setAttrTableDrag({ startX: e.clientX, startY: e.clientY, origX: attrTablePos.x, origY: attrTablePos.y });
                   }}>
                <h2 className="text-lg font-bold text-violet-800">Tabela atrybutów ({sorted.length})</h2>
                <div className="flex items-center gap-2">
                  {sorted.some(mc => !mc.name) && (
                    <button onClick={autoName}
                            className="px-2 py-1 bg-brand-700 text-white rounded text-xs hover:bg-brand-800"
                            title="Nadaj nazwy rzymskie (I, II, III...) poligonom bez nazwy">
                      Autonazwy I, II, III...
                    </button>
                  )}
                  {sorted.length > 0 && (
                    <>
                      <button onClick={() => {
                                const ids = new Set(sorted.map(mc => mc.id));
                                setManualConnections(prev => prev.map(mc => ids.has(mc.id) ? { ...mc, showLabel: true } : mc));
                              }}
                              className="px-2 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700"
                              title="Włącz etykietę (checkbox przy nazwie) dla wszystkich poligonów">
                        ✓ Etykiety: wszystkie
                      </button>
                      <button onClick={() => {
                                const ids = new Set(sorted.map(mc => mc.id));
                                setManualConnections(prev => prev.map(mc => ids.has(mc.id) ? { ...mc, showLabel: false } : mc));
                              }}
                              className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                              title="Wyłącz etykietę dla wszystkich poligonów">
                        ✗ Etykiety: żadne
                      </button>
                    </>
                  )}
                  <button onClick={() => { setShowAttributeTable(false); setEditingAttrRow(null); setAttrTableFocusId(null); }}
                          className="text-gray-500 hover:text-gray-700 text-2xl px-2 leading-none">×</button>
                </div>
              </div>

              {sorted.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Brak poligonów freeDraw. Narysuj poligony lub skonwertuj warstwy.
                </div>
              ) : (
                <div className="overflow-auto" style={{ flex: 1 }}>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100 sticky top-0">
                        <th className="border px-2 py-2 text-left w-10">#</th>
                        <th className="border px-2 py-2 text-left min-w-[120px]">Nazwa</th>
                        <th className="border px-2 py-2 text-center w-16">Kolor</th>
                        <th className="border px-2 py-2 text-left min-w-[130px]">Wzór</th>
                        <th className="border px-2 py-2 text-right w-16">Pkt</th>
                        <th className="border px-2 py-2 text-right min-w-[80px]">Pole [m²]</th>
                        <th className="border px-2 py-2 text-right min-w-[80px]">Obwód [m]</th>
                        <th className="border px-2 py-2 text-right min-w-[80px]">Szer. [m]</th>
                        <th className="border px-2 py-2 text-right min-w-[100px]">Głęb. min</th>
                        <th className="border px-2 py-2 text-right min-w-[100px]">Głęb. max</th>
                        <th className="border px-2 py-2 text-center min-w-[100px]">Centroid</th>
                        <th className="border px-2 py-2 text-center w-14"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((mc, idx) => {
                        const geo = calcGeometry(mc);

                        return (
                          <tr key={mc.id}
                              data-attr-mcid={mc.id}
                              className={`hover:bg-violet-50 ${attrTableFocusId === mc.id ? 'bg-yellow-200' : (selectedFreeDraw === mc.id ? 'bg-yellow-50' : '')}`}
                              onMouseEnter={() => setSelectedFreeDraw(mc.id)}
                              onMouseLeave={() => setSelectedFreeDraw(null)}>
                            <td className="border px-2 py-1 text-gray-500">{idx + 1}</td>

                            {/* Nazwa + checkbox "pokaż etykietę na poligonie" */}
                            <td className="border px-1 py-1">
                              <div className="flex items-center gap-1.5">
                                {(() => {
                                  const effective = mc.showLabel !== undefined ? mc.showLabel : showPolygonLabels;
                                  return (
                                    <input
                                      type="checkbox"
                                      checked={effective}
                                      onChange={() => updateAttr(mc.id, 'showLabel', !effective)}
                                      className="w-4 h-4 cursor-pointer flex-shrink-0"
                                      title={`Pokaż nazwę na środku poligonu (globalne 'Nazwy poligonów' = ${showPolygonLabels ? 'ON' : 'OFF'})`}
                                    />
                                  );
                                })()}
                                <input
                                  type="text"
                                  value={mc.name || ''}
                                  onChange={(e) => updateAttr(mc.id, 'name', e.target.value)}
                                  className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-sm focus:border-violet-400 focus:outline-none"
                                  placeholder="Nazwa warstwy"
                                />
                              </div>
                            </td>

                            {/* Kolor - zawsze edytowalny */}
                            <td className="border px-2 py-1 text-center">
                              <input
                                type="color"
                                value={colorToHex(mc.color)}
                                onChange={(e) => updateAttr(mc.id, 'color', e.target.value)}
                                className="w-7 h-7 cursor-pointer border-0 p-0"
                              />
                            </td>

                            {/* Wzór - picker z szrafurami */}
                            <td className="border px-1 py-1">
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const popup = e.currentTarget.nextElementSibling;
                                    if (popup) popup.style.display = popup.style.display === 'none' ? 'flex' : 'none';
                                  }}
                                  className="flex items-center gap-1.5 border border-gray-200 rounded px-1 py-0.5 hover:border-violet-400 bg-white cursor-pointer"
                                  title={allPatterns.find(p => p.value === (mc.pattern || 'none'))?.label || 'Brak'}
                                >
                                  {renderPatternSvg(mc.pattern || 'none', mc.id, 34)}
                                  <span className="text-xs text-gray-700 font-medium">{allPatterns.find(p => p.value === (mc.pattern || 'none'))?.label || 'Brak'}</span>
                                </button>
                                <div
                                  style={{ display: 'none' }}
                                  className="absolute left-0 top-full mt-1 bg-white border-2 border-gray-300 rounded shadow-lg p-2 flex flex-row gap-1.5 z-[10000]"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {allPatterns.map(({ value, label }) => (
                                    <button
                                      key={value}
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateAttr(mc.id, 'pattern', value);
                                        e.currentTarget.parentElement.style.display = 'none';
                                      }}
                                      className={`flex flex-col items-center gap-0.5 p-1.5 border-2 rounded cursor-pointer hover:border-violet-500 ${(mc.pattern || 'none') === value ? 'border-violet-500 bg-violet-50' : 'border-gray-200'}`}
                                      title={label}
                                    >
                                      {renderPatternSvg(value, `${mc.id}-opt`, 42)}
                                      <span className="text-[10px] text-gray-600 font-medium">{label}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </td>

                            <td className="border px-2 py-1 text-right text-gray-600">{geo.pointCount}</td>
                            <td className="border px-2 py-1 text-right font-mono text-gray-700">{geo.area}</td>
                            <td className="border px-2 py-1 text-right font-mono text-gray-700">{geo.perimeter}</td>
                            <td className="border px-2 py-1 text-right font-mono text-gray-700">{geo.width}</td>
                            <td className="border px-2 py-1 text-right font-mono text-gray-700">{geo.minDepth}</td>
                            <td className="border px-2 py-1 text-right font-mono text-gray-700">{geo.maxDepth}</td>
                            <td className="border px-2 py-1 text-center font-mono text-gray-600 text-xs">
                              ({geo.cx}, {geo.cy})
                            </td>

                            {/* Usuń */}
                            <td className="border px-2 py-1 text-center">
                              <button
                                onClick={() => {
                                  if (window.confirm(`Usunąć poligon #${idx + 1}${mc.name ? ` (${mc.name})` : ''}?`)) {
                                    deleteFreeDraw(mc.id);
                                  }
                                }}
                                className="px-2 py-0.5 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                                title="Usuń poligon"
                              >×</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="p-3 border-t bg-gray-50 text-xs text-gray-500 flex justify-between items-center rounded-b-lg">
                <span>Pole i obwód w metrach rzeczywistych.</span>
                <button onClick={() => { setShowAttributeTable(false); setEditingAttrRow(null); }}
                        className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400 text-gray-700 text-sm">
                  Zamknij
                </button>
              </div>
          </div>
        );
      })()}

      </div>
    </div>
  );
};

export default GeologicalProfile;